/**
 * Move Nourish Flourish — local store mirroring a Convex schema/API shape.
 *
 * Why this exists:
 * - The user wants Convex on the backend (instead of Supabase).
 * - The sandboxed iframe blocks external Convex connections in this demo.
 * - This module exposes the SAME shape Convex would: typed schemas + a
 *   `useQuery` / `useMutation` pair backed by an in-memory reactive store.
 *
 * Swap path to real Convex (production):
 *   1. `npm i convex` and `npx convex dev`
 *   2. Replace the implementations of `useQuery` / `useMutation` below with
 *      `import { useQuery, useMutation } from "convex/react"`.
 *   3. Move the seed data here into `convex/seed.ts` and remove this file's
 *      in-memory store. Schemas in `Schema` already match `convex/schema.ts`.
 *
 * IMPORTANT: do NOT use localStorage / sessionStorage / cookies — sandboxed
 * iframes block them. State here lives in module memory only and resets on
 * page reload, which is the right demo behavior.
 */

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { lydiaReply, LYDIA_OPENER } from "./lydia";
import { shouldQueueForReview } from "./contentSafety";
import { buildSession, mesocycleWeek, accessoryBlock } from "./movementEngine";

/* ============================== Schema ==================================== */

/** Bumped whenever any binding legal doc (ToS, Privacy, Disclaimer,
 *  Community Guidelines) changes in a way that requires re-acceptance. */
export const CURRENT_CONSENT_VERSION = "2026.04.27";

export type Tier = "Seedling" | "Rooted" | "Flourishing";

const TIER_RANK: Record<Tier, number> = { Seedling: 0, Rooted: 1, Flourishing: 2 };
/** Returns true when `userTier` is at or above `requiredTier`. */
export function tierAtLeast(userTier: Tier | undefined | null, requiredTier: Tier): boolean {
  if (!userTier) return false;
  return TIER_RANK[userTier] >= TIER_RANK[requiredTier];
}
export type LifeStage = "single" | "married" | "mama" | "empty-nester";
export type Meal = "morning" | "midday" | "evening" | "snack";
export type CyclePhase = "menstrual" | "follicular" | "ovulatory" | "luteal";
export type SisterStatus = "thriving" | "steady" | "needs love";

/**
 * Permission role. Mirrors the `role` field on the Convex `users` table
 * (convex/users.ts) which is what `ensureDomainId` returns and what
 * convex/coachReview.ts gates moderation on.
 */
export type UserRole = "member" | "coach" | "admin";

export interface User {
  id: string;
  name: string;
  email: string;
  /** Two-letter monogram shown in <Avatar>. Derived from name for real users. */
  initials: string;
  tier: Tier;
  role: UserRole;
  /**
   * Undefined until onboarding completes. A freshly signed-up sister has no
   * life stage, and code that assumes one is present is the reason a real
   * user saw a hardcoded demo plan. Check before reading.
   */
  lifeStage?: LifeStage;
  goals: string[]; // e.g. ["weight-loss", "energy", "scripture-rooted"]
  translation: string; // bible translation: "ESV" | "NIV" | "KJV" ...
  streak: number;
  resting: boolean; // grace flag, not "broken"
  restingDays: number;
  /** Undefined until she logs a cycle. Not every sister tracks one. */
  cycleDay?: number;
  zipCode: string | null;
  city: string | null;
  /** Square profile photo, base64 data URL. null until the sister uploads one. */
  avatarDataUrl: string | null;
  /** Public Instagram handle without @, optional self-expression on profile. */
  instagram: string | null;
  /** Mock password hash (never real auth, demo only). null until set. */
  passwordHash: string | null;
  createdAt: number;
}

export type LiveStatus = "live" | "upcoming" | "replay";
export type LiveCategory = "strength" | "yoga" | "prayer" | "teaching" | "cardio" | "recovery";

export interface LiveClass {
  id: string;
  coachId: string;
  title: string;
  category: LiveCategory;
  description: string;
  scheduledFor: number;        // epoch ms
  durationMin: number;
  status: LiveStatus;
  thumbnail: string;            // emoji or placeholder color seed
  attendees: number;
  watching: number;             // live viewers right now
  replayDurationMin?: number;
  verseRef?: string;
  tierMin: Tier;                // minimum tier to attend
}

export interface LiveChatMessage {
  id: string;
  classId: string;
  userId: string;
  userName: string;
  initials: string;
  body: string;
  type: "chat" | "prayer" | "amen";
  sentAt: number;
}

export type CommunitySource = "farmers-market" | "fitness-meetup" | "faith-event" | "sister-meetup";

export interface CommunityEvent {
  id: string;
  source: CommunitySource;
  title: string;
  venue: string;
  city: string;
  zipCode: string;
  distanceMi: number;
  startsAt: number;
  endsAt?: number;
  recurring?: string;          // e.g. "Every Saturday"
  hostName?: string;            // for sister-meetups
  hostInitials?: string;
  hostId?: string;
  rsvpCount?: number;
  url?: string;                 // external link for non-MNF events
  description: string;
}

export type FeedPostKind = "testimony" | "prayer" | "win" | "verse";

export interface FeedPost {
  id: string;
  userId: string;
  userName: string;
  initials: string;
  tier: Tier;
  kind: FeedPostKind;
  body: string;
  verseRef?: string;
  amens: number;
  waters: number;     // sister prayers / love drops
  comments: number;
  isAmened?: boolean; // current user has tapped amen
  isWatered?: boolean;
  createdAt: number;
}

export interface FeedComment {
  id: string;
  postId: string;
  userId: string;
  userName: string;
  initials: string;
  body: string;
  createdAt: number;
}

/* ============================ The Sisterhood ============================== */

/**
 * The Sisterhood is the community feed all tiers can post into. Five post
 * kinds capture the shape of what sisters share with each other. Posts can
 * be queued for coach review when they trip the sensitive-keyword filter,
 * removed by a coach, or live. Authors get a 24-hour edit window after
 * which the body is locked. Reactions and comments live in their own tables
 * so the viewer's own state is recoverable and counts stay accurate.
 */
export type SisterhoodPostKind =
  | "testimony"
  | "win"
  | "prayer"
  | "verse"
  | "question";

export type SisterhoodPostStatus = "live" | "queued" | "removed";

export interface SisterhoodPost {
  id: string;
  authorId: string;
  kind: SisterhoodPostKind;
  body: string;
  /** Optional photo (data URL when uploaded in-app, http URL when seeded). */
  photoDataUrl: string | null;
  /** Optional verse reference for kind = verse, e.g. "Isaiah 40:31". */
  verseRef: string | null;
  status: SisterhoodPostStatus;
  createdAt: number;
  /** Timestamp after which the body is locked. createdAt + 24h. */
  editLockedAt: number;
  /** Coach can flag a post for follow-up; unrelated to status. */
  flaggedBy?: string | null;
  flaggedReason?: string | null;
}

export type SisterhoodReactionKind = "amen" | "praying" | "me-too";

export interface SisterhoodReaction {
  id: string;
  postId: string;
  userId: string;
  kind: SisterhoodReactionKind;
  reactedAt: number;
}

export type SisterhoodCommentStatus = "live" | "queued" | "removed";

export interface SisterhoodComment {
  id: string;
  postId: string;
  userId: string;
  body: string;
  status: SisterhoodCommentStatus;
  createdAt: number;
}

/**
 * SisterhoodReport is filed when a sister taps the report flag on a post or
 * comment. Coach Kiley sees the queue; she does not see private message
 * content. We keep the row even after she resolves it so the audit trail
 * survives.
 */
export type SisterhoodReportTarget = "post" | "comment";
export type SisterhoodReportStatus = "open" | "reviewed" | "removed";

export interface SisterhoodReport {
  id: string;
  target: SisterhoodReportTarget;
  targetId: string;
  reporterId: string;
  reason: string;
  status: SisterhoodReportStatus;
  createdAt: number;
  resolvedAt: number | null;
}

/* ============================ Sister Network ============================== */

/**
 * The Sister Network is a peer-to-peer layer for sisters who have walked
 * 90+ consecutive days with the same accountability sister. It is opt-in,
 * structured-intro only, and capped at 5 active connections at a time. It
 * never replaces the assigned accountability sister; the primary pair stays
 * the heart of the rhythm. Coach Kiley sees a monthly summary (counts only),
 * never message content.
 */

export type NetworkLifeStage =
  | "single"
  | "married"
  | "mama"
  | "pregnant"
  | "postpartum"
  | "empty-nester";

export type NetworkGoal =
  | "strength"
  | "nourishment"
  | "scripture"
  | "motherhood"
  | "marriage"
  | "grief"
  | "body-image"
  | "rest"
  | "perimenopause";

export interface NetworkProfile {
  userId: string;
  /** When true, this sister appears in the browse grid and can send intros. */
  isOpenIn: boolean;
  /** A short, soft bio sentence shown on her network card. */
  bio: string;
  lifeStage: NetworkLifeStage;
  goals: NetworkGoal[];
  /** When she first opted in. Used to surface "new in network" tags. */
  joinedNetworkAt: number;
  /** When she last toggled isOpenIn. */
  updatedAt: number;
}

/**
 * NetworkIntro is the structured first contact between two veterans. The
 * sender picks a prompt and may add a short custom note. The receiver can
 * accept, decline silently (no notification fires back to the sender), or
 * accept as a one-time exchange. Threads auto-lapse after 14 days of
 * silence; lastMessageAt drives the lapse calculation.
 */
export type NetworkIntroStatus =
  | "pending"
  | "accepted"
  | "declined"
  | "one-time"
  | "lapsed";

export interface NetworkIntro {
  id: string;
  fromUserId: string;
  toUserId: string;
  /** The pre-written prompt the sender selected. */
  prompt: string;
  /** Optional short note (<= 240 chars) added by the sender. */
  customNote: string;
  status: NetworkIntroStatus;
  createdAt: number;
  /** Most recent message timestamp. Drives auto-lapse after 14 days. */
  lastMessageAt: number;
  /** When the receiver acted on the intro (accept/decline/one-time). */
  respondedAt: number | null;
}

export interface NetworkMessage {
  id: string;
  introId: string;
  fromUserId: string;
  body: string;
  sentAt: number;
}

/* ===================== Personalization (NASM + GGS) ====================== *
 *
 * MovementProfile is the single source of truth for what gets surfaced on the
 * Move tab. It captures three things at onboarding and lets her edit any of
 * them later from Settings:
 *
 *   1. Injury flags. NASM Corrective Exercise Specialist categories grouped
 *      into four buckets a sister can self-identify with confidence. A flag
 *      is either active (route around it) or paused (treat as cleared, e.g.
 *      after PT signs off). Active flags filter the exercise library by
 *      contraindication tag.
 *
 *   2. Fitness goals. NASM OPT model maps these to set/rep schemes. A sister
 *      may pick more than one; the engine blends them.
 *
 *   3. Weekly schedule. Days she can move plus minutes per day. The engine
 *      uses these to size the session and pick split (full-body when she has
 *      2-3 days, upper/lower when she has 4+ days, push/pull/legs when she
 *      has 5+).
 *
 * The mesocycle is computed, not stored. Compounds hold for 4 weeks with
 * progressive overload. Accessories rotate every 2 weeks. The anchor field
 * `mesocycleStartedAt` lets us derive the current week without persisting
 * a denormalized current-week field that drifts.
 *
 * GGS Pre/Postnatal V2 protocols layer on top via SeasonState (already
 * modeled). When season is pregnant, postpartum, or nursing, the engine
 * applies GGS overrides regardless of injury flag selection. */

export type InjuryFlag = "joints" | "spine" | "pelvic-floor" | "other";

export interface InjurySelection {
  flag: InjuryFlag;
  /** Free text she can update without surgery on the schema. */
  detail: string;
  /** Active means the engine routes around it. Paused means treat as
   *  cleared (e.g. after PT signs off, she does not have to delete it
   *  to resume normal programming). */
  paused: boolean;
}

export type FitnessGoal =
  | "lose-weight"
  | "build-strength"
  | "flexibility"
  | "postpartum-recovery";

/** Days of the week she expects to move. 0 = Sunday, 6 = Saturday. */
export type WeekDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface WeeklySchedule {
  /** Sorted unique list of weekdays. Empty array = no schedule yet. */
  days: WeekDay[];
  /** Minutes per day, 15 to 90 in 15-min increments. */
  minutesPerDay: number;
}

export interface MovementProfile {
  userId: string;
  injuries: InjurySelection[];
  goals: FitnessGoal[];
  schedule: WeeklySchedule;
  /** Anchor for the rotating mesocycle. Compounds hold for 4 weeks from
   *  this timestamp; accessories rotate every 2 weeks. Reset when she
   *  taps "Refresh my mesocycle" or after a long break. */
  mesocycleStartedAt: number;
  /** True after onboarding finished. Profile may exist with sensible
   *  defaults during onboarding before this flips true. */
  completed: boolean;
  updatedAt: number;
}

/** A movement in the NASM-aligned exercise library. Tags drive injury
 *  routing. Compound exercises hold their slot in the mesocycle for the
 *  full 4-week block; accessories rotate every 2 weeks. */
export type ExerciseTier = "compound" | "accessory" | "core" | "mobility";
export type ExerciseEquipment = "bodyweight" | "dumbbell" | "barbell" | "band" | "kettlebell" | "machine";
export type MovementPattern =
  | "squat"
  | "hinge"
  | "push-horizontal"
  | "push-vertical"
  | "pull-horizontal"
  | "pull-vertical"
  | "lunge"
  | "carry"
  | "core-anti-extension"
  | "core-anti-rotation"
  | "core-pelvic-floor"
  | "hip-mobility"
  | "thoracic-mobility"
  | "cardio";

export interface ExerciseDef {
  id: string;
  name: string;
  tier: ExerciseTier;
  pattern: MovementPattern;
  equipment: ExerciseEquipment;
  /** Coaching cue, surfaced under the exercise name. */
  cue: string;
  /** NASM corrective tags this movement contraindicates. The engine drops
   *  any exercise whose contraindications overlap with active flags. */
  contraindications: InjuryFlag[];
  /** Goals this exercise serves well. Drives prioritization, not filtering. */
  goalEmphasis: FitnessGoal[];
  /** GGS Pre/Postnatal V2 status. "avoid" pulls it from pregnant or
   *  early-postpartum splits. "core-restore" marks rehab-first movements
   *  surfaced before strength returns. */
  ggsPregnant: "safe" | "modify" | "avoid";
  ggsPostpartum: "safe" | "modify" | "avoid" | "core-restore";
}

/** A fully scheduled exercise inside a generated session. Includes
 *  prescription (sets, reps, weight or hold) computed from goal + week. */
export interface SessionExercise {
  exerciseId: string;
  name: string;
  tier: ExerciseTier;
  pattern: MovementPattern;
  equipment: ExerciseEquipment;
  cue: string;
  sets: number;
  reps: number | null;
  holdSec: number | null;
  /** Prescribed load in pounds. null for bodyweight or hold. */
  weight: number | null;
  /** Per-week note such as "Add 5 lb from last week" or "Hold form, no load". */
  progressionNote: string;
}

export interface GeneratedSession {
  /** Stable per-day id, e.g. "sess_2026_w17_d2". */
  id: string;
  /** ISO date for the day this session was generated for. */
  dateIso: string;
  weekday: WeekDay;
  /** Block label, e.g. "Lower body" or "Full body". */
  focus: string;
  durationMin: number;
  /** Mesocycle week 1-4 within the current compound block. */
  mesocycleWeek: 1 | 2 | 3 | 4;
  /** Accessory block A or B within the 2-week accessory rotation. */
  accessoryBlock: "A" | "B";
  exercises: SessionExercise[];
  /** Free text guidance from Coach Kiley for this session, derived from
   *  the season + injuries + week. Always cited if it pulls from GGS. */
  guidance: string;
}

export interface LiveAttendance {
  id: string;
  classId: string;
  userId: string;
  joinedAt: number;
  leftAt: number | null;
}

export interface EventRsvp {
  id: string;
  eventId: string;
  userId: string;
  createdAt: number;
}

export interface FoodLog {
  id: string;
  userId: string;
  meal: Meal;
  name: string;
  kcal: number;
  protein: number;
  fiber: number;
  servings: number;
  loggedAt: number;
}

export interface MealPlan {
  id: string;
  userId: string;
  weekStart: number; // Monday-first epoch ms
  servings: number;
  kidsPlate: boolean;
  meals: Array<{ day: number; meal: Meal; recipeId: string }>;
}

export interface Workout {
  id: string;
  userId: string;
  title: string;
  durationMin: number;
  intensity: "gentle" | "steady" | "strong";
  cyclePhase: CyclePhase | null;
  completedAt: number | null;
}

export type PrayerTheme =
  | "postpartum"
  | "body_image"
  | "motherhood"
  | "marriage"
  | "grief"
  | "faith"
  | "work"
  | "general";

export interface Prayer {
  id: string;
  userId: string;
  body: string;
  waters: number; // sister-prayers received (denormalized from PrayerLift count)
  createdAt: number;
  /** When true, the wall hides the author's name and avatar, showing "A sister". */
  anonymous?: boolean;
  /** Optional theme tag so members can filter the wall to their season. */
  theme?: PrayerTheme;
}

/** One sister tapping "I'm praying" on a prayer. Stored separately so the
 *  viewer's own state is recoverable and prayers can be undone. */
export interface PrayerLift {
  id: string;
  prayerId: string;
  userId: string;
  liftedAt: number;
}

/** A reply to a prayer. Either a short note of encouragement or a verse
 *  reference. Only the prayer author and the responder can see verse-only
 *  replies on the wall (others see the prayer count, not the replies). */
export interface PrayerReply {
  id: string;
  prayerId: string;
  userId: string;
  body: string;
  reference: string | null; // optional verse reference like "Psalm 27:14"
  createdAt: number;
}

export interface Mentee {
  id: string; // user id of the mentee
  coachId: string;
  name: string;
  initials: string;
  tier: Tier;
  streak: number;
  resting: boolean;
  restingDays: number;
  status: SisterStatus;
  lastCheckIn: string;
}

export interface JournalEntry {
  id: string;
  userId: string;
  prompt: string;
  body: string;
  verseRef: string | null;
  createdAt: number;
}

export interface ChatMessage {
  id: string;
  threadId: string; // slug e.g. "bethany"
  fromCoach: boolean;
  type: "text" | "voice" | "verse";
  body: string;
  durationSec?: number; // voice notes
  verseRef?: string;
  sentAt: number;
}

export type ExerciseKind = "weighted" | "bodyweight" | "hold";

/** A single recorded set of an exercise. Sets that have not been logged yet
 *  do not exist as rows; absence means the sister hasn't done that set. */
export interface WorkoutSet {
  id: string;
  userId: string;
  workoutId: string;       // logical workout ("strength-grace-lower")
  workoutTitle: string;    // denormalized for the strength card
  exerciseId: string;      // "goblet-squat"
  exerciseName: string;    // "Goblet squat"
  kind: ExerciseKind;
  setIndex: number;        // 1-based
  weightLb: number | null; // null for bodyweight
  reps: number | null;     // null for hold-based
  holdSec: number | null;  // null for rep-based
  isPR: boolean;           // computed at log time
  loggedAt: number;
}

/* ---------------------- Accountability partnerships ---------------------- */

export type PairStatus = "pending" | "active" | "completed" | "released";
export type CovenantGoalKind = "movement" | "nourishment" | "scripture" | "rest" | "custom";

/** A focused commitment two sisters hold each other to. 1 to 3 per pairing. */
export interface CovenantGoal {
  kind: CovenantGoalKind;
  text: string; // free text, e.g. "Move 4 days a week"
}

/** A pairing of two sisters walking together for a season. */
export interface AccountabilityPair {
  id: string;
  inviterId: string;          // who sent the invite
  inviteeId: string;          // who received it
  status: PairStatus;
  seasonDays: 30 | 60 | 90;
  covenant: CovenantGoal[];
  startedAt: number | null;   // null while pending
  endsAt: number | null;
  createdAt: number;
}

export type PingKind = "praying" | "won_morning" | "struggling";

/** A small one-tap encouragement between paired sisters. */
export interface PairPing {
  id: string;
  pairId: string;
  fromUserId: string;
  toUserId: string;
  kind: PingKind;
  sentAt: number;
}

/** A daily shared check-in. Both sisters answer the same prompt; both answers
 *  are visible only to the pair. */
export interface PairCheckIn {
  id: string;
  pairId: string;
  prompt: string;
  date: string; // YYYY-MM-DD, one shared row per day
  inviterAnswer: string | null;
  inviteeAnswer: string | null;
  createdAt: number;
}

/** A workout the sister has bookmarked to repeat. */
export interface SavedWorkout {
  id: string;
  userId: string;
  workoutId: string;
  title: string;
  focus: string;           // "Lower body & core"
  durationMin: number;
  movements: number;
  savedAt: number;
  lastCompletedAt: number | null;
  timesCompleted: number;
}

export interface CycleLog {
  id: string;
  userId: string;
  day: number;
  phase: CyclePhase;
  mood: "radiant" | "resting" | "restoring";
  notes: string;
  loggedAt: number;
}

export interface Seed {
  id: string;
  inviterId: string;
  name: string;
  initials: string;
  joined: number;
  tier: Tier;
  status: "rooted" | "flourishing" | "tending";
}

/* ----------------------------- Macros engine ------------------------------ */

export type ActivityLevel =
  | "sedentary"      // desk life, no formal movement
  | "light"          // walks plus 1-2 sessions / week
  | "moderate"       // 3-4 sessions / week, on her feet some
  | "active"         // 5-6 sessions / week, or chasing littles all day
  | "very_active";   // physical job + training

export type GoalDirection = "release" | "maintain" | "build";
export type GoalPace = "gentle" | "steady" | "strong";

export type SeasonKind = "none" | "ttc" | "pregnant" | "postpartum" | "nursing";
export type Trimester = 1 | 2 | 3;
export type FeedingMode = "exclusive" | "mixed" | "none";

export interface SeasonState {
  userId: string;
  /** Tag for the season she is in. Drives macros, movement, and scripture. */
  kind: SeasonKind;
  /** Pregnant: which trimester. */
  trimester: Trimester | null;
  /** Postpartum: weeks since birth. */
  weeksPostpartum: number | null;
  /** Nursing intensity (drives kcal bump). */
  feeding: FeedingMode;
  /** Postpartum return-to-movement gate. Kiley sets this. */
  clearedForMovement: boolean;
  /** ISO date for due date or birth date. */
  milestoneDate: string | null;
  /** When sister set or updated this. */
  updatedAt: number;
}

export interface MacroProfile {
  userId: string;
  /** Tag for the season she is in. Pre/postnatal handling is GGS V2
   *  aligned (Kiley is GGS Pre/Postnatal V2 certified). */
  seasonKind: SeasonKind;
  ageYears: number;
  heightIn: number;
  weightLb: number;
  activity: ActivityLevel;
  direction: GoalDirection;
  pace: GoalPace;
  updatedAt: number;
}

export type MacroMode = "auto" | "ratios" | "fixed";
export type MacroSource = "self" | "coach";

export interface MacroPlan {
  userId: string;
  mode: MacroMode;
  /** Final daily targets, always in absolute units. The editor writes both
   *  these and (for ratios) the percentage tuple so the UI can round-trip. */
  kcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  /** Percentage tuple (P,C,F). Sums to 100. Used when mode = ratios. */
  ratios: { p: number; c: number; f: number };
  /** Who set this plan and whether the sister can edit. "coach" + locked
   *  means Kiley walks with her on these for the season. */
  source: MacroSource;
  locked: boolean;
  /** Optional warm note from Kiley shown on the macros page. */
  coachNote: string | null;
  updatedAt: number;
}

export type MealBlock = "breakfast" | "lunch" | "dinner" | "snack";

export interface MealLog {
  id: string;
  userId: string;
  /** YYYY-MM-DD local date, mirrors pairCheckIns shape. */
  date: string;
  block: MealBlock;
  kcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  /** Optional one-line gratitude note: "How did this nourish you?" */
  note: string | null;
  loggedAt: number;
}

/* ============================ Scripture-at-log ============================= */

/**
 * MealVerse — a short scripture shown for ~3 seconds when a sister logs a
 * meal or completes her daily kcal goal. Surfaces are intentionally tiny:
 * one verse, one reference, soft fade. We never quote more than ~140 chars.
 *
 * `contexts` is a tag list. Picker chooses the verse whose context list best
 * matches the moment (block + macro shape + first-meal-of-day + daily total
 * crossing). When multiple match, we rotate using a per-user ring buffer of
 * recently-shown verseIds so a sister doesn't see the same verse twice.
 */
export type VerseContext =
  | "morning"          // breakfast block
  | "midday"           // lunch block
  | "evening"          // dinner block
  | "snack"            // snack block
  | "protein-rich"     // log >= 25g protein
  | "gentle"           // log < 12g protein, or low kcal
  | "first-meal"       // first log of the day
  | "daily-complete"   // crossing 90% of daily kcal goal
  | "general";         // fallback

export interface MealVerse {
  id: string;
  text: string;
  reference: string;
  contexts: VerseContext[];
}

/* ============================== Sabbath Rest =============================== */

/**
 * SabbathSettings — per-user designated weekly Sabbath. When `enabled` and
 * today matches `dayOfWeek`, the app shifts: Today shows a Sabbath hero card,
 * the streak displays as "Resting" (no break, no shame), gentle scripture
 * replaces nudges. We default Sundays for new sisters but never assume.
 *
 * 0 = Sunday … 6 = Saturday (matches `Date#getDay()`).
 */
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface SabbathSettings {
  userId: string;
  enabled: boolean;
  dayOfWeek: DayOfWeek;
  /** When true, streak chip reads "Resting" on the Sabbath, never resets. */
  pauseStreaks: boolean;
  /** When true, show the day's devotional automatically on the Sabbath. */
  devotionalReminder: boolean;
  updatedAt: number;
}

export interface SabbathDevotional {
  id: string;
  /** Which day-of-week this devotional belongs to (matches DayOfWeek). */
  dayOfWeek: DayOfWeek;
  title: string;
  passage: string;
  reference: string;
  /** Short reflection paragraph. Tone: gentle, presence-oriented. */
  reflection: string;
  /** A small, optional invitation, never a command. */
  invitation: string;
}

/* =========================== Verse of the Week ============================ */

/**
 * VerseOfWeek — a curated weekly scripture every sister sees site-wide.
 * Surfaces on Today, Sisters/community feed, and offers a check-in flow
 * ("I sat with this verse") with optional reflection sharing.
 *
 * `weekStartIso` is the YYYY-MM-DD of the Sunday that begins the week.
 * Lookup uses the start-of-week for the current local date.
 */
export type VerseTheme =
  | "strength"
  | "rest"
  | "identity"
  | "hope"
  | "surrender"
  | "presence";

export interface VerseOfWeek {
  id: string;
  weekStartIso: string;
  text: string;
  reference: string;
  theme: VerseTheme;
  /** Short curator reflection paragraph, tone: gentle, presence-oriented. */
  reflection: string;
  /** Small invitation, never a command. */
  invitation: string;
}

export interface VerseCheckIn {
  id: string;
  userId: string;
  weekStartIso: string;
  /** Optional reflection the sister wrote. May be empty. */
  reflection: string;
  /** When true, also post to the community wisdom feed (placeholder flag). */
  sharedToFeed: boolean;
  createdAt: number;
}

/* ====================== Nourish Together Challenges ====================== */

/**
 * Sisterhood challenges are threshold-based, never leaderboard-based. A
 * challenge invites every sister to a shared practice over a window of
 * days. Crossing the threshold by the end of the window means "completed."
 * Coming up short means "tending," never "failed," and the same scripture
 * still applies. There are no rankings, no streak comparisons, and no
 * public counts of who finished or fell short. Only the sister sees her
 * own count, and reflections she chooses to share land in a Together feed.
 *
 * The launch challenge (`isLaunchChallenge: true`) is special: every sister
 * who joins before its window closes is grandfathered in as a founding
 * member, with half-off pricing kept for life on the tier she chose.
 */

export type ChallengeDiscipline =
  | "meal"
  | "verse"
  | "prayer"
  | "movement"
  | "sabbath"
  | "any";

export interface ChallengeThreshold {
  /** Which discipline counts toward the threshold. "any" lets a sister
   *  pick any one practice each day. */
  discipline: ChallengeDiscipline;
  /** How many qualifying days she needs by the end of the window. */
  targetDays: number;
  /** Total length of the challenge window in days. Always >= targetDays
   *  by at least 7 so there's grace built in. */
  windowDays: number;
  /** Optional: max days she can rest without losing the practice. Pure
   *  guidance text, not enforced (we never break a streak). */
  graceDays: number;
}

/**
 * One scripture-anchored challenge a coach (Kiley) opens to her circle.
 * Many can be active at once. Sisters self-join and self-pace.
 */
export interface Challenge {
  id: string;
  /** URL slug, used in /together/:slug. */
  slug: string;
  /** Short title (3-5 words). */
  title: string;
  /** A single line under the title, the heartbeat of the challenge. */
  subtitle: string;
  /** Anchor scripture and reference. */
  scripture: string;
  scriptureRef: string;
  /** Why she would join. Two short paragraphs max. */
  invitation: string;
  /** Threshold rule. */
  threshold: ChallengeThreshold;
  /** Window start, inclusive (Unix ms). */
  startsAt: number;
  /** Window close, inclusive (Unix ms). After this, no new joins. */
  endsAt: number;
  /** Visual tone driving the card and detail accents. */
  tone: "forest" | "coral" | "gold" | "purple" | "fiber";
  /** Emoji pair shown in the hero. */
  hero: { primary: string; secondary?: string };
  /** When true, this is the app's launch challenge: founding-member
   *  grandfathering for half-off pricing applies. */
  isLaunchChallenge: boolean;
  /** Plain copy describing the founding-member offer. Only relevant when
   *  `isLaunchChallenge` is true. */
  foundingOffer?: string;
  /** When true, the challenge is featured on Today and Sisters. */
  featured: boolean;
  /** Author of the challenge (Kiley by default). */
  authorId: string;
  createdAt: number;
}

export interface ChallengeParticipant {
  id: string;
  challengeId: string;
  userId: string;
  joinedAt: number;
  /** Per-day check-ins counted toward this challenge. Each day counts
   *  at most once even if multiple disciplines fire. Stored as YYYY-MM-DD
   *  date strings to keep sabbath and timezone behavior simple. */
  daysCounted: string[];
  /** Set when the sister explicitly marks completed (or the system does
   *  on threshold crossing). */
  completedAt: number | null;
  /** True for sisters who joined the launch challenge before close. */
  isFoundingMember: boolean;
  /** Founding pricing tier she's grandfathered into (if applicable). */
  foundingTier?: Tier;
}

export interface ChallengeReflection {
  id: string;
  challengeId: string;
  userId: string;
  /** Public-by-default within the Together feed for this challenge. */
  body: string;
  /** Day count when this was posted, gives the post a gentle marker. */
  dayMarker: number;
  createdAt: number;
}

/* ============== Autoflow 7-day Welcome Sequence =========================== */

/**
 * Autoflow welcome sequence — a grace-paced 7-day onboarding journey.
 *
 * Philosophy (binding):
 *   - Never pushy. One invitation per day, the rest stay closed.
 *   - Sabbath days do not count against the sequence (clamped not skipped).
 *   - A sister can dismiss the autoflow at any time and revisit at
 *     /welcome-journey to peek ahead or replay completed days.
 *   - Tone: "Plant the seed", "Your why", "Bearing fruit". Never "Day 1 of 7"
 *     style automation language.
 */

export type WelcomeActionKind =
  | "navigate"          // routes the sister to a page
  | "toggle"            // opens an inline toggle/sheet (handled by host)
  | "complete-only";    // gentle acknowledgement, no nav

export interface WelcomeAction {
  /** Stable key for the action, used for completion tracking. */
  key: string;
  kind: WelcomeActionKind;
  /** Button label. "Set my Sabbath", "Open Garden", "Mark as planted". */
  label: string;
  /** For navigate actions, the hash route. */
  href?: string;
  /** Label shown after the action is completed. */
  completedLabel: string;
  /** Optional secondary copy under the button. */
  helper?: string;
}

export interface WelcomeStep {
  /** Stable key, e.g. "day-1". */
  id: string;
  /** 1-7. */
  day: number;
  /** Eyebrow above the title. */
  eyebrow: string;
  /** Display title in serif. */
  title: string;
  /** A short scripture anchor. */
  scripture: string;
  scriptureRef: string;
  /** Body paragraphs, separated by blank lines. */
  body: string;
  /** Tone drives the palette accent for the day. */
  tone: "forest" | "coral" | "gold" | "purple" | "fiber";
  /** Emoji pair for the day's hero. */
  hero: { primary: string; secondary?: string };
  /** One primary action and optional secondary. */
  primaryAction: WelcomeAction;
  secondaryAction?: WelcomeAction;
  /** A gentle one-liner shown when the day is complete. */
  completedReflection: string;
}

export interface WelcomeProgress {
  userId: string;
  /** Action keys the sister has completed. */
  completedActions: string[];
  /** Day numbers (1-7) marked as complete (any required action satisfied). */
  completedDays: number[];
  /** When she dismissed the autoflow card from Today, if at all. */
  dismissedAt: number | null;
  /**
   * Demo override day for Coach previews and screenshots. When set,
   * `welcomeCurrentStep` ignores time-based clamping and returns this
   * day. Set via `setDemoWelcomeDay`. Hannah uses this to peek the
   * journey since she signed up 60 days ago.
   */
  demoOverrideDay: number | null;
  createdAt: number;
}

/* ========================== Kiley's Pick for Your Season ================== */

/**
 * Curated meal suggestion, hand-picked by Coach Kiley for each life
 * season. Not algorithmic, not generated. A small library of recipes
 * paired with a personal note from Kiley explaining why this recipe,
 * for this sister, today.
 *
 * Why curated rather than algorithmic: faith-based sisters are not yet
 * comfortable trusting an algorithm with what to feed their family.
 * Kiley's voice carries the recommendation. The picks rotate by
 * day-of-year so the same pick stays stable all day across reloads,
 * but a sister can swap to tomorrow's pick if today's does not fit.
 *
 * Three season buckets, mirroring the Micronutrient Panels:
 *   - prenatal  (TTC, pregnant, postpartum)
 *   - nursing   (exclusive or mixed feeding)
 *   - general   (no active season)
 */
/**
 * Per-user record of legal acceptance. We record one row per user the first
 * time she accepts the bundle of Terms / Privacy / Disclaimer at signup, and
 * subsequent rows whenever versions change and we re-prompt. The client stores
 * a flat list, but in production this would be append-only audit data so we
 * can defend a claim that user X agreed at time T to version V.
 */
export interface LegalConsent {
  id: string;
  userId: string;
  /** Bundle version, e.g. "2026.04.27". Bumped when any binding doc changes. */
  version: string;
  acceptedTermsOfService: boolean;
  acceptedPrivacyPolicy: boolean;
  acceptedMedicalDisclaimer: boolean;
  acceptedCommunityGuidelines: boolean;
  /** Sister has confirmed she is 18 or older. */
  confirmedAdult: boolean;
  acceptedAt: number;
}

/* ----------------------------- Subscriptions -----------------------------
 * Per-sister billing record. We model the live state of her subscription
 * plus the founding-member flag so the renewal reminder system can apply
 * the right pricing and grace period. Apple/Google in-app subs use
 * platform receipts: those are mirrored here for display only, the
 * source of truth is Apple/Google for store-bought subs.
 * ------------------------------------------------------------------------ */
export type SubscriptionCycle = "monthly" | "annual";
export type SubscriptionStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "canceled";
export type SubscriptionPlatform = "web" | "apple" | "google";

export interface Subscription {
  userId: string;
  tier: Tier;
  cycle: SubscriptionCycle;
  /** True if she joined inside the founding-member window. Locks her into
   *  half-off pricing for life so long as her subscription stays active. */
  isFoundingMember: boolean;
  status: SubscriptionStatus;
  /** Where the subscription was purchased. Determines whether we render
   *  cancel UI in-app (web) or send her to the platform settings (apple/google). */
  platform: SubscriptionPlatform;
  currentPeriodStart: number;
  /** Next renewal date in epoch ms. */
  currentPeriodEnd: number;
  /** Set during a free trial. Null otherwise. */
  trialEndsAt: number | null;
  /** Price actually charged this cycle in cents. Reflects founding-member
   *  half-off when applicable. */
  priceCents: number;
  /** True when the sister has scheduled a cancel that takes effect at the
   *  end of the current paid period (per ToS Section 6). */
  cancelAtPeriodEnd: boolean;
  /** Last four digits of the card on file, for display only. */
  paymentMethodLast4: string;
  startedAt: number;
}

/* ----------------------------- Renewal reminders -------------------------
 * Renewal reminder rows are scheduled in advance per Subscription &
 * Refund Policy Section 4.3:
 *   - annual subs: a reminder 7 days before the renewal date
 *   - free trials of 32+ days: a reminder 3 days before conversion
 *   - founding-member payment failures: a 14-day, 3-email grace window
 * Each row tracks the scheduled time and whether we have actually sent.
 * ------------------------------------------------------------------------ */
export type RenewalReminderKind =
  | "annual-7day"
  | "trial-3day"
  | "founding-grace-1"
  | "founding-grace-2"
  | "founding-grace-3";

export interface RenewalReminder {
  id: string;
  userId: string;
  kind: RenewalReminderKind;
  /** When the reminder is scheduled to be delivered. */
  scheduledFor: number;
  /** The renewal/conversion date the reminder is about. */
  renewalDate: number;
  /** Amount in cents we will charge on that renewal. */
  amountCents: number;
  /** Channel mix; mirrors what the user has in NotificationPrefs. */
  channel: "email" | "push" | "both";
  /** Set to the timestamp the reminder was actually delivered, null if pending. */
  sentAt: number | null;
  createdAt: number;
}

/* ----------------------------- Coach notifications -----------------------
 * Coach Kiley composes encouragement, scripture, class reminders, and
 * announcements for her sisters. Each row captures who she targeted,
 * what she sent, and when. Recipients are computed at send time so the
 * audit row reflects what actually went out.
 * ------------------------------------------------------------------------ */
export type CoachNotificationAudience =
  | "all"
  | "tier"
  | "single"
  | "founding-members"
  | "needs-love";
export type CoachNotificationCategory =
  | "scripture"
  | "encouragement"
  | "class-reminder"
  | "announcement";

export interface CoachNotification {
  id: string;
  authorId: string;
  audience: CoachNotificationAudience;
  /** Used when audience === "tier". */
  audienceTier?: Tier;
  /** Used when audience === "single". */
  audienceUserId?: string;
  title: string;
  body: string;
  category: CoachNotificationCategory;
  /** Future-dated scheduledFor means queued. Null means send immediately. */
  scheduledFor: number | null;
  sentAt: number | null;
  /** Snapshot of how many sisters the notification reached. */
  recipientCount: number;
  createdAt: number;
}

/* ----------------------------- Notifications ------------------------------
 * Per-sister notification preferences. We model the OS-level permission and
 * five granular per-category opt-outs that mirror the Privacy Policy's push
 * notification section. "transactional" notifications (billing, security)
 * are always sent if permission is granted, regardless of category toggles.
 * ------------------------------------------------------------------------ */
export type NotificationPermissionStatus = "unknown" | "granted" | "denied";

export interface NotificationPrefs {
  userId: string;
  /** Mirrors the OS-level permission. "unknown" means we have not asked yet. */
  permissionStatus: NotificationPermissionStatus;
  /** Daily scripture reminder, with a user-chosen time of day (HH:mm 24h). */
  scriptureReminders: boolean;
  scriptureTime: string;
  /** Replies, hearts, and new prayers on the Prayer Wall. */
  prayerWallReplies: boolean;
  /** Weekly check-in nudges and streak reminders. */
  weeklyCheckIns: boolean;
  /** Direct coaching messages from Coach Kiley. */
  coachingMessages: boolean;
  /** Live class start, community events, founding-member releases. */
  communityAndLive: boolean;
  updatedAt: number;
}

export interface KileySeasonPick {
  /** Stable id, e.g. "ksp_prenatal_salmon". */
  id: string;
  /** Which season bucket this pick belongs to. */
  panelKey: MicronutrientPanelKey;
  /** Recipe this pick recommends, must exist in the recipe seed. */
  recipeId: string;
  /** Kiley's personal note, two to four sentences. Why this recipe,
   *  this season. Tone: warm coach, not clinical, not pushy. */
  kileyNote: string;
  /** Anchor scripture pairing for the pick. */
  scripture: string;
  scriptureRef: string;
  /** What Kiley wants the sister to focus on as she eats this. */
  focus: string;
}

/* ========================== Monthly Garden Walk =========================== */

/**
 * Monthly Garden Walk, a Fruit-of-the-Spirit movement challenge.
 *
 * Each month rotates through one fruit (Galatians 5:22-23). A sister
 * logs gentle daily walks, 5 minutes or more of intentional movement
 * paired with a one-line gratitude or intention. Threshold-based, not
 * leaderboard. The goal is 20 walks in the month, but every walk is
 * celebrated, even if she logs only seven.
 *
 * Why monthly: a season long enough to feel rhythm, short enough that
 * starting late is not starting too late. Sisters can join the walk
 * any day of the month.
 *
 * Why walks: the most accessible movement in pregnancy, postpartum,
 * nursing, and recovery. A stroller walk counts. A walk to the mailbox
 * counts. A pace around the kitchen with a fussy newborn counts.
 */
export type FruitOfSpirit =
  | "love"
  | "joy"
  | "peace"
  | "patience"
  | "kindness"
  | "goodness"
  | "faithfulness"
  | "gentleness"
  | "self-control";

export interface GardenWalkMonth {
  /** Stable month id, e.g. "gw_2026_04" for April 2026. */
  id: string;
  /** Year and 1-indexed month for sorting and label rendering. */
  year: number;
  month: number;
  /** Fruit of the Spirit being walked this month. */
  fruit: FruitOfSpirit;
  /** Friendly month label: "April, the month of Patience". */
  title: string;
  /** Single-line invitation, shown on the card and the page. */
  invitation: string;
  /** Anchor scripture for the month. */
  scripture: string;
  scriptureRef: string;
  /** Daily prompts cycled through the month, one per day, 7+ entries. */
  dailyPrompts: string[];
  /** Soft target for the threshold celebration, default 20 walks. */
  targetWalks: number;
  /** Visual tone aligned with the month's fruit. */
  tone: "forest" | "coral" | "purple" | "gold" | "fiber";
  /** Glyph and accent emoji for hero ornaments. */
  hero: { primary: string; secondary: string };
}

/** A single logged walk. Walks are intentionally lightweight, no GPS,
 *  no pace tracking, just minutes and a one-line reflection. */
export interface GardenWalkLog {
  id: string;
  userId: string;
  /** Garden Walk month id this walk belongs to. Computed at log time. */
  monthId: string;
  /** Local-day key YYYY-MM-DD, used to dedupe a sister's daily walk in
   *  the cards but not enforced as unique (a sister can log a morning
   *  walk and an evening walk separately if she wishes). */
  dateKey: string;
  /** Self-reported minutes, capped 5..120 in the UI but not enforced. */
  minutes: number;
  /** One-line gratitude or intention. Optional, often left blank. */
  reflection: string;
  /** When true, the public feed hides her name and avatar. */
  anonymous: boolean;
  loggedAt: number;
}

/* ====================== Life-Season Micronutrient Panels ================== */

/**
 * Curated micronutrient guidance per life season. Not a substitute for
 * Coach Kiley's plan or a provider's care, but a gentle map of what to
 * prioritize this season. Tone: encouraging, never clinical scolding.
 *
 * Three panels ship at launch:
 *   - prenatal  (TTC, pregnant, postpartum recovery)
 *   - nursing   (exclusive or mixed feeding)
 *   - general   (no active season, rhythm of ordinary care)
 */
export type MicronutrientPanelKey = "prenatal" | "nursing" | "general";

export type MicronutrientTone = "forest" | "coral" | "purple" | "gold" | "fiber";

export interface Micronutrient {
  /** Sister-friendly name (e.g. "Folate", not "Vitamin B9"). */
  name: string;
  /** Daily target written for humans (e.g. "600 mcg", "27 mg"). */
  targetLabel: string;
  /** Why it matters in this season, two sentences max. */
  why: string;
  /** 3 to 5 dietary sources, ordered by sister-recognizability. */
  sources: string[];
  /** Visual tone for the chip on its row. */
  tone: MicronutrientTone;
  /** Optional one-line caveat (e.g. "Pair with vitamin C for absorption"). */
  pairing?: string;
}

export interface MicronutrientPanel {
  key: MicronutrientPanelKey;
  title: string;
  /** Sub-line under the title, gentle context. */
  subtitle: string;
  /** Scripture pairing the panel rests on. */
  scripture: string;
  scriptureRef: string;
  /** Ordered list of priority micronutrients for this season. */
  items: Micronutrient[];
  /** A small footer note acknowledging the limits of generic guidance. */
  footer: string;
}

/* =============================== Recipes ================================== */

/** Award tier earned by a recipe. Tiers are framed as care given, not status:
 *  the chip reads "has nourished N sisters". Loved is only counted AFTER a
 *  sister actually cooked the recipe (planned then eaten then loved). */
export type RecipeAwardTier =
  | "none"            // 0 to 4 lovedCount
  | "sprouted"        // 5+ sisters
  | "rooted"          // 25+
  | "flourishing"     // 100+
  | "garden-favorite";// 500+

export type RecipeSeasonTag =
  | "pregnant"
  | "postpartum"
  | "nursing"
  | "family"
  | "quick";

/* ---------------------- Pregnancy/Nursing Safety Flags -------------------- */

/**
 * Safety flags surfaced on recipes to protect sisters in tender seasons.
 * Each flag maps to an advisory in `SAFETY_ADVISORIES` with a plain
 * explanation and a gentle alternative. Tone is informational, never alarming.
 * Coach Kiley and a sister's provider always override.
 */
export type SafetyFlag =
  | "high-mercury"   // king mackerel, tilefish, swordfish, bigeye tuna
  | "raw-fish"       // sushi, sashimi, ceviche
  | "raw-eggs"       // homemade mayo, hollandaise, runny yolks
  | "deli-meat"      // cold cuts, pate, hot dogs (listeria risk)
  | "soft-cheese"    // unpasteurized brie, queso fresco, blue cheese
  | "alcohol"        // wine, beer, spirits in cooking or to drink
  | "high-caffeine"  // > 200 mg per serving
  | "liver";         // organ meat, very high vitamin A

export type SafetySeverity = "caution" | "avoid";

export interface SafetyAdvisory {
  flag: SafetyFlag;
  /** Short label shown on the badge chip. */
  label: string;
  /** Severity scoped to the affected seasons. */
  severity: SafetySeverity;
  /** Which seasons this advisory applies to. */
  seasonsAffected: SeasonKind[];
  /** Plain explanation, two sentences max. No clinical scolding. */
  explanation: string;
  /** Gentle alternative suggestion. */
  alternative: string;
}

export type RecipeSource = "kiley" | "sister" | "imported";

export interface RecipeIngredient {
  qty: number;
  unit: string;
  item: string;
}

export interface Recipe {
  id: string;
  authorId: string;            // user id of who created or imported it
  title: string;
  description: string;
  /** Hero gradient string used by RecipeCard background. Example:
   *  "radial-gradient(120% 80% at 50% 30%, #6b8e6f 0%, #2C6248 60%, #163828 100%)" */
  heroGradient: string;
  /** Decorative emojis for the gradient hero. */
  heroEmojis: string[];
  /** Optional hero photo path served from /public. When null we fall back to the gradient. */
  heroImageUrl: string | null;
  servings: number;
  durationMin: number;
  kcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  fiberG: number;
  ingredients: RecipeIngredient[];
  steps: { title: string; body: string }[];
  seasonTags: RecipeSeasonTag[];
  /** Safety advisories that apply to this recipe. Filtered by season at render. */
  safetyFlags: SafetyFlag[];
  scripturePairing: string | null;  // optional verse + reference
  source: RecipeSource;
  sourceUrl: string | null;
  /** Count of sisters who marked "loved this" AFTER eating. Drives award tier. */
  lovedCount: number;
  awardTier: RecipeAwardTier;
  /** Visible to the whole community feed (Trending) when public is true.
   *  Sister-only or pair-only recipes stay false. */
  public: boolean;
  /** Imported or community-shared recipes start in pending review for future
   *  moderation queue. Kiley's seed recipes are not pending. */
  pendingReview: boolean;
  createdAt: number;
}

export interface SavedRecipe {
  id: string;
  userId: string;
  recipeId: string;
  savedAt: number;
}

/**
 * Pick a generic hero image based on title keywords and season tags. Returns a
 * relative URL into /public/recipes. Falls back to a generic dinner if nothing
 * matches. Used by recipe-creation paths that lack a curated hero image.
 */
export function pickGenericHeroImage(
  tags: ReadonlyArray<string>,
  title: string,
): string {
  const t = (title || "").toLowerCase();
  const tagSet = new Set((tags || []).map((s) => s.toLowerCase()));
  // Title keyword matches first, most specific to least.
  const map: Array<[RegExp | string, string]> = [
    [/salmon/, "recipes/generic_dinner_salmon.webp"],
    [/(fish|cod|halibut|trout)/, "recipes/generic_dinner_fish.webp"],
    [/(meatball|zoodle|zucchini noodle)/, "recipes/generic_dinner_meatballs.webp"],
    [/(stir fry|stir-fry|stirfry)/, "recipes/generic_dinner_stirfry.webp"],
    [/curry/, "recipes/generic_dinner_curry.webp"],
    [/(stew|braise|slow cooker)/, "recipes/generic_dinner_stew.webp"],
    [/(pasta|spaghetti|penne|noodle)/, "recipes/generic_dinner_pasta.webp"],
    [/(sheet ?pan|tray bake)/, "recipes/generic_dinner_sheetpan.webp"],
    [/skillet/, "recipes/generic_dinner_skillet.webp"],
    [/chicken/, "recipes/generic_dinner_chicken.webp"],
    [/(oat|oatmeal|porridge)/, "recipes/generic_breakfast_oatmeal.webp"],
    [/(egg|scramble|omelet|frittata)/, "recipes/generic_breakfast_eggs.webp"],
    [/(smoothie|acai)/, "recipes/generic_breakfast_smoothie.webp"],
    [/(pancake|waffle)/, "recipes/generic_breakfast_pancakes.webp"],
    [/chia/, "recipes/generic_breakfast_chia.webp"],
    [/(grain bowl|farro|barley)/, "recipes/generic_lunch_grainbowl.webp"],
    [/(wrap|burrito wrap)/, "recipes/generic_lunch_wrap.webp"],
    [/sandwich/, "recipes/generic_lunch_sandwich.webp"],
    [/(buddha|tofu bowl)/, "recipes/generic_bowl_buddha.webp"],
    [/(burrito bowl|taco bowl)/, "recipes/generic_bowl_burrito.webp"],
    [/(poke|tuna bowl)/, "recipes/generic_bowl_poke.webp"],
    [/(quinoa salad|tabbouleh)/, "recipes/generic_salad_quinoa.webp"],
    [/(kale salad|kale)/, "recipes/generic_salad_kale.webp"],
    [/(mediterranean|greek salad)/, "recipes/generic_salad_mediterranean.webp"],
    [/(cobb|chopped salad)/, "recipes/generic_salad_chopped.webp"],
    [/quinoa/, "recipes/generic_side_quinoa.webp"],
    [/(lentil soup|red lentil)/, "recipes/generic_soup_lentil.webp"],
    [/(chicken soup|chicken noodle)/, "recipes/generic_soup_chicken.webp"],
    [/(minestrone|tuscan soup)/, "recipes/generic_soup_minestrone.webp"],
    [/(soup|broth|bisque|chowder)/, "recipes/generic_lunch_soup.webp"],
    [/(squash|acorn)/, "recipes/generic_holiday_squash.webp"],
    [/(turkey roast|thanksgiving|holiday)/, "recipes/generic_holiday_turkey.webp"],
    [/(roasted vegetable|roasted veg|sheet of vegetables)/, "recipes/generic_side_roastedveg.webp"],
    [/(energy ball|protein ball|energy bite)/, "recipes/generic_snack_energybites.webp"],
    [/parfait/, "recipes/generic_snack_parfait.webp"],
    [/hummus/, "recipes/generic_snack_hummus.webp"],
    [/(apple slices|apple snack)/, "recipes/generic_snack_apple.webp"],
    [/(cottage cheese)/, "recipes/generic_snack_cottage.webp"],
  ];
  for (const [pat, url] of map) {
    if (typeof pat === "string" ? t.includes(pat) : pat.test(t)) return url;
  }
  // Tag-based fallback.
  if (tagSet.has("breakfast")) return "recipes/generic_breakfast_oatmeal.webp";
  if (tagSet.has("lunch")) return "recipes/generic_lunch_grainbowl.webp";
  if (tagSet.has("snack")) return "recipes/generic_snack_energybites.webp";
  if (tagSet.has("holiday")) return "recipes/generic_holiday_turkey.webp";
  if (tagSet.has("quick")) return "recipes/generic_dinner_skillet.webp";
  return "recipes/generic_dinner_chicken.webp";
}

export interface RecipeLove {
  id: string;
  userId: string;
  recipeId: string;
  lovedAt: number;
}

/* ----------------------------- Pair rituals ------------------------------- */

/** A scripture the pair shares for the week. Both sisters see the same verse
 *  on their pair surface and can react with a heart, an amen, or leave a note. */
export type ScriptureReactionKind = "heart" | "amen" | "praying";
export interface PairSharedScripture {
  id: string;
  pairId: string;
  weekStart: string;          // YYYY-MM-DD, the Monday of the week
  reference: string;          // e.g. "Psalm 27:14"
  text: string;               // the verse text
  inviterReaction: ScriptureReactionKind | null;
  inviteeReaction: ScriptureReactionKind | null;
  inviterNote: string | null;
  inviteeNote: string | null;
  createdAt: number;
}

/** A recipe the pair has agreed to cook together this week. Either sister can
 *  pick the recipe; both can mark themselves as having cooked it. */
export interface PairSharedRecipe {
  id: string;
  pairId: string;
  weekStart: string;          // YYYY-MM-DD
  recipeId: string;
  pickedBy: string;           // userId who picked it
  inviterCookedAt: number | null;
  inviteeCookedAt: number | null;
  createdAt: number;
}

/** An auto-match suggestion the system surfaces to an unpaired sister. She
 *  can accept (creates an active pair, no covenant negotiation) or skip. */
export interface MatchSuggestion {
  id: string;
  forUserId: string;
  candidateId: string;
  reason: string;             // "You're both in the postpartum season"
  createdAt: number;
  status: "open" | "accepted" | "skipped";
}

/** Convex-shaped schema descriptors. In real Convex these live in
 *  `convex/schema.ts` using `defineTable` / `v` validators. */
export const Schema = {
  users:           { primaryKey: "id" as const },
  foodLogs:        { primaryKey: "id" as const, indexes: ["userId", "meal", "loggedAt"] },
  mealPlans:       { primaryKey: "id" as const, indexes: ["userId", "weekStart"] },
  workouts:        { primaryKey: "id" as const, indexes: ["userId", "completedAt"] },
  prayers:         { primaryKey: "id" as const, indexes: ["userId", "createdAt"] },
  prayerLifts:     { primaryKey: "id" as const, indexes: ["prayerId", "userId"] },
  prayerReplies:   { primaryKey: "id" as const, indexes: ["prayerId", "userId"] },
  mentees:         { primaryKey: "id" as const, indexes: ["coachId"] },
  journalEntries:  { primaryKey: "id" as const, indexes: ["userId", "createdAt"] },
  chatMessages:    { primaryKey: "id" as const, indexes: ["threadId", "sentAt"] },
  cycleLogs:       { primaryKey: "id" as const, indexes: ["userId", "day"] },
  seeds:           { primaryKey: "id" as const, indexes: ["inviterId"] },
  workoutSets:     { primaryKey: "id" as const, indexes: ["userId", "loggedAt", "exerciseId"] },
  savedWorkouts:   { primaryKey: "id" as const, indexes: ["userId", "workoutId"] },
  pairs:           { primaryKey: "id" as const, indexes: ["inviterId", "inviteeId", "status"] },
  pairPings:       { primaryKey: "id" as const, indexes: ["pairId", "sentAt"] },
  pairCheckIns:    { primaryKey: "id" as const, indexes: ["pairId", "date"] },
  liveClasses:     { primaryKey: "id" as const, indexes: ["coachId", "status", "scheduledFor"] },
  liveChat:        { primaryKey: "id" as const, indexes: ["classId", "sentAt"] },
  communityEvents: { primaryKey: "id" as const, indexes: ["zipCode", "source", "startsAt"] },
  feedPosts:       { primaryKey: "id" as const, indexes: ["userId", "kind", "createdAt"] },
  classReminders:  { primaryKey: "id" as const, indexes: ["userId", "classId"] },
  feedComments:    { primaryKey: "id" as const, indexes: ["postId", "createdAt"] },
  liveAttendance:  { primaryKey: "id" as const, indexes: ["classId", "userId"] },
  eventRsvps:      { primaryKey: "id" as const, indexes: ["eventId", "userId"] },
  macroProfiles:   { primaryKey: "userId" as const },
  macroPlans:      { primaryKey: "userId" as const },
  mealLogs:        { primaryKey: "id" as const, indexes: ["userId", "date", "block"] },
  seasonStates:    { primaryKey: "userId" as const },
  recipes:         { primaryKey: "id" as const, indexes: ["authorId", "public", "lovedCount"] },
  savedRecipes:    { primaryKey: "id" as const, indexes: ["userId", "recipeId"] },
  recipeLoves:     { primaryKey: "id" as const, indexes: ["userId", "recipeId"] },
  pairSharedScriptures: { primaryKey: "id" as const, indexes: ["pairId", "weekStart"] },
  pairSharedRecipes:    { primaryKey: "id" as const, indexes: ["pairId", "weekStart"] },
  matchSuggestions:     { primaryKey: "id" as const, indexes: ["forUserId", "status"] },
  mealVerses:           { primaryKey: "id" as const, indexes: ["contexts"] },
  verseHistory:         { primaryKey: "id" as const, indexes: ["userId", "shownAt"] },
  sabbathSettings:      { primaryKey: "userId" as const },
  sabbathDevotionals:   { primaryKey: "id" as const, indexes: ["dayOfWeek"] },
  versesOfWeek:         { primaryKey: "id" as const, indexes: ["weekStartIso"] },
  verseCheckIns:        { primaryKey: "id" as const, indexes: ["userId", "weekStartIso"] },
  challenges:           { primaryKey: "id" as const, indexes: ["slug", "endsAt"] },
  challengeParticipants:{ primaryKey: "id" as const, indexes: ["challengeId", "userId"] },
  challengeReflections: { primaryKey: "id" as const, indexes: ["challengeId", "createdAt"] },
  welcomeProgress:      { primaryKey: "userId" as const },
  gardenWalkMonths:     { primaryKey: "id" as const, indexes: ["year", "month"] },
  gardenWalkLogs:       { primaryKey: "id" as const, indexes: ["userId", "monthId", "loggedAt"] },
  kileySeasonPicks:     { primaryKey: "id" as const, indexes: ["panelKey"] },
  legalConsents:        { primaryKey: "id" as const, indexes: ["userId"] },
  notificationPrefs:    { primaryKey: "userId" as const },
  subscriptions:        { primaryKey: "userId" as const },
  renewalReminders:     { primaryKey: "id" as const, indexes: ["userId", "sentAt"] },
  coachNotifications:   { primaryKey: "id" as const, indexes: ["authorId", "createdAt"] },
  sisterhoodPosts:      { primaryKey: "id" as const, indexes: ["authorId", "kind", "status", "createdAt"] },
  sisterhoodReactions:  { primaryKey: "id" as const, indexes: ["postId", "userId", "kind"] },
  sisterhoodComments:   { primaryKey: "id" as const, indexes: ["postId", "createdAt"] },
  sisterhoodReports:    { primaryKey: "id" as const, indexes: ["target", "targetId", "status"] },
  networkProfiles:      { primaryKey: "userId" as const, indexes: ["isOpenIn", "lifeStage"] },
  networkIntros:        { primaryKey: "id" as const, indexes: ["fromUserId", "toUserId", "status"] },
  networkMessages:      { primaryKey: "id" as const, indexes: ["introId", "sentAt"] },
  movementProfiles:     { primaryKey: "userId" as const },
};

/* =============================== Tables =================================== */

interface VerseHistoryEntry {
  id: string;
  userId: string;
  verseId: string;
  shownAt: number;
  /** Optional context tag that triggered the show, used for analytics. */
  reason: VerseContext | "daily-complete";
}

interface DB {
  users: User[];
  foodLogs: FoodLog[];
  mealPlans: MealPlan[];
  workouts: Workout[];
  workoutSets: WorkoutSet[];
  savedWorkouts: SavedWorkout[];
  pairs: AccountabilityPair[];
  pairPings: PairPing[];
  pairCheckIns: PairCheckIn[];
  prayers: Prayer[];
  prayerLifts: PrayerLift[];
  prayerReplies: PrayerReply[];
  mentees: Mentee[];
  journalEntries: JournalEntry[];
  chatMessages: ChatMessage[];
  cycleLogs: CycleLog[];
  seeds: Seed[];
  liveClasses: LiveClass[];
  liveChat: LiveChatMessage[];
  communityEvents: CommunityEvent[];
  feedPosts: FeedPost[];
  classReminders: { id: string; userId: string; classId: string; createdAt: number }[];
  feedComments: FeedComment[];
  liveAttendance: LiveAttendance[];
  eventRsvps: EventRsvp[];
  macroProfiles: MacroProfile[];
  macroPlans: MacroPlan[];
  mealLogs: MealLog[];
  seasonStates: SeasonState[];
  recipes: Recipe[];
  savedRecipes: SavedRecipe[];
  recipeLoves: RecipeLove[];
  pairSharedScriptures: PairSharedScripture[];
  pairSharedRecipes: PairSharedRecipe[];
  matchSuggestions: MatchSuggestion[];
  mealVerses: MealVerse[];
  verseHistory: VerseHistoryEntry[];
  sabbathSettings: SabbathSettings[];
  sabbathDevotionals: SabbathDevotional[];
  versesOfWeek: VerseOfWeek[];
  verseCheckIns: VerseCheckIn[];
  challenges: Challenge[];
  challengeParticipants: ChallengeParticipant[];
  challengeReflections: ChallengeReflection[];
  welcomeProgress: WelcomeProgress[];
  gardenWalkMonths: GardenWalkMonth[];
  gardenWalkLogs: GardenWalkLog[];
  kileySeasonPicks: KileySeasonPick[];
  legalConsents: LegalConsent[];
  notificationPrefs: NotificationPrefs[];
  subscriptions: Subscription[];
  renewalReminders: RenewalReminder[];
  coachNotifications: CoachNotification[];
  sisterhoodPosts: SisterhoodPost[];
  sisterhoodReactions: SisterhoodReaction[];
  sisterhoodComments: SisterhoodComment[];
  sisterhoodReports: SisterhoodReport[];
  networkProfiles: NetworkProfile[];
  networkIntros: NetworkIntro[];
  networkMessages: NetworkMessage[];
  movementProfiles: MovementProfile[];
}

const now = () => Date.now();

/** Seed Hannah's workout history so the strength card has substance.
 *  We model 9 logged sessions over the last ~28 days with progressive
 *  overload on her three main lifts and held bodyweight finishers. */
function seedWorkoutSets(userId: string): WorkoutSet[] {
  const day = 1000 * 60 * 60 * 24;
  const wid = "strength-grace-lower";
  const wt = "Strength & Grace · Lower";
  const sets: WorkoutSet[] = [];
  let n = 0;
  const push = (
    daysAgo: number, exId: string, exName: string, kind: ExerciseKind,
    setIndex: number, weight: number | null, reps: number | null, hold: number | null,
    isPR = false,
  ) => {
    n += 1;
    sets.push({
      id: `ws_${n}`, userId, workoutId: wid, workoutTitle: wt,
      exerciseId: exId, exerciseName: exName, kind,
      setIndex, weightLb: weight, reps, holdSec: hold, isPR,
      loggedAt: now() - day * daysAgo + 1000 * 60 * 60 * 7,
    });
  };
  // Sessions across April: days ago 27, 23, 20, 16, 13, 9, 6, 3, 1
  // Goblet squat ramps 18 → 25 lb (PR on day 3)
  // RDL ramps 30 → 40 lb (PR on day 1)
  // Single-arm row ramps 12 → 18 lb (PR on day 6)
  const sessions: Array<{ days: number; gob: number; rdl: number; row: number; goblPr?: boolean; rdlPr?: boolean; rowPr?: boolean; lighter?: boolean }> = [
    { days: 27, gob: 18, rdl: 30, row: 12 },
    { days: 23, gob: 18, rdl: 30, row: 12 },
    { days: 20, gob: 20, rdl: 32, row: 12 },
    { days: 16, gob: 20, rdl: 32, row: 15 },
    { days: 13, gob: 22, rdl: 35, row: 15, lighter: false },
    { days: 9,  gob: 22, rdl: 35, row: 15 },
    { days: 6,  gob: 22, rdl: 38, row: 18, rowPr: true },
    { days: 3,  gob: 25, rdl: 38, row: 18, goblPr: true },
    { days: 1,  gob: 25, rdl: 40, row: 18, rdlPr: true },
  ];
  for (const s of sessions) {
    // Goblet squat: 4 × 10
    for (let i = 1; i <= 4; i += 1) push(s.days, "goblet-squat", "Goblet squat", "weighted", i, s.gob, 10, null, !!s.goblPr && i === 4);
    // Romanian deadlift: 4 × 8
    for (let i = 1; i <= 4; i += 1) push(s.days, "romanian-deadlift", "Romanian deadlift", "weighted", i, s.rdl, 8, null, !!s.rdlPr && i === 4);
    // Single-arm row: 3 × 12
    for (let i = 1; i <= 3; i += 1) push(s.days, "single-arm-row", "Single-arm row", "weighted", i, s.row, 12, null, !!s.rowPr && i === 3);
    // Glute bridge hold: 3 × 30s
    for (let i = 1; i <= 3; i += 1) push(s.days, "glute-bridge-hold", "Glute bridge hold", "hold", i, null, null, 30 + (s.days < 7 ? 10 : 0));
    // Bird dog: 3 × 10 BW
    for (let i = 1; i <= 3; i += 1) push(s.days, "bird-dog", "Bird dog", "bodyweight", i, null, 10, null);
  }
  return sets;
}

const SEED_DB: DB = {
  users: [
    {
      id: "u_hannah",
      name: "Hannah Doyle",
      email: "hannah@example.com",
      initials: "HD",
      tier: "Rooted",
      role: "member",
      lifeStage: "married",
      goals: ["energy", "scripture-rooted", "family-meals"],
      translation: "ESV",
      streak: 12,
      resting: false,
      restingDays: 0,
      cycleDay: 14,
      zipCode: "37215",
      city: "Nashville, TN",
      avatarDataUrl: null,
      instagram: "hannahd.flourish",
      passwordHash: "demo-hash", // mock value, never used for actual auth
      createdAt: now() - 1000 * 60 * 60 * 24 * 60,
    },
  ],
  foodLogs: [],
  mealPlans: [],
  workouts: [],
  workoutSets: seedWorkoutSets("u_hannah"),
  savedWorkouts: [
    {
      id: "sw_1", userId: "u_hannah", workoutId: "strength-grace-lower",
      title: "Strength & Grace · Lower", focus: "Lower body & core",
      durationMin: 32, movements: 5,
      savedAt: now() - 1000 * 60 * 60 * 24 * 25,
      lastCompletedAt: now() - 1000 * 60 * 60 * 36,
      timesCompleted: 5,
    },
    {
      id: "sw_2", userId: "u_hannah", workoutId: "sunrise-flow",
      title: "Sunrise Flow · Hips & Spine", focus: "Mobility & breath",
      durationMin: 18, movements: 7,
      savedAt: now() - 1000 * 60 * 60 * 24 * 18,
      lastCompletedAt: now() - 1000 * 60 * 60 * 24 * 4,
      timesCompleted: 3,
    },
    {
      id: "sw_3", userId: "u_hannah", workoutId: "upper-pull",
      title: "Upper Pull · Posture & Strength", focus: "Back & shoulders",
      durationMin: 28, movements: 6,
      savedAt: now() - 1000 * 60 * 60 * 24 * 11,
      lastCompletedAt: now() - 1000 * 60 * 60 * 24 * 6,
      timesCompleted: 2,
    },
  ],
  prayers: [
    {
      id: "p1",
      userId: "u_rebecca",
      body: "Wisdom for a hard conversation with my husband tonight. Pray for soft words and an open heart on both sides.",
      waters: 14,
      createdAt: now() - 1000 * 60 * 30,
      theme: "marriage",
    },
    {
      id: "p2",
      userId: "u_joanna",
      body: "Strength to keep showing up at the gym. Some weeks my body feels like a stranger and I want to hide.",
      waters: 9,
      createdAt: now() - 1000 * 60 * 90,
      theme: "body_image",
    },
    {
      id: "p3",
      userId: "u_bethany",
      body: "Three weeks postpartum and the night feeds are crushing me. Pray for sleep, milk, and a quiet mind.",
      waters: 22,
      createdAt: now() - 1000 * 60 * 60 * 4,
      theme: "postpartum",
    },
    {
      id: "p4",
      userId: "u_anon_1",
      body: "I lost a pregnancy last month and have not told anyone outside my husband. Carry me, sisters.",
      waters: 38,
      createdAt: now() - 1000 * 60 * 60 * 8,
      anonymous: true,
      theme: "grief",
    },
    {
      id: "p5",
      userId: "u_grace",
      body: "My oldest is starting kindergarten and I'm grieving and rejoicing in the same breath. Wisdom for the season change.",
      waters: 11,
      createdAt: now() - 1000 * 60 * 60 * 14,
      theme: "motherhood",
    },
    {
      id: "p6",
      userId: "u_abigail",
      body: "My quiet time has been dry for months. Pray for hunger for the Word again, sisters.",
      waters: 17,
      createdAt: now() - 1000 * 60 * 60 * 22,
      theme: "faith",
    },
  ],
  prayerLifts: [
    // Hannah has prayed for Bethany and the anonymous grief sister already.
    { id: "pl1", prayerId: "p3", userId: "u_hannah",  liftedAt: now() - 1000 * 60 * 60 * 3 },
    { id: "pl2", prayerId: "p4", userId: "u_hannah",  liftedAt: now() - 1000 * 60 * 60 * 7 },
    { id: "pl3", prayerId: "p1", userId: "u_bethany", liftedAt: now() - 1000 * 60 * 25 },
    { id: "pl4", prayerId: "p4", userId: "u_bethany", liftedAt: now() - 1000 * 60 * 60 * 6 },
    { id: "pl5", prayerId: "p4", userId: "u_grace",   liftedAt: now() - 1000 * 60 * 60 * 5 },
  ],
  prayerReplies: [
    {
      id: "pr1",
      prayerId: "p4",
      userId: "u_bethany",
      body: "He sees every tear, sister. You are not alone in this hidden grief.",
      reference: "Psalm 56:8",
      createdAt: now() - 1000 * 60 * 60 * 5,
    },
    {
      id: "pr2",
      prayerId: "p3",
      userId: "u_hannah",
      body: "Walked through this last winter. He multiplies what we offer in weakness.",
      reference: null,
      createdAt: now() - 1000 * 60 * 60 * 2,
    },
  ],
  pairs: (() => {
    const day = 1000 * 60 * 60 * 24;
    return [
      // Hannah's active pairing with Bethany, fresh 3 days into a 30-day season.
      // Auto-matched by season tag (both in postpartum), so the covenant is the
      // four shared rituals rather than a hand-picked list.
      {
        id: "pair_h_b",
        inviterId: "u_hannah",
        inviteeId: "u_bethany",
        status: "active" as const,
        seasonDays: 30 as const,
        covenant: [
          { kind: "scripture"   as const, text: "Share a verse for the week" },
          { kind: "nourishment" as const, text: "Cook one meal together each week" },
          { kind: "custom"      as const, text: "Cheer each other's garden goals" },
        ],
        startedAt: now() - day * 3,
        endsAt:   now() + day * 27,
        createdAt: now() - day * 4,
      },
      // Pending invite from Mara to Hannah, waiting on Hannah's response
      {
        id: "pair_m_h",
        inviterId: "u_mara",
        inviteeId: "u_hannah",
        status: "pending" as const,
        seasonDays: 60 as const,
        covenant: [
          { kind: "nourishment" as const, text: "Three honest meals a day" },
          { kind: "movement" as const,    text: "Walk together by phone twice a week" },
        ],
        startedAt: null,
        endsAt:   null,
        createdAt: now() - day * 1,
      },
    ];
  })(),
  pairPings: (() => {
    const hour = 1000 * 60 * 60;
    return [
      // Fresh pair, three days in: a small handful of opening whispers.
      { id: "pp1", pairId: "pair_h_b", fromUserId: "u_bethany", toUserId: "u_hannah",  kind: "praying"     as const, sentAt: now() - hour * 4 },
      { id: "pp2", pairId: "pair_h_b", fromUserId: "u_hannah",  toUserId: "u_bethany", kind: "won_morning" as const, sentAt: now() - hour * 28 },
      { id: "pp3", pairId: "pair_h_b", fromUserId: "u_bethany", toUserId: "u_hannah",  kind: "struggling"  as const, sentAt: now() - hour * 50 },
    ];
  })(),
  pairCheckIns: (() => {
    const day = 1000 * 60 * 60 * 24;
    const ymd = (offset: number) => {
      const d = new Date(now() - day * offset);
      return d.toISOString().slice(0, 10);
    };
    return [
      // Today, both still blank, the prompt is waiting for a quiet moment.
      { id: "ci_today", pairId: "pair_h_b", prompt: "How is your heart this morning, sister?", date: ymd(0), inviterAnswer: null, inviteeAnswer: null, createdAt: now() - 1000 * 60 * 60 * 4 },
      // Yesterday, both answered.
      { id: "ci_y1",    pairId: "pair_h_b", prompt: "Where did the Lord meet you yesterday?", date: ymd(1), inviterAnswer: "On the long walk after dinner. He gave me patience with the kids.", inviteeAnswer: "In the kitchen with the baby on my hip. Slow grace.", createdAt: now() - day * 1 },
      // Two days ago, only Bethany answered (her early days are tender).
      { id: "ci_y2",    pairId: "pair_h_b", prompt: "What is one rep of obedience for today?", date: ymd(2), inviterAnswer: null, inviteeAnswer: "Drinking the water before the coffee.", createdAt: now() - day * 2 },
    ];
  })(),
  mentees: [
    { id: "u_rebecca",  coachId: "u_kiley", name: "Rebecca Hayes",  initials: "RH", tier: "Flourishing", streak: 38, resting: false, restingDays: 0, status: "thriving",  lastCheckIn: "2h ago" },
    { id: "u_joanna",   coachId: "u_kiley", name: "Joanna Pierce",  initials: "JP", tier: "Rooted",       streak: 21, resting: false, restingDays: 0, status: "thriving",  lastCheckIn: "5h ago" },
    { id: "u_hannah",   coachId: "u_kiley", name: "Hannah Doyle",   initials: "HD", tier: "Rooted",       streak: 12, resting: false, restingDays: 0, status: "steady",    lastCheckIn: "Today" },
    { id: "u_mara",     coachId: "u_kiley", name: "Mara Whitfield", initials: "MW", tier: "Flourishing",  streak: 56, resting: false, restingDays: 0, status: "steady",    lastCheckIn: "Yesterday" },
    { id: "u_bethany",  coachId: "u_kiley", name: "Bethany Cole",   initials: "BC", tier: "Seedling",     streak: 4,  resting: false, restingDays: 0, status: "needs love", lastCheckIn: "3d ago" },
    { id: "u_ruth",     coachId: "u_kiley", name: "Ruth Adler",     initials: "RA", tier: "Rooted",       streak: 17, resting: false, restingDays: 0, status: "steady",    lastCheckIn: "1d ago" },
    { id: "u_eden",     coachId: "u_kiley", name: "Eden Marsh",     initials: "EM", tier: "Seedling",     streak: 0,  resting: true,  restingDays: 4, status: "needs love", lastCheckIn: "4d ago" },
  ],
  journalEntries: [],
  chatMessages: [
    { id: "km1", threadId: "kiley", fromCoach: true,  type: "voice", body: "Voice note · welcome", durationSec: 41, sentAt: now() - 1000 * 60 * 60 * 24 * 3 },
    { id: "km2", threadId: "kiley", fromCoach: true,  type: "text",  body: "Sister, I'm so glad you said yes to Flourishing. This thread is yours, anything you'd ask in a phone call, ask me here.", sentAt: now() - 1000 * 60 * 60 * 24 * 3 + 1000 * 60 },
    { id: "km3", threadId: "kiley", fromCoach: false, type: "text",  body: "Coach, I'm a little overwhelmed thinking about training through a busy season. Can we plan a softer week?", sentAt: now() - 1000 * 60 * 60 * 24 * 2 },
    { id: "km4", threadId: "kiley", fromCoach: true,  type: "verse", body: "Matthew 11:28\u201330", verseRef: "Matthew 11:28\u201330 \u00b7 'Come to me, all you who are weary and burdened, and I will give you rest.'", sentAt: now() - 1000 * 60 * 60 * 24 * 2 + 1000 * 60 * 5 },
    { id: "km5", threadId: "kiley", fromCoach: true,  type: "text",  body: "Yes, sending a 4-day rhythm tonight. Two strength, one walk, one yoga, three rest. Body is a garden, not a factory. \ud83c\udf3f", sentAt: now() - 1000 * 60 * 60 * 24 * 2 + 1000 * 60 * 8 },
    // Bethany thread (Hannah is mentoring her)
    { id: "bm1", threadId: "bethany", fromCoach: false, type: "text", body: "Hannah, the postpartum plate ideas you sent are saving dinner this week. Truly.", sentAt: now() - 1000 * 60 * 60 * 8 },
    { id: "bm2", threadId: "bethany", fromCoach: true,  type: "text", body: "Sister, that makes my whole heart. You are doing holy work in a tired body.", sentAt: now() - 1000 * 60 * 60 * 7 },
    { id: "bm3", threadId: "bethany", fromCoach: false, type: "text", body: "Could we walk Saturday morning? Baby naps in the carrier and I need a friend.", sentAt: now() - 1000 * 60 * 42 },
    // Rebecca thread
    { id: "rm1", threadId: "rebecca", fromCoach: false, type: "text", body: "Hit a PR on deadlifts today, kept Phil 4:13 on the mirror.", sentAt: now() - 1000 * 60 * 60 * 26 },
    { id: "rm2", threadId: "rebecca", fromCoach: true,  type: "text", body: "Yes, Rebecca. Strength of body and spirit, both gifts.", sentAt: now() - 1000 * 60 * 60 * 25 },
    { id: "rm3", threadId: "rebecca", fromCoach: false, type: "verse", body: "Isaiah 40:31", verseRef: "Isaiah 40:31 \u00b7 'They will run and not grow weary.'", sentAt: now() - 1000 * 60 * 18 },
    // Joanna thread
    { id: "jm1", threadId: "joanna", fromCoach: false, type: "text", body: "Perimenopause sleep is a riddle. Any rhythm tweaks you'd suggest?", sentAt: now() - 1000 * 60 * 60 * 50 },
    { id: "jm2", threadId: "joanna", fromCoach: true,  type: "text", body: "Earlier protein, magnesium glycinate at dusk, walk after dinner. Try 5 nights and tell me.", sentAt: now() - 1000 * 60 * 60 * 49 },
    { id: "jm3", threadId: "joanna", fromCoach: false, type: "text", body: "Three nights in, sleeping deeper already. Thank you, sister.", sentAt: now() - 1000 * 60 * 95 },
    // Sister Lydia (the on-brand assistant) opens with her standard greeting
    { id: "ly1", threadId: "lydia", fromCoach: true,  type: "text", body: LYDIA_OPENER, sentAt: now() - 1000 * 60 * 60 * 24 * 4 },
    { id: "ly2", threadId: "lydia", fromCoach: false, type: "text", body: "Lydia, my squat depth feels stuck. Cues?", sentAt: now() - 1000 * 60 * 60 * 24 * 3 },
    { id: "ly3", threadId: "lydia", fromCoach: true,  type: "text", body: "Strong squats start at the foot. Three points of contact: big toe, pinky toe, heel. Knees track over the middle three toes, ribs stacked over hips. Two to three sets of eight, leaving two reps in the tank.", sentAt: now() - 1000 * 60 * 60 * 24 * 3 + 1000 * 30 },
    // Coach Kiley queue, simulating a Sunday-night build-up
    { id: "qm1", threadId: "mara",   fromCoach: false, type: "text", body: "Coach, the new program lands tomorrow, can you sanity-check my Tuesday lift?", sentAt: now() - 1000 * 60 * 38 },
    { id: "qm2", threadId: "ruth",   fromCoach: false, type: "text", body: "Coach Kiley, my hip is grumbling on RDLs, what should I sub in this week?", sentAt: now() - 1000 * 60 * 22 },
  ],
  cycleLogs: [],
  seeds: [
    { id: "s1", inviterId: "u_hannah", name: "Rebecca H.", initials: "RH", joined: now() - 1000 * 60 * 60 * 24 * 21, tier: "Flourishing", status: "flourishing" },
    { id: "s2", inviterId: "u_hannah", name: "Joanna P.",  initials: "JP", joined: now() - 1000 * 60 * 60 * 24 * 60, tier: "Rooted",      status: "rooted" },
    { id: "s3", inviterId: "u_hannah", name: "Bethany C.", initials: "BC", joined: now() - 1000 * 60 * 60 * 24 * 5,  tier: "Seedling",    status: "tending" },
  ],
  liveClasses: [
    {
      id: "lc_now", coachId: "u_kiley",
      title: "Strength & Grace · Lower Body",
      category: "strength",
      description: "45-minute heavy-day lower split with hip-stable variations for cycle-aware training.",
      scheduledFor: now() - 1000 * 60 * 8, // started 8 min ago
      durationMin: 45,
      status: "live",
      thumbnail: "🏋️‍♀️",
      attendees: 142,
      watching: 87,
      verseRef: "Phil 4:13",
      tierMin: "Seedling",
    },
    {
      id: "lc_tonight", coachId: "u_kiley",
      title: "Sunset Stretch & Surrender",
      category: "yoga",
      description: "30 minutes of gentle mobility to close your day. Bring a journal.",
      scheduledFor: now() + 1000 * 60 * 60 * 2.5, // in 2.5 hours
      durationMin: 30,
      status: "upcoming",
      thumbnail: "🌇",
      attendees: 64,
      watching: 0,
      verseRef: "Ps 23:1",
      tierMin: "Seedling",
    },
    {
      id: "lc_mon", coachId: "u_kiley",
      title: "Monday Prayer Walk",
      category: "prayer",
      description: "Audio-only guided prayer walk to start the week. Lace up and meet me here.",
      scheduledFor: now() + 1000 * 60 * 60 * 36, // ~Monday 6am
      durationMin: 25,
      status: "upcoming",
      thumbnail: "🌿",
      attendees: 38,
      watching: 0,
      tierMin: "Seedling",
    },
    {
      id: "lc_wed_circle", coachId: "u_kiley",
      title: "Sister Circle · Wednesday",
      category: "teaching",
      description: "Live teaching + Q&A on tending the temple. Flourishing tier perk.",
      scheduledFor: now() + 1000 * 60 * 60 * 96, // ~3.5 days out
      durationMin: 60,
      status: "upcoming",
      thumbnail: "📖",
      attendees: 12,
      watching: 0,
      tierMin: "Flourishing",
    },
    {
      id: "lc_replay_1", coachId: "u_kiley",
      title: "Postpartum Power · Core Restoration",
      category: "recovery",
      description: "Diastasis-aware core work for healing mamas. From Wednesday's live.",
      scheduledFor: now() - 1000 * 60 * 60 * 48,
      durationMin: 0,
      status: "replay",
      thumbnail: "🌷",
      attendees: 218,
      watching: 0,
      replayDurationMin: 38,
      tierMin: "Rooted",
    },
    {
      id: "lc_replay_2", coachId: "u_kiley",
      title: "Cardio for the Cycle-Aware",
      category: "cardio",
      description: "Heart-rate zones tuned to luteal phase. Light, joyful, sustainable.",
      scheduledFor: now() - 1000 * 60 * 60 * 96,
      durationMin: 0,
      status: "replay",
      thumbnail: "✨",
      attendees: 156,
      watching: 0,
      replayDurationMin: 28,
      tierMin: "Seedling",
    },
    {
      id: "lc_replay_3", coachId: "u_kiley",
      title: "Feast Without Fear · Holiday Tables",
      category: "teaching",
      description: "Teaching on enjoying family meals without dieting anxiety.",
      scheduledFor: now() - 1000 * 60 * 60 * 168,
      durationMin: 0,
      status: "replay",
      thumbnail: "🍽️",
      attendees: 412,
      watching: 0,
      replayDurationMin: 52,
      tierMin: "Seedling",
    },
  ],
  liveChat: [
    { id: "lcm1", classId: "lc_now", userId: "u_rebecca",  userName: "Rebecca H.",  initials: "RH", body: "This squat pulse is wrecking me 😭🙌", type: "chat",   sentAt: now() - 1000 * 60 * 6 },
    { id: "lcm2", classId: "lc_now", userId: "u_kiley",    userName: "Coach Kiley", initials: "K",  body: "You’ve got this, sister. Breathe through it.", type: "chat",   sentAt: now() - 1000 * 60 * 5 },
    { id: "lcm3", classId: "lc_now", userId: "u_joanna",   userName: "Joanna P.",  initials: "JP", body: "Praying for strength on this last set.",        type: "prayer", sentAt: now() - 1000 * 60 * 4 },
    { id: "lcm4", classId: "lc_now", userId: "u_mara",     userName: "Mara W.",    initials: "MW", body: "Amen 🙌",                                           type: "amen",   sentAt: now() - 1000 * 60 * 3 },
    { id: "lcm5", classId: "lc_now", userId: "u_ruth",     userName: "Ruth A.",    initials: "RA", body: "My quads are FIRE",                              type: "chat",   sentAt: now() - 1000 * 60 * 2 },
    { id: "lcm6", classId: "lc_now", userId: "u_kiley",    userName: "Coach Kiley", initials: "K",  body: "Phil 4:13 · we lift in His strength tonight.",  type: "chat",   sentAt: now() - 1000 * 60 * 1 },
  ],
  communityEvents: [
    // Farmers markets
    {
      id: "ce_fm1", source: "farmers-market",
      title: "Richland Park Farmers Market",
      venue: "Richland Park",
      city: "Nashville, TN", zipCode: "37205",
      distanceMi: 1.8,
      startsAt: tomorrowAt(9), endsAt: tomorrowAt(12),
      recurring: "Every Saturday · May–Oct",
      description: "30+ local growers. Sourdough, raw honey, pasture eggs, and seasonal produce.",
    },
    {
      id: "ce_fm2", source: "farmers-market",
      title: "East Nashville Farmers Market",
      venue: "Shelby Park",
      city: "Nashville, TN", zipCode: "37206",
      distanceMi: 4.6,
      startsAt: daysFromNow(3, 15), endsAt: daysFromNow(3, 18),
      recurring: "Every Wednesday · May–Oct",
      description: "Mid-week market with prepared foods and live folk music.",
    },
    // Fitness meetups
    {
      id: "ce_fit1", source: "fitness-meetup",
      title: "Sunrise Walking Sisters",
      venue: "Percy Warner Park",
      city: "Nashville, TN", zipCode: "37205",
      distanceMi: 2.3,
      startsAt: tomorrowAt(6, 30), endsAt: tomorrowAt(7, 30),
      recurring: "Every Saturday",
      description: "3-mile sunrise walk for women. All paces welcome. We pray and chat.",
    },
    {
      id: "ce_fit2", source: "fitness-meetup",
      title: "Park Hill Run Club",
      venue: "Centennial Park",
      city: "Nashville, TN", zipCode: "37203",
      distanceMi: 3.4,
      startsAt: daysFromNow(2, 6), endsAt: daysFromNow(2, 7),
      recurring: "Tuesdays + Thursdays",
      description: "5K easy pace. Strollers welcome. Free coffee at the band shell after.",
    },
    // Faith events
    {
      id: "ce_faith1", source: "faith-event",
      title: "Women in the Word · Tuesday Study",
      venue: "Christ Presbyterian Church",
      city: "Nashville, TN", zipCode: "37215",
      distanceMi: 0.7,
      startsAt: daysFromNow(2, 9, 30), endsAt: daysFromNow(2, 11, 30),
      recurring: "Tuesdays · 7-week study",
      description: "This semester: Hosea. Childcare provided. New sisters always welcome.",
    },
    {
      id: "ce_faith2", source: "faith-event",
      title: "Worship Night for Mothers",
      venue: "Cross Point Nashville",
      city: "Nashville, TN", zipCode: "37203",
      distanceMi: 5.1,
      startsAt: daysFromNow(5, 19), endsAt: daysFromNow(5, 21),
      description: "Acoustic worship and prayer for moms. Coffee bar open. No registration needed.",
    },
    // Sister meetups (in-app)
    {
      id: "ce_sm1", source: "sister-meetup",
      title: "Prayer + Walk · Saturday Morning",
      venue: "Radnor Lake Trailhead",
      city: "Nashville, TN", zipCode: "37220",
      distanceMi: 3.2,
      startsAt: tomorrowAt(7),
      hostName: "Hannah Doyle", hostInitials: "HD", hostId: "u_hannah",
      rsvpCount: 4,
      description: "Easy 2-mile loop with prayer prompts at each bench. Bringing thermoses of coffee.",
    },
    {
      id: "ce_sm2", source: "sister-meetup",
      title: "Sourdough + Scripture",
      venue: "Rebecca's kitchen",
      city: "Nashville, TN", zipCode: "37204",
      distanceMi: 2.9,
      startsAt: daysFromNow(4, 10),
      hostName: "Rebecca Hayes", hostInitials: "RH", hostId: "u_rebecca",
      rsvpCount: 3,
      description: "Fold-and-fellowship. We bake while we work through Proverbs 31. Come hungry.",
    },
  ],
  feedPosts: [
    {
      id: "fp1", userId: "u_rebecca", userName: "Rebecca H.", initials: "RH", tier: "Flourishing",
      kind: "testimony",
      body: "Forty days of showing up. I cried lifting the bar today, but I lifted it. Thank you sisters for praying me through last week.",
      amens: 38, waters: 14, comments: 6, isAmened: false, isWatered: false,
      createdAt: now() - 1000 * 60 * 25,
    },
    {
      id: "fp2", userId: "u_joanna", userName: "Joanna P.", initials: "JP", tier: "Rooted",
      kind: "prayer",
      body: "Asking for prayer over a hard parenting season. Some days I forget I'm allowed to rest. Praying I find the still place this week.",
      amens: 22, waters: 31, comments: 9, isAmened: false, isWatered: false,
      createdAt: now() - 1000 * 60 * 90,
    },
    {
      id: "fp3", userId: "u_kiley", userName: "Coach Kiley", initials: "K", tier: "Flourishing",
      kind: "verse",
      body: "She is clothed with strength and dignity, and she laughs without fear of the future.",
      verseRef: "Proverbs 31:25",
      amens: 102, waters: 0, comments: 14, isAmened: true, isWatered: false,
      createdAt: now() - 1000 * 60 * 60 * 3,
    },
    {
      id: "fp4", userId: "u_mara", userName: "Mara W.", initials: "MW", tier: "Flourishing",
      kind: "win",
      body: "Cooked the family meal plan three nights in a row. Husband asked what got into me. I told him it was Jesus and a slow cooker.",
      amens: 47, waters: 5, comments: 11, isAmened: false, isWatered: false,
      createdAt: now() - 1000 * 60 * 60 * 5,
    },
    {
      id: "fp5", userId: "u_ruth", userName: "Ruth A.", initials: "RA", tier: "Rooted",
      kind: "prayer",
      body: "Walking into a tough doctor visit Friday. Praying for a steady heart and a clear word.",
      amens: 18, waters: 27, comments: 4, isAmened: false, isWatered: true,
      createdAt: now() - 1000 * 60 * 60 * 8,
    },
    {
      id: "fp6", userId: "u_bethany", userName: "Bethany C.", initials: "BC", tier: "Seedling",
      kind: "testimony",
      body: "First week of Seedling. I cooked one real meal instead of grazing on chips. It feels small but it isn't. Thank you for the welcome.",
      amens: 56, waters: 8, comments: 12, isAmened: false, isWatered: false,
      createdAt: now() - 1000 * 60 * 60 * 14,
    },
  ],
  classReminders: [],
  feedComments: [
    { id: "fc1", postId: "fp1", userId: "u_kiley",  userName: "Coach Kiley", initials: "K",  body: "Sister, this is a banner. Forty days of yes is forty victories.", createdAt: now() - 1000 * 60 * 22 },
    { id: "fc2", postId: "fp1", userId: "u_mara",   userName: "Mara W.",     initials: "MW", body: "You moved me to tears. Lifting with you next time.",                createdAt: now() - 1000 * 60 * 18 },
    { id: "fc3", postId: "fp2", userId: "u_hannah", userName: "Hannah D.",   initials: "HD", body: "Praying right now, sister. Rest is faithful too.",                  createdAt: now() - 1000 * 60 * 70 },
  ],
  liveAttendance: [],
  eventRsvps: [
    { id: "er1", eventId: "ce_sm1", userId: "u_rebecca", createdAt: now() - 1000 * 60 * 60 * 18 },
    { id: "er2", eventId: "ce_sm1", userId: "u_joanna",  createdAt: now() - 1000 * 60 * 60 * 12 },
  ],
  macroProfiles: [
    {
      userId: "u_hannah",
      seasonKind: "none",
      ageYears: 32,
      heightIn: 66,
      weightLb: 145,
      activity: "moderate",
      direction: "maintain",
      pace: "gentle",
      updatedAt: now() - 1000 * 60 * 60 * 24 * 6,
    },
    {
      userId: "u_joanna",
      seasonKind: "pregnant",
      ageYears: 30,
      heightIn: 65,
      weightLb: 152,
      activity: "light",
      direction: "maintain",
      pace: "gentle",
      updatedAt: now() - 1000 * 60 * 60 * 24 * 3,
    },
  ],
  macroPlans: [
    {
      userId: "u_hannah",
      mode: "auto",
      kcal: 2150,
      proteinG: 145,
      carbsG: 235,
      fatG: 72,
      ratios: { p: 27, c: 44, f: 29 },
      source: "self",
      locked: false,
      coachNote: null,
      updatedAt: now() - 1000 * 60 * 60 * 24 * 6,
    },
    {
      // Joanna T2: base ~1980 + 340 T2 bump = 2320, protein anchored to
      // 1.7 g/kg (~117g) but lifted to GGS T2 minimum.
      userId: "u_joanna",
      mode: "auto",
      kcal: 2320,
      proteinG: 130,
      carbsG: 290,
      fatG: 80,
      ratios: { p: 22, c: 50, f: 28 },
      source: "self",
      locked: false,
      coachNote: null,
      updatedAt: now() - 1000 * 60 * 60 * 24 * 3,
    },
    /* Coach-set plans for the rest of Kiley's mentees so the compliance
     * dashboard has real data to aggregate. */
    { userId: "u_rebecca", mode: "auto" as const, kcal: 2050, proteinG: 140, carbsG: 215, fatG: 70, ratios: { p: 27, c: 42, f: 31 }, source: "coach" as const, locked: true,  coachNote: "You are crushing this season, Reb. Hold steady.", updatedAt: now() - 1000 * 60 * 60 * 24 * 4 },
    { userId: "u_mara",    mode: "auto" as const, kcal: 2200, proteinG: 150, carbsG: 235, fatG: 75, ratios: { p: 27, c: 43, f: 30 }, source: "coach" as const, locked: true,  coachNote: "Marathon prep, sis. Keep that protein anchored.", updatedAt: now() - 1000 * 60 * 60 * 24 * 5 },
    { userId: "u_bethany", mode: "auto" as const, kcal: 2480, proteinG: 145, carbsG: 290, fatG: 85, ratios: { p: 23, c: 47, f: 30 }, source: "coach" as const, locked: true,  coachNote: "Three weeks postpartum, eat the snack. I love you.", updatedAt: now() - 1000 * 60 * 60 * 24 * 7 },
    { userId: "u_ruth",    mode: "auto" as const, kcal: 1950, proteinG: 130, carbsG: 200, fatG: 68, ratios: { p: 27, c: 41, f: 32 }, source: "self" as const,  locked: false, coachNote: null, updatedAt: now() - 1000 * 60 * 60 * 24 * 9 },
    { userId: "u_eden",    mode: "auto" as const, kcal: 1900, proteinG: 120, carbsG: 200, fatG: 65, ratios: { p: 25, c: 42, f: 33 }, source: "coach" as const, locked: true,  coachNote: "One log a day, Eden. That is enough this week.", updatedAt: now() - 1000 * 60 * 60 * 24 * 12 },
  ],
  seasonStates: [
    {
      userId: "u_hannah",
      kind: "none",
      trimester: null,
      weeksPostpartum: null,
      feeding: "none",
      clearedForMovement: true,
      milestoneDate: null,
      updatedAt: now() - 1000 * 60 * 60 * 24 * 30,
    },
    {
      // Joanna: pregnant, T2, week 18, due late October 2026.
      userId: "u_joanna",
      kind: "pregnant",
      trimester: 2,
      weeksPostpartum: null,
      feeding: "none",
      clearedForMovement: true,
      milestoneDate: "2026-10-22",
      updatedAt: now() - 1000 * 60 * 60 * 24 * 14,
    },
  ],
  mealLogs: (() => {
    const day = 1000 * 60 * 60 * 24;
    const dStr = (offset: number) => new Date(now() - day * offset).toISOString().slice(0, 10);
    const id = (n: string) => `ml_${n}`;
    return [
      // Today (so far): breakfast + lunch logged, dinner and snack still ahead
      { id: id("t_b"), userId: "u_hannah", date: dStr(0), block: "breakfast" as const, kcal: 420, proteinG: 32, carbsG: 38, fatG: 16, note: "Eggs, sourdough, and the morning quiet.", loggedAt: now() - 1000 * 60 * 60 * 4 },
      { id: id("t_l"), userId: "u_hannah", date: dStr(0), block: "lunch"     as const, kcal: 540, proteinG: 38, carbsG: 52, fatG: 18, note: "Chicken salad with the kids.", loggedAt: now() - 1000 * 60 * 60 * 1 },
      // Yesterday: full day, slightly under target
      { id: id("y_b"), userId: "u_hannah", date: dStr(1), block: "breakfast" as const, kcal: 380, proteinG: 28, carbsG: 36, fatG: 14, note: null, loggedAt: now() - day * 1 - 1000 * 60 * 60 * 14 },
      { id: id("y_l"), userId: "u_hannah", date: dStr(1), block: "lunch"     as const, kcal: 510, proteinG: 36, carbsG: 50, fatG: 16, note: null, loggedAt: now() - day * 1 - 1000 * 60 * 60 * 8 },
      { id: id("y_d"), userId: "u_hannah", date: dStr(1), block: "dinner"    as const, kcal: 720, proteinG: 48, carbsG: 78, fatG: 24, note: "Family taco night.", loggedAt: now() - day * 1 - 1000 * 60 * 60 * 2 },
      { id: id("y_s"), userId: "u_hannah", date: dStr(1), block: "snack"     as const, kcal: 240, proteinG: 14, carbsG: 24, fatG: 10, note: null, loggedAt: now() - day * 1 - 1000 * 60 * 60 * 4 },
      // Two days ago: hit close to target
      { id: id("d2_b"), userId: "u_hannah", date: dStr(2), block: "breakfast" as const, kcal: 410, proteinG: 30, carbsG: 38, fatG: 15, note: null, loggedAt: now() - day * 2 - 1000 * 60 * 60 * 14 },
      { id: id("d2_l"), userId: "u_hannah", date: dStr(2), block: "lunch"     as const, kcal: 560, proteinG: 40, carbsG: 54, fatG: 18, note: null, loggedAt: now() - day * 2 - 1000 * 60 * 60 * 8 },
      { id: id("d2_d"), userId: "u_hannah", date: dStr(2), block: "dinner"    as const, kcal: 750, proteinG: 50, carbsG: 80, fatG: 26, note: null, loggedAt: now() - day * 2 - 1000 * 60 * 60 * 2 },
      { id: id("d2_s"), userId: "u_hannah", date: dStr(2), block: "snack"     as const, kcal: 320, proteinG: 18, carbsG: 32, fatG: 12, note: null, loggedAt: now() - day * 2 - 1000 * 60 * 60 * 5 },
      // Three days ago: a fuller day
      { id: id("d3_b"), userId: "u_hannah", date: dStr(3), block: "breakfast" as const, kcal: 460, proteinG: 34, carbsG: 42, fatG: 18, note: null, loggedAt: now() - day * 3 - 1000 * 60 * 60 * 14 },
      { id: id("d3_l"), userId: "u_hannah", date: dStr(3), block: "lunch"     as const, kcal: 580, proteinG: 38, carbsG: 56, fatG: 20, note: null, loggedAt: now() - day * 3 - 1000 * 60 * 60 * 8 },
      { id: id("d3_d"), userId: "u_hannah", date: dStr(3), block: "dinner"    as const, kcal: 780, proteinG: 52, carbsG: 82, fatG: 28, note: null, loggedAt: now() - day * 3 - 1000 * 60 * 60 * 2 },

      /* === Other Kiley mentees, week of logs to fuel the compliance dashboard ===
       * Each block is sized roughly proportional to the sister's macro plan,
       * shaped to reflect her status: thriving sisters log more days near
       * target, steady sisters mostly hit it, "needs love" sisters miss days. */

      // Rebecca (Flourishing, thriving): full week, mostly on target
      ...[0,1,2,3,4,5,6].flatMap((d) => [
        { id: id(`reb_b_${d}`), userId: "u_rebecca", date: dStr(d), block: "breakfast" as const, kcal: 480 - d*10, proteinG: 36, carbsG: 48, fatG: 18, note: d===0 ? "Coffee, oats, and Psalm 23." : null, loggedAt: now() - day * d - 1000 * 60 * 60 * 14 },
        { id: id(`reb_l_${d}`), userId: "u_rebecca", date: dStr(d), block: "lunch"     as const, kcal: 560 + (d%2)*20, proteinG: 42, carbsG: 56, fatG: 20, note: null, loggedAt: now() - day * d - 1000 * 60 * 60 * 8 },
        { id: id(`reb_d_${d}`), userId: "u_rebecca", date: dStr(d), block: "dinner"    as const, kcal: 720 - (d%3)*30, proteinG: 50, carbsG: 70, fatG: 24, note: null, loggedAt: now() - day * d - 1000 * 60 * 60 * 2 },
        { id: id(`reb_s_${d}`), userId: "u_rebecca", date: dStr(d), block: "snack"     as const, kcal: 280, proteinG: 14, carbsG: 30, fatG: 10, note: null, loggedAt: now() - day * d - 1000 * 60 * 60 * 5 },
      ]),

      // Mara (Flourishing, steady, marathon prep): full week, kcal slightly under target most days
      ...[0,1,2,3,4,5,6].flatMap((d) => [
        { id: id(`mar_b_${d}`), userId: "u_mara", date: dStr(d), block: "breakfast" as const, kcal: 420, proteinG: 30, carbsG: 50, fatG: 14, note: null, loggedAt: now() - day * d - 1000 * 60 * 60 * 14 },
        { id: id(`mar_l_${d}`), userId: "u_mara", date: dStr(d), block: "lunch"     as const, kcal: 580, proteinG: 40, carbsG: 60, fatG: 22, note: null, loggedAt: now() - day * d - 1000 * 60 * 60 * 8 },
        { id: id(`mar_d_${d}`), userId: "u_mara", date: dStr(d), block: "dinner"    as const, kcal: 720, proteinG: 48, carbsG: 75, fatG: 26, note: null, loggedAt: now() - day * d - 1000 * 60 * 60 * 2 },
        ...(d % 3 !== 0 ? [{ id: id(`mar_s_${d}`), userId: "u_mara", date: dStr(d), block: "snack" as const, kcal: 250, proteinG: 16, carbsG: 26, fatG: 8, note: null, loggedAt: now() - day * d - 1000 * 60 * 60 * 5 }] : []),
      ]),

      // Bethany (Seedling, postpartum, needs love): only logs days 0, 2, 5
      { id: id("beth_b_0"), userId: "u_bethany", date: dStr(0), block: "breakfast" as const, kcal: 380, proteinG: 22, carbsG: 50, fatG: 12, note: "One-handed oatmeal at 5am.", loggedAt: now() - 1000 * 60 * 60 * 11 },
      { id: id("beth_b_2"), userId: "u_bethany", date: dStr(2), block: "breakfast" as const, kcal: 360, proteinG: 20, carbsG: 48, fatG: 12, note: null, loggedAt: now() - day * 2 - 1000 * 60 * 60 * 12 },
      { id: id("beth_l_2"), userId: "u_bethany", date: dStr(2), block: "lunch"     as const, kcal: 540, proteinG: 30, carbsG: 60, fatG: 18, note: null, loggedAt: now() - day * 2 - 1000 * 60 * 60 * 6 },
      { id: id("beth_b_5"), userId: "u_bethany", date: dStr(5), block: "breakfast" as const, kcal: 410, proteinG: 26, carbsG: 52, fatG: 14, note: null, loggedAt: now() - day * 5 - 1000 * 60 * 60 * 13 },

      // Ruth (Rooted, steady): logs 5 of 7 days, average adherence
      ...[0,1,2,4,5].flatMap((d) => [
        { id: id(`ruth_b_${d}`), userId: "u_ruth", date: dStr(d), block: "breakfast" as const, kcal: 380, proteinG: 28, carbsG: 40, fatG: 14, note: null, loggedAt: now() - day * d - 1000 * 60 * 60 * 14 },
        { id: id(`ruth_l_${d}`), userId: "u_ruth", date: dStr(d), block: "lunch"     as const, kcal: 520, proteinG: 36, carbsG: 50, fatG: 18, note: null, loggedAt: now() - day * d - 1000 * 60 * 60 * 8 },
        { id: id(`ruth_d_${d}`), userId: "u_ruth", date: dStr(d), block: "dinner"    as const, kcal: 660, proteinG: 44, carbsG: 70, fatG: 22, note: null, loggedAt: now() - day * d - 1000 * 60 * 60 * 2 },
      ]),

      // Eden (Seedling, needs love, resting 4 days): only an old log 4 days back
      { id: id("eden_b_4"), userId: "u_eden", date: dStr(4), block: "breakfast" as const, kcal: 340, proteinG: 18, carbsG: 44, fatG: 12, note: null, loggedAt: now() - day * 4 - 1000 * 60 * 60 * 14 },
    ];
  })(),
  recipes: (() => {
    const day = 1000 * 60 * 60 * 24;
    const ago = (d: number) => now() - day * d;
    /** Helper so seeded recipes derive their tier from lovedCount the same
     *  way live recipes will. Keeps the chip honest. */
    const tierFor = (n: number): RecipeAwardTier => {
      if (n >= 500) return "garden-favorite";
      if (n >= 100) return "flourishing";
      if (n >= 25)  return "rooted";
      if (n >= 5)   return "sprouted";
      return "none";
    };
    type Seed = Omit<Recipe, "awardTier">;
    const seeds: Seed[] = [
      // Garden Favorite (500+) by Kiley. This is Today's Harvest hero.
      {
        id: "r_sheetpan_salmon",
        authorId: "u_kiley",
        title: "Sheet-pan salmon and roots",
        description: "Wild salmon, sweet potato, and brassicas roasted on one tray. Twenty minutes, big protein, baby-friendly portions.",
        heroGradient:
          "radial-gradient(120% 80% at 50% 30%, #d18a5f 0%, #C0784A 55%, #5a3a26 100%)",
        heroEmojis: ["\ud83d\udc1f", "\ud83e\udd66", "\ud83c\udf60", "\ud83c\udf4b"],
        heroImageUrl: "recipes/r_sheetpan_salmon.webp",
        servings: 4,
        durationMin: 25,
        kcal: 520, proteinG: 38, carbsG: 32, fatG: 24, fiberG: 7,
        ingredients: [
          { qty: 1.25, unit: "lb", item: "wild salmon fillet" },
          { qty: 2,    unit: "medium", item: "sweet potatoes, cubed" },
          { qty: 1,    unit: "head", item: "broccoli, in florets" },
          { qty: 3,    unit: "tbsp", item: "olive oil" },
          { qty: 1,    unit: "tsp", item: "flaky sea salt" },
          { qty: 1,    unit: "", item: "lemon, halved" },
        ],
        steps: [
          { title: "Heat the oven", body: "425\u00b0F. Line a sheet pan with parchment." },
          { title: "Roast roots", body: "Toss sweet potatoes with olive oil and salt. Roast 12 minutes alone." },
          { title: "Add the rest", body: "Add broccoli and salmon, drizzle oil, salt, lemon. Roast 10 to 12 minutes." },
          { title: "Serve", body: "Squeeze the second lemon half over everything. Plate over rice or greens." },
        ],
        seasonTags: ["family", "postpartum", "nursing"],
        safetyFlags: [],
        scripturePairing: "He provides food for those who fear him. \u00b7 Psalm 111:5",
        source: "kiley",
        sourceUrl: null,
        lovedCount: 612,
        public: true,
        pendingReview: false,
        createdAt: ago(120),
      },
      // Garden Favorite (500+) by Kiley
      {
        id: "r_sweet_potato_kale",
        authorId: "u_kiley",
        title: "Sweet potato and kale vitality bowl",
        description: "Roasted roots, massaged kale, tahini dressing. The prep-ahead lunch that will not betray you at 2pm.",
        heroGradient:
          "radial-gradient(120% 80% at 50% 30%, #6b8e6f 0%, #2C6248 60%, #163828 100%)",
        heroEmojis: ["\ud83c\udf60", "\ud83e\udd6c", "\ud83c\udf45", "\ud83c\udf3f"],
        heroImageUrl: "recipes/r_sweet_potato_kale.webp",
        servings: 4,
        durationMin: 30,
        kcal: 460, proteinG: 18, carbsG: 56, fatG: 18, fiberG: 12,
        ingredients: [
          { qty: 2,    unit: "", item: "large sweet potatoes, cubed" },
          { qty: 1,    unit: "large bunch", item: "curly kale, stemmed" },
          { qty: 1,    unit: "can", item: "organic chickpeas, drained" },
          { qty: 0.25, unit: "cup", item: "creamy tahini" },
          { qty: 0.5,  unit: "cup", item: "pomegranate arils" },
          { qty: 1,    unit: "", item: "lemon, juiced" },
        ],
        steps: [
          { title: "Roast the foundation", body: "400\u00b0F. Toss sweet potato and chickpeas with olive oil, salt, cumin. Roast 25 minutes." },
          { title: "Massage the greens", body: "Drizzle kale with oil and salt. Hand-massage 2 to 3 minutes until tender and dark." },
          { title: "Whisk the dressing", body: "Tahini, lemon, garlic, warm water. Shake in a jar until creamy." },
          { title: "Compose with grace", body: "Layer kale, roasted veg, dressing, pomegranate. Cover and rest." },
        ],
        seasonTags: ["family", "pregnant"],
        safetyFlags: [],
        scripturePairing: "I am the vine, you are the branches. \u00b7 John 15:5",
        source: "kiley",
        sourceUrl: null,
        lovedCount: 524,
        public: true,
        pendingReview: false,
        createdAt: ago(95),
      },
      // Flourishing (100+) by Mara
      {
        id: "r_chicken_lentil_soup",
        authorId: "u_mara",
        title: "Chicken and lentil nursing soup",
        description: "Brothy, iron-rich, freezer-friendly. Mara's batch goes on the stove the night the baby comes home.",
        heroGradient:
          "radial-gradient(120% 80% at 50% 30%, #d4a058 0%, #A07820 55%, #4a3810 100%)",
        heroEmojis: ["\ud83c\udf72", "\ud83d\udc14", "\ud83e\uded8", "\ud83c\udf3f"],
        heroImageUrl: "recipes/r_chicken_lentil_soup.webp",
        servings: 6,
        durationMin: 45,
        kcal: 420, proteinG: 34, carbsG: 38, fatG: 14, fiberG: 9,
        ingredients: [
          { qty: 1.5,  unit: "lb", item: "bone-in chicken thighs" },
          { qty: 1,    unit: "cup", item: "french green lentils" },
          { qty: 8,    unit: "cups", item: "homemade broth" },
          { qty: 2,    unit: "", item: "carrots, diced" },
          { qty: 3,    unit: "cloves", item: "garlic, minced" },
          { qty: 1,    unit: "bunch", item: "lacinato kale, ribbons" },
        ],
        steps: [
          { title: "Sear the chicken", body: "Brown thighs in olive oil. Set aside." },
          { title: "Sweat the aromatics", body: "Carrots and garlic until soft. Add lentils, broth, chicken." },
          { title: "Simmer", body: "30 minutes covered, low heat. Pull chicken, shred, return." },
          { title: "Finish", body: "Stir in kale ribbons. Salt to taste. Lemon at the table." },
        ],
        seasonTags: ["postpartum", "nursing", "family"],
        safetyFlags: [],
        scripturePairing: "She opens her hand to the poor. \u00b7 Proverbs 31:20",
        source: "sister",
        sourceUrl: null,
        lovedCount: 187,
        public: true,
        pendingReview: false,
        createdAt: ago(60),
      },
      // Flourishing (100+) by Rebecca
      {
        id: "r_protein_overnight_oats",
        authorId: "u_rebecca",
        title: "Cottage cheese protein oats",
        description: "Forty grams of protein in a mason jar. Stir at night, eat at 6am while nursing or pumping.",
        heroGradient:
          "radial-gradient(120% 80% at 50% 30%, #d8c89a 0%, #a89668 55%, #4a3f1f 100%)",
        heroEmojis: ["\ud83e\udd63", "\ud83e\uded0", "\ud83c\udf6f", "\ud83e\udd64"],
        heroImageUrl: "recipes/r_protein_overnight_oats.webp",
        servings: 1,
        durationMin: 5,
        kcal: 480, proteinG: 42, carbsG: 48, fatG: 12, fiberG: 8,
        ingredients: [
          { qty: 0.5,  unit: "cup", item: "rolled oats" },
          { qty: 0.75, unit: "cup", item: "cottage cheese" },
          { qty: 1,    unit: "scoop", item: "vanilla whey" },
          { qty: 0.5,  unit: "cup", item: "frozen berries" },
          { qty: 1,    unit: "tbsp", item: "honey" },
        ],
        steps: [
          { title: "Layer the jar", body: "Oats, whey, cottage cheese, milk to cover. Stir." },
          { title: "Top and rest", body: "Berries and honey on top. Lid on. Fridge overnight." },
          { title: "Morning grace", body: "Stir once, eat cold. The protein is already done." },
        ],
        seasonTags: ["quick", "nursing", "postpartum"],
        safetyFlags: [],
        scripturePairing: null,
        source: "sister",
        sourceUrl: null,
        lovedCount: 134,
        public: true,
        pendingReview: false,
        createdAt: ago(45),
      },
      // Rooted (25+) by Hannah
      {
        id: "r_taco_skillet",
        authorId: "u_hannah",
        title: "Family taco skillet",
        description: "One pan, picky-kid approved. Hannah swaps the rice for cauliflower when she wants leaner macros.",
        heroGradient:
          "radial-gradient(120% 80% at 50% 30%, #d97757 0%, #a8451f 55%, #4a1f0e 100%)",
        heroEmojis: ["\ud83c\udf2e", "\ud83e\udd51", "\ud83c\udf45", "\ud83e\uddc0"],
        heroImageUrl: "recipes/r_taco_skillet.webp",
        servings: 5,
        durationMin: 25,
        kcal: 540, proteinG: 36, carbsG: 42, fatG: 22, fiberG: 8,
        ingredients: [
          { qty: 1.25, unit: "lb", item: "grass-fed ground beef" },
          { qty: 1,    unit: "", item: "yellow onion, diced" },
          { qty: 1,    unit: "can", item: "black beans, drained" },
          { qty: 1,    unit: "cup", item: "jasmine rice" },
          { qty: 1,    unit: "jar", item: "salsa" },
          { qty: 0.5,  unit: "cup", item: "sharp cheddar, grated" },
        ],
        steps: [
          { title: "Brown the beef", body: "Beef plus onion until cooked through." },
          { title: "Build the skillet", body: "Add beans, rice, salsa, water. Simmer covered 18 minutes." },
          { title: "Top and serve", body: "Cheese on top, lid back on, 2 minutes to melt. Avocado at table." },
        ],
        seasonTags: ["family", "quick"],
        safetyFlags: [],
        scripturePairing: null,
        source: "sister",
        sourceUrl: "https://example.com/family-taco-skillet",
        lovedCount: 38,
        public: true,
        pendingReview: false,
        createdAt: ago(30),
      },
      // Rooted (25+) by Joanna (pregnant T2)
      {
        id: "r_iron_lentil_salad",
        authorId: "u_joanna",
        title: "Iron-up lentil and beet salad",
        description: "Joanna's second-trimester staple. Lentils plus beets plus orange to help iron actually absorb.",
        heroGradient:
          "radial-gradient(120% 80% at 50% 30%, #b85a78 0%, #7a2a4a 55%, #3a0f1f 100%)",
        heroEmojis: ["\ud83e\udd57", "\ud83c\udf4a", "\ud83e\udd6c", "\ud83e\uded8"],
        heroImageUrl: "recipes/r_iron_lentil_salad.webp",
        servings: 4,
        durationMin: 20,
        kcal: 380, proteinG: 18, carbsG: 52, fatG: 12, fiberG: 14,
        ingredients: [
          { qty: 1,    unit: "cup", item: "french lentils, cooked" },
          { qty: 3,    unit: "", item: "roasted beets, sliced" },
          { qty: 2,    unit: "", item: "oranges, supremed" },
          { qty: 4,    unit: "cups", item: "baby spinach" },
          { qty: 0.25, unit: "cup", item: "feta, crumbled" },
          { qty: 2,    unit: "tbsp", item: "olive oil and red wine vinegar" },
        ],
        steps: [
          { title: "Build the base", body: "Spinach in a wide bowl, lentils on top." },
          { title: "Add color", body: "Beets, oranges, feta. Drizzle oil and vinegar." },
          { title: "Toss gently", body: "Salt and pepper to taste. Eat soon while greens are bright." },
        ],
        seasonTags: ["pregnant", "quick"],
        safetyFlags: [],
        scripturePairing: "Your body is a temple of the Holy Spirit. \u00b7 1 Corinthians 6:19",
        source: "sister",
        sourceUrl: null,
        lovedCount: 31,
        public: true,
        pendingReview: false,
        createdAt: ago(20),
      },
      // Rooted (25+) by Ruth
      {
        id: "r_yogurt_breakfast_bowl",
        authorId: "u_ruth",
        title: "Honey yogurt bowl with seeds",
        description: "Greek yogurt, hemp, chia, raw honey, in-season fruit. Five minutes. Ruth's quiet-time breakfast.",
        heroGradient:
          "radial-gradient(120% 80% at 50% 30%, #f0c890 0%, #c89858 55%, #5a3f1a 100%)",
        heroEmojis: ["\ud83e\udd63", "\ud83c\udf6f", "\ud83c\udf53", "\ud83c\udf3e"],
        heroImageUrl: "recipes/r_yogurt_breakfast_bowl.webp",
        servings: 1,
        durationMin: 5,
        kcal: 360, proteinG: 24, carbsG: 38, fatG: 12, fiberG: 7,
        ingredients: [
          { qty: 1,    unit: "cup", item: "plain Greek yogurt" },
          { qty: 1,    unit: "tbsp", item: "hemp hearts" },
          { qty: 1,    unit: "tsp", item: "chia seeds" },
          { qty: 1,    unit: "tbsp", item: "raw honey" },
          { qty: 0.5,  unit: "cup", item: "berries" },
        ],
        steps: [
          { title: "Spoon and top", body: "Yogurt in a bowl, seeds and honey, fruit on top." },
          { title: "Pause", body: "Eat slowly. The morning quiet is the seasoning." },
        ],
        seasonTags: ["quick", "family"],
        safetyFlags: [],
        scripturePairing: null,
        source: "sister",
        sourceUrl: null,
        lovedCount: 27,
        public: true,
        pendingReview: false,
        createdAt: ago(15),
      },
      // Sprouted (5+) by Bethany
      {
        id: "r_postpartum_steel_oats",
        authorId: "u_bethany",
        title: "Healing steel-cut oats with ghee",
        description: "Slow-cook overnight. Bethany's postpartum doula handed her this recipe in week one.",
        heroGradient:
          "radial-gradient(120% 80% at 50% 30%, #c8a878 0%, #8a6840 55%, #3f2a1a 100%)",
        heroEmojis: ["\ud83e\uddc8", "\ud83e\udd64", "\ud83c\udf6f", "\ud83c\udf30"],
        heroImageUrl: "recipes/r_postpartum_steel_oats.webp",
        servings: 4,
        durationMin: 360,
        kcal: 320, proteinG: 10, carbsG: 48, fatG: 11, fiberG: 6,
        ingredients: [
          { qty: 1,    unit: "cup", item: "steel-cut oats" },
          { qty: 4,    unit: "cups", item: "water and milk mix" },
          { qty: 2,    unit: "tbsp", item: "grass-fed ghee" },
          { qty: 1,    unit: "tsp", item: "cinnamon" },
          { qty: 0.25, unit: "cup", item: "toasted walnuts" },
          { qty: 2,    unit: "tbsp", item: "raw honey" },
        ],
        steps: [
          { title: "Crock the oats", body: "All but walnuts and honey in a slow cooker on low, 6 hours." },
          { title: "Top in the morning", body: "Walnuts and honey to taste." },
        ],
        seasonTags: ["postpartum", "nursing"],
        safetyFlags: [],
        scripturePairing: "He restores my soul. \u00b7 Psalm 23:3",
        source: "sister",
        sourceUrl: null,
        lovedCount: 11,
        public: true,
        pendingReview: false,
        createdAt: ago(8),
      },
      // Sprouted (5+) by Eden, imported
      {
        id: "r_quick_chicken_wraps",
        authorId: "u_eden",
        title: "Five-minute chicken hummus wraps",
        description: "Eden imported this from a recipe site, then made it twice in one week. Lunchbox approved.",
        heroGradient:
          "radial-gradient(120% 80% at 50% 30%, #c8a06a 0%, #8a6438 55%, #3a280f 100%)",
        heroEmojis: ["\ud83c\udf2f", "\ud83d\udc14", "\ud83e\udd51", "\ud83c\udf45"],
        heroImageUrl: "recipes/r_quick_chicken_wraps.webp",
        servings: 2,
        durationMin: 5,
        kcal: 440, proteinG: 32, carbsG: 38, fatG: 16, fiberG: 6,
        ingredients: [
          { qty: 2,    unit: "", item: "whole-grain wraps" },
          { qty: 1,    unit: "cup", item: "shredded rotisserie chicken" },
          { qty: 0.5,  unit: "cup", item: "hummus" },
          { qty: 1,    unit: "", item: "cucumber, sliced thin" },
          { qty: 2,    unit: "cups", item: "baby spinach" },
        ],
        steps: [
          { title: "Spread", body: "Hummus thick across each wrap." },
          { title: "Layer and roll", body: "Spinach, chicken, cucumber. Roll tight, slice on the bias." },
        ],
        seasonTags: ["quick", "family"],
        safetyFlags: [],
        scripturePairing: null,
        source: "imported",
        sourceUrl: "https://example.com/quick-chicken-wraps",
        lovedCount: 7,
        public: true,
        pendingReview: false,
        createdAt: ago(4),
      },
      // Sprouted (5+) by Eden. Demonstrates raw-fish + high-mercury filtering for pregnant sisters.
      {
        id: "r_spicy_tuna_bowl",
        authorId: "u_eden",
        title: "Spicy tuna and avocado bowl",
        description: "Sushi-grade ahi over rice, sriracha mayo, avocado, sesame. Eden's date-night lunch.",
        heroGradient:
          "radial-gradient(120% 80% at 50% 30%, #d96a5a 0%, #8a3528 55%, #3a1612 100%)",
        heroEmojis: ["\ud83c\udf63", "\ud83e\udd51", "\ud83c\udf5a", "\ud83c\udf36\ufe0f"],
        heroImageUrl: "recipes/r_spicy_tuna_bowl.webp",
        servings: 2,
        durationMin: 15,
        kcal: 540, proteinG: 32, carbsG: 52, fatG: 22, fiberG: 6,
        ingredients: [
          { qty: 0.75, unit: "lb", item: "sushi-grade ahi tuna, cubed" },
          { qty: 1.5,  unit: "cups", item: "jasmine rice, cooked" },
          { qty: 1,    unit: "", item: "avocado, sliced" },
          { qty: 2,    unit: "tbsp", item: "mayo and sriracha mix" },
          { qty: 1,    unit: "tbsp", item: "toasted sesame seeds" },
          { qty: 1,    unit: "", item: "cucumber, ribboned" },
        ],
        steps: [
          { title: "Cube the tuna", body: "Bite-sized pieces, very cold knife and board." },
          { title: "Toss", body: "Tuna with sriracha mayo and sesame." },
          { title: "Build the bowl", body: "Rice, tuna, avocado, cucumber. Eat right away." },
        ],
        seasonTags: ["quick"],
        safetyFlags: ["raw-fish", "high-mercury"],
        scripturePairing: null,
        source: "sister",
        sourceUrl: null,
        lovedCount: 9,
        public: true,
        pendingReview: false,
        createdAt: ago(11),
      },
      // Sprouted (5+) by Mara. Demonstrates deli-meat caution for pregnant sisters.
      {
        id: "r_turkey_brie_sandwich",
        authorId: "u_mara",
        title: "Turkey, brie, and apple sandwich",
        description: "A picnic-pretty sandwich on sourdough. Mara likes it cold from the fridge.",
        heroGradient:
          "radial-gradient(120% 80% at 50% 30%, #d8b878 0%, #a08440 55%, #4a3818 100%)",
        heroEmojis: ["\ud83e\udd6a", "\ud83c\udf4e", "\ud83e\uddc0", "\ud83c\udf5e"],
        heroImageUrl: "recipes/r_turkey_brie_sandwich.webp",
        servings: 2,
        durationMin: 5,
        kcal: 480, proteinG: 28, carbsG: 42, fatG: 22, fiberG: 4,
        ingredients: [
          { qty: 4,    unit: "slices", item: "sourdough bread" },
          { qty: 6,    unit: "oz", item: "sliced deli turkey" },
          { qty: 3,    unit: "oz", item: "brie, sliced" },
          { qty: 1,    unit: "", item: "apple, thin slices" },
          { qty: 2,    unit: "tsp", item: "grainy mustard" },
          { qty: 1,    unit: "handful", item: "arugula" },
        ],
        steps: [
          { title: "Spread", body: "Mustard on each slice." },
          { title: "Layer", body: "Turkey, brie, apple, arugula." },
          { title: "Press and slice", body: "Press gently. Halve on the diagonal." },
        ],
        seasonTags: ["quick", "family"],
        safetyFlags: ["deli-meat", "soft-cheese"],
        scripturePairing: null,
        source: "sister",
        sourceUrl: null,
        lovedCount: 8,
        public: true,
        pendingReview: false,
        createdAt: ago(6),
      },
      // Sprouted (5+) by Ruth. Demonstrates alcohol filter (caution for pregnancy + nursing).
      {
        id: "r_red_wine_braised_beef",
        authorId: "u_ruth",
        title: "Red wine braised beef and root veg",
        description: "Sunday-dinner braise. Burgundy, thyme, carrots, parsnips, falls-apart chuck.",
        heroGradient:
          "radial-gradient(120% 80% at 50% 30%, #a85a4a 0%, #6a2818 55%, #2a0e08 100%)",
        heroEmojis: ["\ud83c\udf77", "\ud83e\udd69", "\ud83e\udd55", "\ud83c\udf3f"],
        heroImageUrl: "recipes/r_red_wine_braised_beef.webp",
        servings: 6,
        durationMin: 180,
        kcal: 580, proteinG: 38, carbsG: 22, fatG: 32, fiberG: 5,
        ingredients: [
          { qty: 3,    unit: "lb", item: "chuck roast, cubed" },
          { qty: 2,    unit: "cups", item: "dry red wine" },
          { qty: 4,    unit: "", item: "carrots, large dice" },
          { qty: 3,    unit: "", item: "parsnips, large dice" },
          { qty: 1,    unit: "", item: "yellow onion, sliced" },
          { qty: 4,    unit: "sprigs", item: "fresh thyme" },
          { qty: 2,    unit: "cups", item: "beef broth" },
        ],
        steps: [
          { title: "Sear hot", body: "Brown beef hard on all sides. Set aside." },
          { title: "Build the braise", body: "Onion until soft. Wine to deglaze, reduce by half. Add broth, herbs, beef." },
          { title: "Slow oven", body: "325\u00b0F covered, 2.5 hours. Add roots in the last hour." },
        ],
        seasonTags: ["family"],
        safetyFlags: ["alcohol"],
        scripturePairing: null,
        source: "sister",
        sourceUrl: null,
        lovedCount: 6,
        public: true,
        pendingReview: false,
        createdAt: ago(9),
      },
    ];
    return seeds.map((s) => ({ ...s, awardTier: tierFor(s.lovedCount) }));
  })(),
  savedRecipes: [
    // Hannah has saved Kiley's hero and Mara's nursing soup.
    { id: "sr_1", userId: "u_hannah", recipeId: "r_sheetpan_salmon",     savedAt: now() - 1000 * 60 * 60 * 24 * 5 },
    { id: "sr_2", userId: "u_hannah", recipeId: "r_chicken_lentil_soup", savedAt: now() - 1000 * 60 * 60 * 24 * 2 },
  ],
  recipeLoves: [
    // Hannah loved the salmon after cooking it last week.
    { id: "rl_1", userId: "u_hannah", recipeId: "r_sheetpan_salmon", lovedAt: now() - 1000 * 60 * 60 * 24 * 4 },
  ],
  pairSharedScriptures: (() => {
    const day = 1000 * 60 * 60 * 24;
    // The Monday of this week, in YYYY-MM-DD form.
    const d = new Date();
    const dayOfWeek = d.getDay();          // 0..6 with Sunday = 0
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    d.setDate(d.getDate() - mondayOffset);
    const weekStart = d.toISOString().slice(0, 10);
    return [
      {
        id: "ps_w1",
        pairId: "pair_h_b",
        weekStart,
        reference: "Isaiah 40:31",
        text: "But those who hope in the Lord will renew their strength. They will soar on wings like eagles; they will run and not grow weary, they will walk and not be faint.",
        // Bethany has already reacted, Hannah has not yet.
        inviterReaction: null,
        inviteeReaction: "heart" as const,
        inviterNote: null,
        inviteeNote: "This one is steadying me through the night feeds.",
        createdAt: now() - day * 1,
      },
    ];
  })(),
  pairSharedRecipes: (() => {
    const day = 1000 * 60 * 60 * 24;
    const d = new Date();
    const dayOfWeek = d.getDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    d.setDate(d.getDate() - mondayOffset);
    const weekStart = d.toISOString().slice(0, 10);
    return [
      {
        id: "psr_w1",
        pairId: "pair_h_b",
        weekStart,
        // Bethany picked her own postpartum oats so they would cook the same
        // dish on different mornings and compare notes.
        recipeId: "r_postpartum_steel_oats",
        pickedBy: "u_bethany",
        inviterCookedAt: null,
        inviteeCookedAt: now() - day * 1,
        createdAt: now() - day * 2,
      },
    ];
  })(),
  matchSuggestions: [
    // No open suggestion for Hannah, she's already paired. Seed a skipped one
    // for the audit trail and so the screen can show "recently skipped" if we
    // want to later. For now this is just here to keep the table populated.
    {
      id: "ms_h_skip_1",
      forUserId: "u_hannah",
      candidateId: "u_ruth",
      reason: "You're both walking through the postpartum season.",
      createdAt: now() - 1000 * 60 * 60 * 24 * 9,
      status: "skipped" as const,
    },
  ],
  /* ---------------------- Scripture-at-log seed pool ---------------------- */
  mealVerses: [
    // First-meal-of-day blessings (run when no other log exists for today)
    { id: "mv_first_1", text: "Give us today our daily bread.", reference: "Matthew 6:11",
      contexts: ["first-meal", "morning", "general"] },
    { id: "mv_first_2", text: "His mercies are new every morning.", reference: "Lamentations 3:23",
      contexts: ["first-meal", "morning"] },
    { id: "mv_first_3", text: "In the morning, Lord, you hear my voice.", reference: "Psalm 5:3",
      contexts: ["first-meal", "morning"] },

    // Morning / breakfast
    { id: "mv_morn_1", text: "Satisfy us in the morning with your unfailing love.", reference: "Psalm 90:14",
      contexts: ["morning"] },
    { id: "mv_morn_2", text: "You open your hand and satisfy the desires of every living thing.", reference: "Psalm 145:16",
      contexts: ["morning", "general"] },

    // Midday / lunch
    { id: "mv_mid_1", text: "Whatever you eat or drink, do it all for the glory of God.", reference: "1 Corinthians 10:31",
      contexts: ["midday", "general"] },
    { id: "mv_mid_2", text: "Taste and see that the Lord is good.", reference: "Psalm 34:8",
      contexts: ["midday", "general"] },
    { id: "mv_mid_3", text: "Come to me, all you who are weary, and I will give you rest.", reference: "Matthew 11:28",
      contexts: ["midday", "gentle"] },

    // Evening / dinner
    { id: "mv_eve_1", text: "You prepare a table before me. My cup overflows.", reference: "Psalm 23:5",
      contexts: ["evening"] },
    { id: "mv_eve_2", text: "In peace I will lie down and sleep, for you alone make me dwell in safety.", reference: "Psalm 4:8",
      contexts: ["evening"] },
    { id: "mv_eve_3", text: "Where two or three gather in my name, there am I with them.", reference: "Matthew 18:20",
      contexts: ["evening", "general"] },

    // Snack / small bites
    { id: "mv_snack_1", text: "How sweet are your words to my taste, sweeter than honey to my mouth.", reference: "Psalm 119:103",
      contexts: ["snack"] },
    { id: "mv_snack_2", text: "He gives strength to the weary and increases the power of the weak.", reference: "Isaiah 40:29",
      contexts: ["snack", "gentle"] },

    // Protein-rich (strength building)
    { id: "mv_prot_1", text: "She sets about her work vigorously; her arms are strong for her tasks.", reference: "Proverbs 31:17",
      contexts: ["protein-rich"] },
    { id: "mv_prot_2", text: "The Lord is the strength of my life.", reference: "Psalm 27:1",
      contexts: ["protein-rich"] },
    { id: "mv_prot_3", text: "She is clothed with strength and dignity.", reference: "Proverbs 31:25",
      contexts: ["protein-rich"] },

    // Gentle (low-protein, low-kcal logs, comfort food, hard days)
    { id: "mv_gentle_1", text: "He tends his flock like a shepherd; he gathers the lambs in his arms.", reference: "Isaiah 40:11",
      contexts: ["gentle"] },
    { id: "mv_gentle_2", text: "The Lord is close to the brokenhearted.", reference: "Psalm 34:18",
      contexts: ["gentle"] },
    { id: "mv_gentle_3", text: "My grace is sufficient for you, for my power is made perfect in weakness.", reference: "2 Corinthians 12:9",
      contexts: ["gentle"] },

    // Daily total complete (kcal goal reached, ~90%+)
    { id: "mv_done_1", text: "Well done, good and faithful servant.", reference: "Matthew 25:23",
      contexts: ["daily-complete"] },
    { id: "mv_done_2", text: "She watches over the affairs of her household.", reference: "Proverbs 31:27",
      contexts: ["daily-complete"] },
    { id: "mv_done_3", text: "You have done well to nourish the body God gave you.", reference: "a blessing for today",
      contexts: ["daily-complete"] },
    { id: "mv_done_4", text: "The Lord bless you and keep you. The Lord make his face shine on you.", reference: "Numbers 6:24-25",
      contexts: ["daily-complete"] },

    // General fallbacks
    { id: "mv_gen_1", text: "You are altogether beautiful, my darling; there is no flaw in you.", reference: "Song of Songs 4:7",
      contexts: ["general"] },
    { id: "mv_gen_2", text: "You knit me together in my mother's womb. I am fearfully and wonderfully made.", reference: "Psalm 139:13-14",
      contexts: ["general"] },
    { id: "mv_gen_3", text: "Cast all your anxiety on him because he cares for you.", reference: "1 Peter 5:7",
      contexts: ["general", "gentle"] },
  ] as MealVerse[],
  verseHistory: [] as VerseHistoryEntry[],
  /* ----------------------------- Sabbath seed ---------------------------- */
  sabbathSettings: [
    {
      userId: "u_hannah",
      enabled: true,
      dayOfWeek: 0 as DayOfWeek, // Sunday
      pauseStreaks: true,
      devotionalReminder: true,
      updatedAt: now() - 1000 * 60 * 60 * 24 * 14,
    },
  ] as SabbathSettings[],
  sabbathDevotionals: [
    {
      id: "sd_sun",
      dayOfWeek: 0,
      title: "He calls you to rest",
      passage: "Come to me, all you who are weary and burdened, and I will give you rest.",
      reference: "Matthew 11:28",
      reflection: "Today the to-do list can wait. The kitchen counter, the protein log, the thousand small ways you serve. He is not asking for one more rep today. He is asking for your presence.",
      invitation: "Sit with one cup of coffee. No phone for ten minutes.",
    },
    {
      id: "sd_mon",
      dayOfWeek: 1,
      title: "A still beginning",
      passage: "Be still, and know that I am God.",
      reference: "Psalm 46:10",
      reflection: "Mondays do not need to be conquered. The week ahead belongs to the One who already walked it. You can begin from rest, not toward it.",
      invitation: "Name one thing you are not in charge of today.",
    },
    {
      id: "sd_tue",
      dayOfWeek: 2,
      title: "Quiet waters",
      passage: "He leads me beside quiet waters, he refreshes my soul.",
      reference: "Psalm 23:2-3",
      reflection: "Refreshment is a gift, not a goal. Notice today where the Shepherd is leading you toward stillness, and follow him there without earning it.",
      invitation: "Take a slow walk outside, even just to the mailbox.",
    },
    {
      id: "sd_wed",
      dayOfWeek: 3,
      title: "Daily bread, midweek",
      passage: "Give us today our daily bread.",
      reference: "Matthew 6:11",
      reflection: "Today, not the whole pantry. Today, not the whole calendar. He provides for the day in front of you, not the imagined ones.",
      invitation: "Eat one meal slowly, with gratitude, no scrolling.",
    },
    {
      id: "sd_thu",
      dayOfWeek: 4,
      title: "He carries the lambs",
      passage: "He tends his flock like a shepherd; he gathers the lambs in his arms and carries them close to his heart.",
      reference: "Isaiah 40:11",
      reflection: "The mothering work, the caring work, the work that no one sees. He sees. And he carries the one who carries.",
      invitation: "Tell one sister, \"I see you.\" That is the whole assignment.",
    },
    {
      id: "sd_fri",
      dayOfWeek: 5,
      title: "It is finished",
      passage: "It is finished.",
      reference: "John 19:30",
      reflection: "The week's unfinished list is not your verdict. The work that mattered most was finished long before your inbox filled. Lay it down.",
      invitation: "Close one open tab, literal or figurative, before bed.",
    },
    {
      id: "sd_sat",
      dayOfWeek: 6,
      title: "Sown in tears, reaped in joy",
      passage: "Those who sow with tears will reap with songs of joy.",
      reference: "Psalm 126:5",
      reflection: "Saturdays often feel mixed, half-full, half-rest, half-task. The Lord is not measuring your output today. He is measuring your tenderness with yourself.",
      invitation: "Bake or buy something good. Share half of it.",
    },
  ] as SabbathDevotional[],

  versesOfWeek: [
    {
      id: "vow_2026_04_19",
      weekStartIso: "2026-04-19",
      text: "She is clothed with strength and dignity, and she laughs without fear of the future.",
      reference: "Proverbs 31:25",
      theme: "strength",
      reflection:
        "Strength here is not steel. It is dignity rooted deep enough that tomorrow loses its power to threaten today. The laugh is the giveaway.",
      invitation:
        "Where might you laugh, even quietly, at a fear that has been loud this week?",
    },
    {
      id: "vow_2026_04_26",
      weekStartIso: "2026-04-26",
      text: "Come to me, all you who are weary and burdened, and I will give you rest.",
      reference: "Matthew 11:28",
      theme: "rest",
      reflection:
        "Rest is not the reward for finishing. It is the place from which the rest of the week is lived. He invites the weary first, not the productive first.",
      invitation:
        "What is one weight you can set down this week, even for an afternoon?",
    },
    {
      id: "vow_2026_05_03",
      weekStartIso: "2026-05-03",
      text: "You are altogether beautiful, my darling; there is no flaw in you.",
      reference: "Song of Songs 4:7",
      theme: "identity",
      reflection:
        "This is the voice of love speaking over you before any list of fixes begins. Identity precedes improvement, never the other way around.",
      invitation:
        "What sentence about yourself could you let go of, just for today?",
    },
    {
      id: "vow_2026_05_10",
      weekStartIso: "2026-05-10",
      text: "Those who hope in the Lord will renew their strength. They will soar on wings like eagles.",
      reference: "Isaiah 40:31",
      theme: "hope",
      reflection:
        "Renewal is layered into the design. Hope is not denial of the tired, it is the slow lift that finds you waiting.",
      invitation:
        "Where do you most need renewal, and could you ask for it out loud?",
    },
  ] as VerseOfWeek[],

  verseCheckIns: [
    {
      id: "vci_seed_bethany",
      userId: "u_bethany",
      weekStartIso: "2026-04-26",
      reflection:
        "Sat with Matthew 11:28 between feedings. The word 'come' is doing something in me.",
      sharedToFeed: true,
      createdAt: Date.now() - 1000 * 60 * 60 * 6,
    },
    {
      id: "vci_seed_rebecca",
      userId: "u_rebecca",
      weekStartIso: "2026-04-26",
      reflection: "",
      sharedToFeed: false,
      createdAt: Date.now() - 1000 * 60 * 60 * 22,
    },
    {
      id: "vci_seed_joanna",
      userId: "u_joanna",
      weekStartIso: "2026-04-26",
      reflection:
        "Sat with this verse on a walk this morning. Setting down the to-do list felt like obedience, not laziness.",
      sharedToFeed: true,
      createdAt: Date.now() - 1000 * 60 * 60 * 30,
    },
    {
      id: "vci_seed_ruth",
      userId: "u_ruth",
      weekStartIso: "2026-04-26",
      reflection:
        "Came back to 'come to me' three times today. Each time I had to remind myself the invitation is open.",
      sharedToFeed: true,
      createdAt: Date.now() - 1000 * 60 * 60 * 50,
    },
  ] as VerseCheckIn[],

  challenges: (() => {
    const day = 1000 * 60 * 60 * 24;
    const t = Date.now();
    return [
      {
        id: "ch_launch",
        slug: "bear-fruit-together",
        title: "Bear Fruit Together",
        subtitle: "Our launch circle. Forty days of returning, side by side.",
        scripture:
          "By their fruit you will recognize them. Every good tree bears good fruit.",
        scriptureRef: "Matthew 7:16-17",
        invitation:
          "Forty days, one practice a day. Log a meal, sit with the verse, lift up a sister, or move your body. Any one counts. We are walking this together.\n\nFounding members who join before the window closes keep half-off pricing for life on the tier they choose. This is our way of thanking the sisters who said yes first.",
        threshold: {
          discipline: "any" as const,
          targetDays: 30,
          windowDays: 40,
          graceDays: 10,
        },
        startsAt: t - day * 6,
        endsAt: t + day * 34,
        tone: "gold" as const,
        hero: { primary: "\ud83c\udf42", secondary: "\ud83c\udf31" },
        isLaunchChallenge: true,
        foundingOffer:
          "Founding members keep half-off pricing for life. Seedling $23.50, Rooted $48.50, Flourishing $98.50, locked in as long as you stay.",
        featured: true,
        authorId: "u_kiley",
        createdAt: t - day * 7,
      },
      {
        id: "ch_psalm23",
        slug: "psalm-23-mornings",
        title: "Psalm 23 Mornings",
        subtitle: "Begin each day with the same shepherd, for two weeks.",
        scripture:
          "The Lord is my shepherd, I lack nothing.",
        scriptureRef: "Psalm 23:1",
        invitation:
          "For fourteen days, open Psalm 23 before you open anything else. Sit with one verse a day, in the order they come. No journaling required, no posting required, just the words of the shepherd before the noise.",
        threshold: {
          discipline: "verse" as const,
          targetDays: 12,
          windowDays: 14,
          graceDays: 3,
        },
        startsAt: t - day * 2,
        endsAt: t + day * 12,
        tone: "forest" as const,
        hero: { primary: "\ud83d\udcd6", secondary: "\u2728" },
        isLaunchChallenge: false,
        featured: true,
        authorId: "u_kiley",
        createdAt: t - day * 3,
      },
      {
        id: "ch_lift_a_sister",
        slug: "lift-a-sister",
        title: "Lift A Sister",
        subtitle: "Pray over one sister a day, for twenty-one days.",
        scripture:
          "Carry each other's burdens, and in this way you will fulfill the law of Christ.",
        scriptureRef: "Galatians 6:2",
        invitation:
          "Open the prayer wall once a day and water one sister's seed with grace. Three weeks of small, real prayer for women you may never meet, watching what God does in your own heart while you do.",
        threshold: {
          discipline: "prayer" as const,
          targetDays: 18,
          windowDays: 21,
          graceDays: 4,
        },
        startsAt: t + day * 7,
        endsAt: t + day * 28,
        tone: "coral" as const,
        hero: { primary: "\ud83d\ude4f", secondary: "\ud83d\udca7" },
        isLaunchChallenge: false,
        featured: false,
        authorId: "u_kiley",
        createdAt: t - day * 1,
      },
    ] as Challenge[];
  })(),

  challengeParticipants: (() => {
    const day = 1000 * 60 * 60 * 24;
    const t = Date.now();
    const isoNDaysAgo = (n: number) =>
      new Date(t - n * day).toISOString().slice(0, 10);
    // Hannah is in the launch challenge as a founding member.
    const hannahDays: string[] = [];
    for (let i = 0; i < 5; i++) hannahDays.push(isoNDaysAgo(i));

    const sisters: Array<[string, number, boolean]> = [
      // userId, daysCounted, completed
      ["u_hannah", 5, false],
      ["u_rebecca", 6, false],
      ["u_joanna", 6, false],
      ["u_mara", 4, false],
      ["u_ruth", 3, false],
      ["u_bethany", 2, false],
    ];
    const launch = sisters.map(([uid, n, done], idx) => {
      const days: string[] = [];
      for (let i = 0; i < n; i++) days.push(isoNDaysAgo(i));
      return {
        id: `cp_launch_${uid}`,
        challengeId: "ch_launch",
        userId: uid,
        joinedAt: t - day * 6 + idx * 1000 * 60 * 30,
        daysCounted: days,
        completedAt: done ? t - day : null,
        isFoundingMember: true,
        foundingTier: uid === "u_hannah" ? "Rooted" : uid === "u_bethany" ? "Seedling" : (idx % 2 === 0 ? "Flourishing" : "Rooted"),
      } as ChallengeParticipant;
    });

    // Rebecca and Joanna joined the Psalm 23 challenge.
    const psalmDaysReb: string[] = [];
    for (let i = 0; i < 3; i++) psalmDaysReb.push(isoNDaysAgo(i));
    const psalmDaysJo: string[] = [];
    for (let i = 0; i < 2; i++) psalmDaysJo.push(isoNDaysAgo(i));

    const psalm: ChallengeParticipant[] = [
      {
        id: "cp_psalm_rebecca",
        challengeId: "ch_psalm23",
        userId: "u_rebecca",
        joinedAt: t - day * 2,
        daysCounted: psalmDaysReb,
        completedAt: null,
        isFoundingMember: false,
      },
      {
        id: "cp_psalm_joanna",
        challengeId: "ch_psalm23",
        userId: "u_joanna",
        joinedAt: t - day * 2,
        daysCounted: psalmDaysJo,
        completedAt: null,
        isFoundingMember: false,
      },
    ];
    return [...launch, ...psalm];
  })(),

  challengeReflections: (() => {
    const t = Date.now();
    const hr = 1000 * 60 * 60;
    return [
      {
        id: "cr_launch_rebecca_1",
        challengeId: "ch_launch",
        userId: "u_rebecca",
        body: "Day 6. The Verse of the Week showed up at the exact moment I needed it. Day-by-day grace.",
        dayMarker: 6,
        createdAt: t - hr * 3,
      },
      {
        id: "cr_launch_joanna_1",
        challengeId: "ch_launch",
        userId: "u_joanna",
        body: "Logged a real lunch instead of grazing. Small, but the kind of small that adds up over forty days.",
        dayMarker: 5,
        createdAt: t - hr * 8,
      },
      {
        id: "cr_launch_bethany_1",
        challengeId: "ch_launch",
        userId: "u_bethany",
        body: "First week of mothering with intention. The challenge gives me a reason to sit and breathe with the verse before the day begins.",
        dayMarker: 2,
        createdAt: t - hr * 14,
      },
      {
        id: "cr_psalm_rebecca_1",
        challengeId: "ch_psalm23",
        userId: "u_rebecca",
        body: "Verse one this morning. 'I lack nothing.' Said it three times before I believed it.",
        dayMarker: 1,
        createdAt: t - hr * 36,
      },
    ] as ChallengeReflection[];
  })(),

  /* Hannah signed up 60 days ago. We seed her with the autoflow already
   * dismissed so it does not pop on Today, but she can revisit any day
   * via /welcome-journey. demoOverrideDay can be set from there to peek. */
  welcomeProgress: [
    {
      userId: "u_hannah",
      completedActions: [
        "day-1.set-sabbath",
        "day-1.complete",
        "day-2.complete",
        "day-3.log-meal",
        "day-3.complete",
        "day-4.complete",
        "day-5.complete",
      ],
      completedDays: [1, 2, 3, 4, 5],
      dismissedAt: now() - 1000 * 60 * 60 * 24 * 50,
      demoOverrideDay: null,
      createdAt: now() - 1000 * 60 * 60 * 24 * 60,
    },
  ] as WelcomeProgress[],

  /* Hannah pre-accepted current legal bundle so the demo skips the gate.
   * New sisters will hit the consent screen first launch. */
  legalConsents: [
    {
      id: "lc_hannah_seed",
      userId: "u_hannah",
      version: "2026.04.27",
      acceptedTermsOfService: true,
      acceptedPrivacyPolicy: true,
      acceptedMedicalDisclaimer: true,
      acceptedCommunityGuidelines: true,
      confirmedAdult: true,
      acceptedAt: now() - 1000 * 60 * 60 * 24 * 7,
    },
  ] as LegalConsent[],

  /* Hannah leaves notifications in the unknown state on first launch so the
   * permission prompt shows once. Once she answers it, all five categories
   * default on, with the daily scripture reminder set to 7am. */
  notificationPrefs: [
    {
      userId: "u_hannah",
      permissionStatus: "unknown",
      scriptureReminders: true,
      scriptureTime: "07:00",
      prayerWallReplies: true,
      weeklyCheckIns: true,
      coachingMessages: true,
      communityAndLive: true,
      updatedAt: now() - 1000 * 60 * 60 * 24,
    },
  ] as NotificationPrefs[],

  /* Hannah's subscription. Founding member, Rooted tier, annual cycle.
   * Founding price for Rooted is $48.50/year so we model 4850 cents.
   * Renewal is set 9 days out so a 7-day-prior reminder lands in 2 days. */
  subscriptions: [
    {
      userId: "u_hannah",
      tier: "Rooted" as Tier,
      cycle: "annual" as SubscriptionCycle,
      isFoundingMember: true,
      status: "active" as SubscriptionStatus,
      platform: "web" as SubscriptionPlatform,
      currentPeriodStart: now() - 1000 * 60 * 60 * 24 * 356,
      currentPeriodEnd: now() + 1000 * 60 * 60 * 24 * 9,
      trialEndsAt: null,
      priceCents: 4850,
      cancelAtPeriodEnd: false,
      paymentMethodLast4: "4242",
      startedAt: now() - 1000 * 60 * 60 * 24 * 356,
    },
  ] as Subscription[],

  /* The 7-day annual reminder Hannah should receive in 2 days, plus one
   * already-sent renewal from a year ago so she has a billing history. */
  renewalReminders: [
    {
      id: "rr_hannah_2027",
      userId: "u_hannah",
      kind: "annual-7day" as RenewalReminderKind,
      scheduledFor: now() + 1000 * 60 * 60 * 24 * 2,
      renewalDate: now() + 1000 * 60 * 60 * 24 * 9,
      amountCents: 4850,
      channel: "both" as const,
      sentAt: null,
      createdAt: now() - 1000 * 60 * 60 * 24 * 30,
    },
    {
      id: "rr_hannah_2026",
      userId: "u_hannah",
      kind: "annual-7day" as RenewalReminderKind,
      scheduledFor: now() - 1000 * 60 * 60 * 24 * 363,
      renewalDate: now() - 1000 * 60 * 60 * 24 * 356,
      amountCents: 4850,
      channel: "both" as const,
      sentAt: now() - 1000 * 60 * 60 * 24 * 363,
      createdAt: now() - 1000 * 60 * 60 * 24 * 393,
    },
  ] as RenewalReminder[],

  /* A handful of coach notifications so Kiley's history looks lived-in. */
  coachNotifications: [
    {
      id: "cn_seed_1",
      authorId: "u_kiley",
      audience: "all" as CoachNotificationAudience,
      title: "Sunday reflection",
      body: "Sisters, take five quiet minutes tonight. Where did you see grace this week?",
      category: "encouragement" as CoachNotificationCategory,
      scheduledFor: null,
      sentAt: now() - 1000 * 60 * 60 * 24 * 6,
      recipientCount: 247,
      createdAt: now() - 1000 * 60 * 60 * 24 * 6,
    },
    {
      id: "cn_seed_2",
      authorId: "u_kiley",
      audience: "founding-members" as CoachNotificationAudience,
      title: "A note for our founding sisters",
      body: "Thank you for walking with me from the beginning. Your half-off pricing is locked in for life.",
      category: "announcement" as CoachNotificationCategory,
      scheduledFor: null,
      sentAt: now() - 1000 * 60 * 60 * 24 * 2,
      recipientCount: 38,
      createdAt: now() - 1000 * 60 * 60 * 24 * 2,
    },
  ] as CoachNotification[],

  /* The Sisterhood feed. Mix of all five post kinds across mentees so the
   * feed feels lived-in for Hannah at first open. Reactions and comments
   * follow below. */
  sisterhoodPosts: ((): SisterhoodPost[] => {
    const min = 1000 * 60;
    const hr = min * 60;
    const day = hr * 24;
    const editLock = (createdAt: number) => createdAt + day;
    return [
      {
        id: "sp_1", authorId: "u_rebecca", kind: "win",
        body: "Forty days of showing up. I cried lifting the bar this morning, but I lifted it. Thank you sisters for praying me through last week.",
        photoDataUrl: null, verseRef: null, status: "live",
        createdAt: now() - min * 22, editLockedAt: editLock(now() - min * 22),
        flaggedBy: null, flaggedReason: null,
      },
      {
        id: "sp_2", authorId: "u_joanna", kind: "prayer",
        body: "Asking for prayer over a hard parenting season. Some days I forget I'm allowed to rest. Praying I find the still place this week.",
        photoDataUrl: null, verseRef: null, status: "live",
        createdAt: now() - min * 95, editLockedAt: editLock(now() - min * 95),
        flaggedBy: null, flaggedReason: null,
      },
      {
        id: "sp_3", authorId: "u_mara", kind: "verse",
        body: "She is clothed with strength and dignity, and she laughs without fear of the future.",
        photoDataUrl: null, verseRef: "Proverbs 31:25", status: "live",
        createdAt: now() - hr * 4, editLockedAt: editLock(now() - hr * 4),
        flaggedBy: null, flaggedReason: null,
      },
      {
        id: "sp_4", authorId: "u_ruth", kind: "testimony",
        body: "A year ago I could not finish a flight of stairs without my chest tight. Today I walked 4 miles after morning chores. The Lord rebuilds slowly.",
        photoDataUrl: null, verseRef: null, status: "live",
        createdAt: now() - hr * 6, editLockedAt: editLock(now() - hr * 6),
        flaggedBy: null, flaggedReason: null,
      },
      {
        id: "sp_5", authorId: "u_bethany", kind: "question",
        body: "Sisters, three weeks postpartum. What is one small ritual that helped you find yourself again? Looking for one tender thing to add to mornings.",
        photoDataUrl: null, verseRef: null, status: "live",
        createdAt: now() - hr * 8, editLockedAt: editLock(now() - hr * 8),
        flaggedBy: null, flaggedReason: null,
      },
      {
        id: "sp_6", authorId: "u_eden", kind: "prayer",
        body: "Pray for me, sisters. The honesty is, I am tired and I miss who I used to be. Asking for one steady week.",
        photoDataUrl: null, verseRef: null, status: "live",
        createdAt: now() - hr * 11, editLockedAt: editLock(now() - hr * 11),
        flaggedBy: null, flaggedReason: null,
      },
      {
        id: "sp_7", authorId: "u_rebecca", kind: "verse",
        body: "I can do all things through Christ who strengthens me. Carrying this into deadlift day.",
        photoDataUrl: null, verseRef: "Philippians 4:13", status: "live",
        createdAt: now() - hr * 14, editLockedAt: editLock(now() - hr * 14),
        flaggedBy: null, flaggedReason: null,
      },
      {
        id: "sp_8", authorId: "u_mara", kind: "win",
        body: "Cooked the family meal plan three nights in a row. Husband asked what got into me. I told him it was Jesus and a slow cooker.",
        photoDataUrl: null, verseRef: null, status: "live",
        createdAt: now() - hr * 18, editLockedAt: editLock(now() - hr * 18),
        flaggedBy: null, flaggedReason: null,
      },
      {
        id: "sp_9", authorId: "u_joanna", kind: "testimony",
        body: "First family meal plan complete. The children even asked for the kale bowl again. Praise God.",
        photoDataUrl: null, verseRef: null, status: "live",
        createdAt: now() - hr * 22, editLockedAt: editLock(now() - hr * 22),
        flaggedBy: null, flaggedReason: null,
      },
      {
        id: "sp_10", authorId: "u_ruth", kind: "question",
        body: "Looking for honest stories from sisters in the second half of perimenopause. What helped you sleep again?",
        photoDataUrl: null, verseRef: null, status: "live",
        createdAt: now() - day * 1 - hr * 2, editLockedAt: editLock(now() - day * 1 - hr * 2),
        flaggedBy: null, flaggedReason: null,
      },
      {
        id: "sp_11", authorId: "u_bethany", kind: "testimony",
        body: "First week of Seedling. I cooked one real meal instead of grazing on chips. It feels small but it isn't. Thank you for the welcome.",
        photoDataUrl: null, verseRef: null, status: "live",
        createdAt: now() - day * 1 - hr * 6, editLockedAt: editLock(now() - day * 1 - hr * 6),
        flaggedBy: null, flaggedReason: null,
      },
      // One queued post in moderation, hit a sensitive keyword (weight)
      {
        id: "sp_12", authorId: "u_eden", kind: "testimony",
        body: "Down 8 pounds since starting Seedling. The numbers are not the point. Slept through the night four times this week, that is.",
        photoDataUrl: null, verseRef: null, status: "queued",
        createdAt: now() - hr * 1, editLockedAt: editLock(now() - hr * 1),
        flaggedBy: null, flaggedReason: null,
      },
    ];
  })(),

  sisterhoodReactions: ((): SisterhoodReaction[] => {
    const min = 1000 * 60;
    return [
      // Hannah amen-ed Rebecca's win and is praying with Joanna and Eden
      { id: "sr_1",  postId: "sp_1",  userId: "u_hannah",  kind: "amen",     reactedAt: now() - min * 12 },
      { id: "sr_2",  postId: "sp_2",  userId: "u_hannah",  kind: "praying",  reactedAt: now() - min * 60 },
      { id: "sr_3",  postId: "sp_6",  userId: "u_hannah",  kind: "praying",  reactedAt: now() - min * 30 },
      { id: "sr_4",  postId: "sp_3",  userId: "u_hannah",  kind: "amen",     reactedAt: now() - min * 90 },
      { id: "sr_5",  postId: "sp_5",  userId: "u_hannah",  kind: "me-too",   reactedAt: now() - min * 200 },
      // Other sisters reacting
      { id: "sr_6",  postId: "sp_1",  userId: "u_mara",    kind: "amen",     reactedAt: now() - min * 14 },
      { id: "sr_7",  postId: "sp_1",  userId: "u_kiley",   kind: "amen",     reactedAt: now() - min * 16 },
      { id: "sr_8",  postId: "sp_1",  userId: "u_ruth",    kind: "amen",     reactedAt: now() - min * 19 },
      { id: "sr_9",  postId: "sp_1",  userId: "u_bethany", kind: "amen",     reactedAt: now() - min * 21 },
      { id: "sr_10", postId: "sp_2",  userId: "u_kiley",   kind: "praying",  reactedAt: now() - min * 70 },
      { id: "sr_11", postId: "sp_2",  userId: "u_rebecca", kind: "praying",  reactedAt: now() - min * 75 },
      { id: "sr_12", postId: "sp_2",  userId: "u_mara",    kind: "me-too",   reactedAt: now() - min * 80 },
      { id: "sr_13", postId: "sp_3",  userId: "u_rebecca", kind: "amen",     reactedAt: now() - min * 100 },
      { id: "sr_14", postId: "sp_3",  userId: "u_bethany", kind: "amen",     reactedAt: now() - min * 110 },
      { id: "sr_15", postId: "sp_4",  userId: "u_mara",    kind: "amen",     reactedAt: now() - min * 200 },
      { id: "sr_16", postId: "sp_5",  userId: "u_mara",    kind: "me-too",   reactedAt: now() - min * 220 },
      { id: "sr_17", postId: "sp_5",  userId: "u_kiley",   kind: "praying",  reactedAt: now() - min * 240 },
      { id: "sr_18", postId: "sp_6",  userId: "u_kiley",   kind: "praying",  reactedAt: now() - min * 280 },
      { id: "sr_19", postId: "sp_6",  userId: "u_rebecca", kind: "praying",  reactedAt: now() - min * 285 },
      { id: "sr_20", postId: "sp_7",  userId: "u_hannah",  kind: "amen",     reactedAt: now() - min * 300 },
      { id: "sr_21", postId: "sp_8",  userId: "u_hannah",  kind: "amen",     reactedAt: now() - min * 400 },
      { id: "sr_22", postId: "sp_11", userId: "u_hannah",  kind: "amen",     reactedAt: now() - min * 1000 },
      { id: "sr_23", postId: "sp_11", userId: "u_kiley",   kind: "amen",     reactedAt: now() - min * 1010 },
      { id: "sr_24", postId: "sp_11", userId: "u_mara",    kind: "me-too",   reactedAt: now() - min * 1020 },
    ];
  })(),

  sisterhoodComments: ((): SisterhoodComment[] => {
    const min = 1000 * 60;
    return [
      { id: "sc_1", postId: "sp_1", userId: "u_kiley",  body: "Sister, this is a banner. Forty days of yes is forty victories.", status: "live", createdAt: now() - min * 18 },
      { id: "sc_2", postId: "sp_1", userId: "u_mara",   body: "You moved me to tears. Lifting with you next time.",                status: "live", createdAt: now() - min * 14 },
      { id: "sc_3", postId: "sp_2", userId: "u_hannah", body: "Praying right now, sister. Rest is faithful too.",                  status: "live", createdAt: now() - min * 70 },
      { id: "sc_4", postId: "sp_5", userId: "u_mara",   body: "A warm cup of something with the baby in the carrier, before anyone else is up. That five minutes saved me.", status: "live", createdAt: now() - min * 220 },
      { id: "sc_5", postId: "sp_5", userId: "u_rebecca",body: "Open the curtains before you check your phone. Light first, screens later. Small thing, big return.", status: "live", createdAt: now() - min * 240 },
      { id: "sc_6", postId: "sp_6", userId: "u_kiley",  body: "Praying, Eden. You are not behind. One steady day at a time.",        status: "live", createdAt: now() - min * 280 },
      { id: "sc_7", postId: "sp_10", userId: "u_hannah",body: "Magnesium glycinate at dusk and a 20-minute walk after dinner shifted the needle for me.", status: "live", createdAt: now() - min * 1100 },
    ];
  })(),

  sisterhoodReports: [] as SisterhoodReport[],

  /* Network profiles. Hannah is NOT yet a member; she is 3 days into pair_h_b
   * which gives a clean eligibility-gate demo. The five mentees who have
   * walked their seasons through serve as initial veterans Hannah can browse
   * once she crosses 90 days. */
  networkProfiles: ((): NetworkProfile[] => {
    const day = 1000 * 60 * 60 * 24;
    return [
      {
        userId: "u_rebecca", isOpenIn: true,
        bio: "Lifting heavy and learning to receive grace at the same pace.",
        lifeStage: "married", goals: ["strength", "scripture", "body-image"],
        joinedNetworkAt: now() - day * 110, updatedAt: now() - day * 8,
      },
      {
        userId: "u_joanna", isOpenIn: true,
        bio: "Perimenopause, three teens, learning to rest without guilt.",
        lifeStage: "mama", goals: ["perimenopause", "rest", "motherhood"],
        joinedNetworkAt: now() - day * 96, updatedAt: now() - day * 4,
      },
      {
        userId: "u_mara", isOpenIn: true,
        bio: "Empty-nester rebuilding rhythm. Slow cooker evangelist.",
        lifeStage: "empty-nester", goals: ["nourishment", "strength", "scripture"],
        joinedNetworkAt: now() - day * 140, updatedAt: now() - day * 12,
      },
      {
        userId: "u_ruth", isOpenIn: true,
        bio: "Walking through grief and finding the Lord in small returns.",
        lifeStage: "married", goals: ["grief", "scripture", "rest"],
        joinedNetworkAt: now() - day * 100, updatedAt: now() - day * 2,
      },
      {
        userId: "u_bethany", isOpenIn: false,
        bio: "Postpartum and protecting the early days. Will open in soon.",
        lifeStage: "postpartum", goals: ["motherhood", "rest", "nourishment"],
        joinedNetworkAt: now() - day * 95, updatedAt: now() - day * 1,
      },
    ];
  })(),

  networkIntros: [] as NetworkIntro[],
  networkMessages: [] as NetworkMessage[],

  /* Movement profile. Hannah arrives with a fully completed profile so the
   * Move tab demo lights up immediately. New sisters get an empty profile
   * created on first onboarding step and flipped to completed on finish. */
  movementProfiles: ((): MovementProfile[] => {
    const day = 1000 * 60 * 60 * 24;
    return [
      {
        userId: "u_hannah",
        injuries: [
          { flag: "joints", detail: "Cranky right knee from running", paused: false },
        ],
        goals: ["build-strength", "flexibility"],
        schedule: { days: [1, 2, 4, 5], minutesPerDay: 45 },
        mesocycleStartedAt: now() - day * 9, // mid week 2
        completed: true,
        updatedAt: now() - day * 1,
      },
    ];
  })(),

  /* Garden Walk months ship a year of fruit. April 2026 is the active
   * month at launch, with March (Love) already closed and May (Joy)
   * lined up for the next rollover. */
  gardenWalkMonths: ((): GardenWalkMonth[] => [
    {
      id: "gw_2026_03",
      year: 2026,
      month: 3,
      fruit: "love",
      title: "March, the month of Love",
      invitation:
        "Walk in love this month. Five gentle minutes a day, a small offering of presence.",
      scripture:
        "And now these three remain, faith, hope and love. But the greatest of these is love.",
      scriptureRef: "1 Corinthians 13:13",
      dailyPrompts: [
        "Who am I walking for today?",
        "Name one woman the Lord brought to mind.",
        "Walk slow enough to notice the sky.",
        "Pray for someone you find hard to love.",
        "Carry one verse with you on this walk.",
        "What kind of love did you receive today?",
        "Step softly. Love is not in a hurry.",
      ],
      targetWalks: 20,
      tone: "coral",
      hero: { primary: "\ud83c\udf37", secondary: "\ud83d\udc95" },
    },
    {
      id: "gw_2026_04",
      year: 2026,
      month: 4,
      fruit: "patience",
      title: "April, the month of Patience",
      invitation:
        "Walk patient. Twenty walks across the month, in your own pace and your own season.",
      scripture:
        "Be still before the Lord and wait patiently for him.",
      scriptureRef: "Psalm 37:7",
      dailyPrompts: [
        "What am I rushing today that the Lord is not?",
        "Notice three small green things on this walk.",
        "Slow your steps to match your breath.",
        "Whose timeline am I borrowing? Return it.",
        "Patience is love, slowed down.",
        "Walk past one impatient thought without grabbing it.",
        "Let your shoulders fall. The day will keep.",
        "What is the Lord asking me to wait on, gently?",
      ],
      targetWalks: 20,
      tone: "forest",
      hero: { primary: "\ud83c\udf3f", secondary: "\ud83d\udd4a\ufe0f" },
    },
    {
      id: "gw_2026_05",
      year: 2026,
      month: 5,
      fruit: "joy",
      title: "May, the month of Joy",
      invitation:
        "Walk in joy this month. Even five minutes counts. Even one step counts as a yes.",
      scripture:
        "The joy of the Lord is your strength.",
      scriptureRef: "Nehemiah 8:10",
      dailyPrompts: [
        "Where did joy meet me today?",
        "Name one thing that delighted you this week.",
        "Walk and let yourself smile, even unprompted.",
        "Hum on this walk. Let the body remember.",
        "Joy is not a feeling, it is a fountain.",
        "Whose joy do I want to carry today?",
        "Look up. Joy is often above eye level.",
      ],
      targetWalks: 20,
      tone: "gold",
      hero: { primary: "\ud83c\udf3b", secondary: "\u2728" },
    },
  ])(),

  /* Hannah is mid-month on April 2026 (Patience), with 12 walks in. The
   * other coach roster sisters have a sprinkling of walks each, mostly
   * non-anonymous, so the feed feels populated and warm. */
  gardenWalkLogs: ((): GardenWalkLog[] => {
    const day = 1000 * 60 * 60 * 24;
    const t = now();
    const dateKeyOf = (offsetDays: number) => {
      const d = new Date(t - offsetDays * day);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    };
    const monthId = "gw_2026_04";
    const make = (
      userId: string,
      offsetDays: number,
      minutes: number,
      reflection: string,
      anonymous = false,
    ): GardenWalkLog => ({
      id: `gwl_${userId}_${offsetDays}_${Math.random().toString(36).slice(2, 6)}`,
      userId,
      monthId,
      dateKey: dateKeyOf(offsetDays),
      minutes,
      reflection,
      anonymous,
      loggedAt: t - offsetDays * day,
    });
    return [
      // Hannah, 12 walks across the month so far
      make("u_hannah", 25, 18, "Walked the boys to the park, breathed slow."),
      make("u_hannah", 23, 10, ""),
      make("u_hannah", 22, 22, "Patience is love, slowed down. Sat with that."),
      make("u_hannah", 20, 15, "Stroller loop, baby dozed."),
      make("u_hannah", 18, 12, ""),
      make("u_hannah", 15, 25, "Long walk after the hard morning. Tears, then peace."),
      make("u_hannah", 13, 8, "Quick lap of the cul-de-sac."),
      make("u_hannah", 10, 20, "Watched the sun come up. Three small green things."),
      make("u_hannah", 7, 14, "Walked while I prayed for Mara."),
      make("u_hannah", 5, 10, ""),
      make("u_hannah", 3, 30, "Trail walk with Caleb. He held my hand the whole way."),
      make("u_hannah", 1, 12, "Slowed my steps to match my breath."),
      // Other sisters, lighter cadence
      make("u_rebecca", 4, 18, ""),
      make("u_rebecca", 9, 22, "Old neighborhood. Saw the dogwoods bloom."),
      make("u_joanna", 2, 14, "Walked between feedings. Five minutes was enough today."),
      make("u_joanna", 6, 8, "", true),
      make("u_mara", 1, 30, "After chemo. Slow but real."),
      make("u_mara", 8, 12, ""),
      make("u_bethany", 3, 20, "Mile loop with the dog. Patience, not pace."),
      make("u_ruth", 5, 15, ""),
      make("u_eden", 2, 25, "Whole hour outside. The Lord is not rushing."),
      make("u_eden", 11, 12, ""),
    ];
  })(),

  /* Kiley's Pick library, hand-curated. Four picks per panel key, paired
   * with a personal note from Coach Kiley and an anchor scripture. The
   * pick rotates by day-of-year so it stays stable through the day but
   * does not feel repetitive across a week. */
  kileySeasonPicks: [
    /* ---- Prenatal: TTC, pregnant, postpartum recovery --------------------- */
    {
      id: "ksp_prenatal_salmon",
      panelKey: "prenatal" as const,
      recipeId: "r_sheetpan_salmon",
      kileyNote:
        "For my prenatal sisters, salmon is one of the kindest proteins your body and your baby will know. Wild-caught is low-mercury and rich in DHA, the omega-3 your baby's brain is being knit out of right now. Pair it with sweet potato for the folate and a steady blood sugar.",
      scripture:
        "For you created my inmost being, you knit me together in my mother's womb.",
      scriptureRef: "Psalm 139:13",
      focus: "DHA for baby's brain. Folate for the blood. A real meal in twenty-five honest minutes.",
    },
    {
      id: "ksp_prenatal_lentil_salad",
      panelKey: "prenatal" as const,
      recipeId: "r_iron_lentil_salad",
      kileyNote:
        "Iron is the unsung hero of pregnancy and the postpartum healing window. Lentils plus beets, lifted with a little citrus, gives your body the iron it is hungry for without a heavy supplement on a sensitive stomach. Eat slow, sister.",
      scripture:
        "She is clothed with strength and dignity.",
      scriptureRef: "Proverbs 31:25",
      focus: "Plant iron, vitamin C to absorb it. Honest fuel for a body building life.",
    },
    {
      id: "ksp_prenatal_steel_oats",
      panelKey: "prenatal" as const,
      recipeId: "r_postpartum_steel_oats",
      kileyNote:
        "This bowl carried me through both of my postpartum windows. Steel-cut oats and a spoon of ghee is warm, grounding, and easy on a body that just gave so much. The fiber settles your gut and the warm fat tells your nervous system it is safe.",
      scripture:
        "He restores my soul.",
      scriptureRef: "Psalm 23:3",
      focus: "Warming, grounding, healing. Eat it slow with both hands.",
    },
    {
      id: "ksp_prenatal_yogurt_bowl",
      panelKey: "prenatal" as const,
      recipeId: "r_yogurt_breakfast_bowl",
      kileyNote:
        "On the mornings nothing else sounds good, this is the bowl. Honey yogurt with seeds gives you protein, calcium, and a small handful of healthy fats without asking you to cook. Five minutes, sister, and you have eaten.",
      scripture:
        "He gives strength to the weary.",
      scriptureRef: "Isaiah 40:29",
      focus: "Protein and calcium when appetite is shy. A no-cook morning win.",
    },

    /* ---- Nursing: exclusive or mixed feeding ----------------------------- */
    {
      id: "ksp_nursing_lentil_soup",
      panelKey: "nursing" as const,
      recipeId: "r_chicken_lentil_soup",
      kileyNote:
        "This soup was made for nursing mamas. Long-simmered chicken broth, lentils, and warming spices keep your milk supply steady and your body deeply nourished. Make a big batch on Sunday and you have a week of one-handed lunches.",
      scripture:
        "Like newborn babies, crave pure spiritual milk.",
      scriptureRef: "1 Peter 2:2",
      focus: "Hydration, protein, and warming spices for steady supply.",
    },
    {
      id: "ksp_nursing_oats",
      panelKey: "nursing" as const,
      recipeId: "r_protein_overnight_oats",
      kileyNote:
        "Cottage cheese and oats are a quiet champion combo for nursing. Oats are the lactation grain your grandmothers swore by, and the cottage cheese stacks the protein your body is asking for. Make it the night before so it is ready when the baby finally lets you eat.",
      scripture:
        "My cup overflows.",
      scriptureRef: "Psalm 23:5",
      focus: "Oats for supply, cottage cheese for protein. Overnight kindness.",
    },
    {
      id: "ksp_nursing_chicken_wraps",
      panelKey: "nursing" as const,
      recipeId: "r_quick_chicken_wraps",
      kileyNote:
        "Five minutes from fridge to plate. Roasted chicken, hummus, and a soft wrap means you can feed yourself between feedings without standing for thirty minutes. Fold it tight, eat it warm, drink your water.",
      scripture:
        "Give us this day our daily bread.",
      scriptureRef: "Matthew 6:11",
      focus: "Five-minute protein. Eat one-handed. Drink your water.",
    },
    {
      id: "ksp_nursing_steel_oats",
      panelKey: "nursing" as const,
      recipeId: "r_postpartum_steel_oats",
      kileyNote:
        "Steel-cut oats with ghee earns its place on the nursing list too. The slow-burning carbs steady your supply across the morning and the warm fat keeps your body in safe, fed mode. Add a sprinkle of cinnamon if your baby does well with it.",
      scripture:
        "Cast all your anxiety on him because he cares for you.",
      scriptureRef: "1 Peter 5:7",
      focus: "Steady carbs for steady supply. A grounding bowl.",
    },

    /* ---- General: rhythm of ordinary care -------------------------------- */
    {
      id: "ksp_general_taco_skillet",
      panelKey: "general" as const,
      recipeId: "r_taco_skillet",
      kileyNote:
        "A weeknight win for the whole family. One pan, lean protein, plenty of vegetables tucked in, and small enough leftovers to be a quick lunch tomorrow. Ordinary care looks like feeding the people you love something honest in twenty-five minutes.",
      scripture:
        "Whatever you do, do it all for the glory of God.",
      scriptureRef: "1 Corinthians 10:31",
      focus: "Family-sized, protein-forward, low fuss. The faithful weeknight.",
    },
    {
      id: "ksp_general_sweet_potato",
      panelKey: "general" as const,
      recipeId: "r_sweet_potato_kale",
      kileyNote:
        "My go-to for the days I want to feel cared for without making a project of it. Sweet potato, kale, a little tahini, and you have fiber, beta-carotene, and plant iron in one warm bowl. Easy to double for a friend who is tired this week.",
      scripture:
        "Taste and see that the Lord is good.",
      scriptureRef: "Psalm 34:8",
      focus: "Fiber and color. A bowl that tastes like rest.",
    },
    {
      id: "ksp_general_oats",
      panelKey: "general" as const,
      recipeId: "r_protein_overnight_oats",
      kileyNote:
        "Even outside a tender season, this is one of the kindest breakfasts I know. Cottage cheese stacks the protein, oats steady the morning, and you do all the work the night before so morning-you gets to be a sister, not a chef.",
      scripture:
        "His mercies are new every morning.",
      scriptureRef: "Lamentations 3:23",
      focus: "Make-ahead protein. Mornings get gentler.",
    },
    {
      id: "ksp_general_yogurt_bowl",
      panelKey: "general" as const,
      recipeId: "r_yogurt_breakfast_bowl",
      kileyNote:
        "A simple yogurt bowl is not lazy, sister. It is wisdom. Honey, seeds, and a good plain yogurt is enough on the days when enough is what the Lord is asking of you.",
      scripture:
        "Be still and know that I am God.",
      scriptureRef: "Psalm 46:10",
      focus: "Quiet, simple, enough.",
    },
  ] as KileySeasonPick[],
};

function tomorrowAt(hour: number, minute = 0): number {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(hour, minute, 0, 0);
  return d.getTime();
}
function daysFromNow(days: number, hour: number, minute = 0): number {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, minute, 0, 0);
  return d.getTime();
}

/* ======================= Reactive subscription core ======================= */

let db: DB = JSON.parse(JSON.stringify(SEED_DB));
const listeners = new Set<() => void>();

/**
 * Auth override: when a real user signs in, AuthBoot calls
 * setCurrentUserOverride with their domain id (and optional name) so
 * queries.currentUser returns the real identity instead of the seeded
 * u_hannah demo shell. Demo fields (lifestage, cycleDay, mentor links,
 * etc.) still come from the seed user — only id/name are swapped.
 */
let currentUserOverride: {
  id: string;
  name?: string | null;
  tier?: string | null;
  role?: string | null;
} | null = null;
export function setCurrentUserOverride(
  next: { id: string; name?: string | null; tier?: string | null; role?: string | null } | null,
): void {
  currentUserOverride = next;
  emit();
}
export function getCurrentUserOverride() {
  return currentUserOverride;
}
function deriveInitials(name: string | null | undefined): string | null {
  if (!name) return null;
  const parts = name.split(/\s+/).filter(Boolean);
  const out = ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
  return out || null;
}

let storeVersion = 0;
function emit() {
  storeVersion += 1;
  listeners.forEach((l) => l());
}

/** Subscribe to a store version counter for `useSyncExternalStore`. */
function getStoreVersion() {
  return storeVersion;
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) if (!Object.is(a[i], b[i])) return false;
    return true;
  }
  if (a && b && typeof a === "object" && typeof b === "object") {
    const ak = Object.keys(a as object);
    const bk = Object.keys(b as object);
    if (ak.length !== bk.length) return false;
    for (const k of ak) {
      if (!Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])) return false;
    }
    return true;
  }
  return false;
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* =================== Snapshot helpers (server persistence) =================== */

/** Returns the current full DB. Used by the snapshot sync to push to the
 *  server. The returned object is the live reference; callers must not mutate
 *  it. */
export function getDbSnapshot(): DB {
  return db;
}

/** Replace the entire DB with a server-provided snapshot. Used on app boot
 *  when the user's account has a saved snapshot. */
export function replaceDb(next: DB): void {
  db = next;
  emit();
}

/** Subscribe to any store change. Returns an unsubscribe fn. */
export function subscribeToStore(fn: () => void): () => void {
  return subscribe(fn);
}

/* ============================ Macros math ================================ */

/** Activity multipliers for TDEE. Conservative versions of the Harris
 *  Benedict / Mifflin coefficients, tuned a touch lower for wellness use. */
const ACTIVITY_MULT: Record<ActivityLevel, number> = {
  sedentary: 1.30,
  light: 1.45,
  moderate: 1.55,
  active: 1.70,
  very_active: 1.85,
};

/** Mifflin-St Jeor BMR for women (the population MNF serves). */
export function mifflinStJeorWomen(weightLb: number, heightIn: number, ageYears: number): number {
  const kg = weightLb * 0.453592;
  const cm = heightIn * 2.54;
  return 10 * kg + 6.25 * cm - 5 * ageYears - 161;
}

export function tdee(profile: MacroProfile): number {
  const bmr = mifflinStJeorWomen(profile.weightLb, profile.heightIn, profile.ageYears);
  return Math.round(bmr * ACTIVITY_MULT[profile.activity]);
}

/** Returns the daily kcal target for the sister's goal direction and pace.
 *  Pace gates how aggressive the deficit or surplus is. "gentle" is the MNF
 *  default, grace over hustle. */
export function targetKcal(profile: MacroProfile): number {
  const base = tdee(profile);
  const adjustment: Record<GoalPace, number> = { gentle: 250, steady: 400, strong: 550 };
  if (profile.direction === "maintain") return base;
  const delta = adjustment[profile.pace];
  return profile.direction === "release" ? base - delta : base + delta;
}

/** Smart default ratios. We anchor protein to bodyweight (g/lb), then split
 *  the remaining kcal between carbs and fat with a slight carb bias for the
 *  women's wellness profile. Returns absolute grams + percentages. */
export function autoMacros(profile: MacroProfile): {
  kcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  ratios: { p: number; c: number; f: number };
} {
  const kcal = targetKcal(profile);
  // Protein floor: 0.8 g/lb for release/build, 0.7 for maintain.
  const perLb = profile.direction === "maintain" ? 0.7 : 0.8;
  const proteinG = Math.round(profile.weightLb * perLb);
  const proteinKcal = proteinG * 4;
  const remaining = Math.max(0, kcal - proteinKcal);
  // Split remaining 60/40 carbs/fat.
  const carbsKcal = Math.round(remaining * 0.6);
  const fatKcal = remaining - carbsKcal;
  const carbsG = Math.round(carbsKcal / 4);
  const fatG = Math.round(fatKcal / 9);
  const total = proteinKcal + carbsKcal + fatKcal;
  const p = total > 0 ? Math.round((proteinKcal / total) * 100) : 30;
  const c = total > 0 ? Math.round((carbsKcal / total) * 100) : 40;
  const f = 100 - p - c;
  return { kcal, proteinG, carbsG, fatG, ratios: { p, c, f } };
}

/** Apply a percentage tuple to an energy target and return absolute grams. */
export function ratiosToMacros(kcal: number, ratios: { p: number; c: number; f: number }): {
  proteinG: number;
  carbsG: number;
  fatG: number;
} {
  return {
    proteinG: Math.round((kcal * (ratios.p / 100)) / 4),
    carbsG: Math.round((kcal * (ratios.c / 100)) / 4),
    fatG: Math.round((kcal * (ratios.f / 100)) / 9),
  };
}

/** Compute the kcal that result from fixed gram targets. */
export function fixedKcal(p: number, c: number, f: number): number {
  return p * 4 + c * 4 + f * 9;
}

/* ----------------------- GGS Pre/Postnatal V2 math ----------------------- */

export interface SeasonAdjustment {
  /** Extra kcal/day to add to the base target. */
  kcalDelta: number;
  /** Protein floor in grams per kilogram of bodyweight. */
  proteinPerKg: number;
  /** Short, plain-language label shown next to the targets. */
  label: string;
  /** Honest one-line note explaining the adjustment. */
  rationale: string;
  /** Scripture pairing for this season. */
  scripture: { text: string; ref: string };
}

/** Returns kcal + protein adjustments for the sister's current season,
 *  aligned with the GGS Pre/Postnatal V2 framework. "none" returns a no-op. */
export function seasonAdjustment(season: SeasonState | null): SeasonAdjustment | null {
  if (!season || season.kind === "none") return null;
  if (season.kind === "ttc") {
    return {
      kcalDelta: 0,
      proteinPerKg: 1.4,
      label: "Trying to conceive",
      rationale: "No kcal change. Protein lifted to 1.4 g/kg to support hormone health and ovulation.",
      scripture: { text: "The Lord remembered her.", ref: "1 Samuel 1:19" },
    };
  }
  if (season.kind === "pregnant") {
    if (season.trimester === 1) {
      return {
        kcalDelta: 0,
        proteinPerKg: 1.6,
        label: "Pregnant, T1",
        rationale: "No kcal bump in T1. Protein floor lifts to 1.6 g/kg for the baby's tissue and your comfort.",
        scripture: { text: "You knit me together in my mother's womb.", ref: "Psalm 139:13" },
      };
    }
    if (season.trimester === 2) {
      return {
        kcalDelta: 340,
        proteinPerKg: 1.7,
        label: "Pregnant, T2",
        rationale: "+340 kcal/day for steady second-trimester growth. Protein 1.7 g/kg.",
        scripture: { text: "Blessed are you among women, and blessed is the child you will bear.", ref: "Luke 1:42" },
      };
    }
    if (season.trimester === 3) {
      return {
        kcalDelta: 450,
        proteinPerKg: 1.9,
        label: "Pregnant, T3",
        rationale: "+450 kcal/day as baby gains. Protein 1.9 g/kg supports the final stretch.",
        scripture: { text: "For surely I know the plans I have for you, says the Lord.", ref: "Jeremiah 29:11" },
      };
    }
    return null;
  }
  if (season.kind === "postpartum") {
    return {
      kcalDelta: 100,
      proteinPerKg: 1.6,
      label: "Postpartum recovery",
      rationale: "+100 kcal/day for tissue repair. Protein 1.6 g/kg. Move only when Kiley clears you.",
      scripture: { text: "He gathers the lambs in his arms and gently leads those that have young.", ref: "Isaiah 40:11" },
    };
  }
  if (season.kind === "nursing") {
    const delta = season.feeding === "exclusive" ? 475 : 275;
    const protein = season.feeding === "exclusive" ? 2.0 : 1.8;
    return {
      kcalDelta: delta,
      proteinPerKg: protein,
      label: season.feeding === "exclusive" ? "Nursing, exclusive" : "Nursing, mixed",
      rationale: season.feeding === "exclusive"
        ? "+475 kcal/day for exclusive nursing. Protein 2.0 g/kg."
        : "+275 kcal/day for mixed feeding. Protein 1.8 g/kg.",
      scripture: { text: "You will nurse and be satisfied at her comforting breasts.", ref: "Isaiah 66:11" },
    };
  }
  return null;
}

/** Auto-macros, season aware. Layers the GGS V2 adjustments on top of the
 *  base autoMacros math so the targets honor the season the sister is in. */
export function autoMacrosWithSeason(
  profile: MacroProfile,
  season: SeasonState | null,
): {
  kcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  ratios: { p: number; c: number; f: number };
  adjustment: SeasonAdjustment | null;
} {
  const adj = seasonAdjustment(season);
  const base = autoMacros(profile);
  if (!adj) return { ...base, adjustment: null };
  const kcal = base.kcal + adj.kcalDelta;
  const kg = profile.weightLb * 0.453592;
  // Protein: take the higher of base autoMacros protein and the GGS floor.
  const proteinG = Math.max(base.proteinG, Math.round(kg * adj.proteinPerKg));
  const proteinKcal = proteinG * 4;
  const remaining = Math.max(0, kcal - proteinKcal);
  // Pregnancy/nursing: carb-leaning split (65/35) for steady energy and milk supply.
  const carbShare = season?.kind === "pregnant" || season?.kind === "nursing" ? 0.65 : 0.6;
  const carbsKcal = Math.round(remaining * carbShare);
  const fatKcal = remaining - carbsKcal;
  const carbsG = Math.round(carbsKcal / 4);
  const fatG = Math.round(fatKcal / 9);
  const total = proteinKcal + carbsKcal + fatKcal;
  const p = total > 0 ? Math.round((proteinKcal / total) * 100) : 25;
  const c = total > 0 ? Math.round((carbsKcal / total) * 100) : 50;
  const f = 100 - p - c;
  return { kcal, proteinG, carbsG, fatG, ratios: { p, c, f }, adjustment: adj };
}

/* ----------------- Life-Season Micronutrient Panel data ----------------- */

/**
 * Three curated panels. Targets reflect commonly accepted daily intake
 * recommendations for adult women in the U.S. (NIH/ODS) but are written
 * as ranges and gentle prompts, not prescriptions. Coach Kiley's plan
 * always overrides; a sister's provider always overrides Kiley.
 */
export const MICRONUTRIENT_PANELS: Record<MicronutrientPanelKey, MicronutrientPanel> = {
  prenatal: {
    key: "prenatal",
    title: "Prenatal nutrient panel",
    subtitle: "For sisters trying to conceive, pregnant, or recovering postpartum.",
    scripture: "You knit me together in my mother's womb.",
    scriptureRef: "Psalm 139:13",
    items: [
      {
        name: "Folate",
        targetLabel: "600 mcg",
        why: "Builds the baby's neural tube in the earliest weeks, often before a sister even knows she is expecting. Lift it now, lift it gently.",
        sources: ["Lentils", "Spinach", "Asparagus", "Avocado", "Fortified oats"],
        tone: "fiber",
        pairing: "Often paired with B12 in a prenatal vitamin.",
      },
      {
        name: "Iron",
        targetLabel: "27 mg",
        why: "Blood volume rises by nearly half in pregnancy. Iron carries oxygen for two and softens the postpartum tired.",
        sources: ["Beef", "Lentils", "Pumpkin seeds", "Dark turkey meat", "Spinach"],
        tone: "coral",
        pairing: "Pair plant iron with vitamin C (citrus, peppers) for absorption.",
      },
      {
        name: "Choline",
        targetLabel: "450 mg",
        why: "Critical for the baby's brain and memory architecture. Most prenatals under-deliver, so food matters.",
        sources: ["Whole eggs", "Beef", "Salmon", "Chicken thighs", "Soybeans"],
        tone: "gold",
      },
      {
        name: "DHA omega-3",
        targetLabel: "200 to 300 mg",
        why: "Builds the baby's brain and eyes in the second and third trimesters and supports postpartum mood.",
        sources: ["Salmon", "Sardines", "Algae oil", "Chia seeds", "Walnuts"],
        tone: "forest",
        pairing: "Aim for 2 servings of low-mercury fish weekly.",
      },
      {
        name: "Iodine",
        targetLabel: "220 mcg",
        why: "Drives thyroid hormone production for both you and the baby. Easy to under-eat on a low-salt diet.",
        sources: ["Yogurt", "Cod", "Iodized salt", "Eggs", "Seaweed"],
        tone: "purple",
      },
      {
        name: "Vitamin D",
        targetLabel: "600 IU (15 mcg)",
        why: "Builds bone for both of you and supports the immune handoff. Many sisters run low, especially in winter.",
        sources: ["Salmon", "Egg yolks", "Fortified milk", "Mushrooms (sun-grown)", "Sunlight"],
        tone: "gold",
      },
    ],
    footer: "This panel is a map, not a prescription. Coach Kiley and your provider write the route.",
  },

  nursing: {
    key: "nursing",
    title: "Nursing nutrient panel",
    subtitle: "For sisters making milk, with a body that is still pouring out.",
    scripture: "As a mother comforts her child, so I will comfort you.",
    scriptureRef: "Isaiah 66:13",
    items: [
      {
        name: "Protein",
        targetLabel: "71+ g",
        why: "Milk is roughly 1 g of protein per 100 mL. Your body will make milk first and you second, so eat enough for both.",
        sources: ["Greek yogurt", "Eggs", "Chicken", "Cottage cheese", "Beans"],
        tone: "forest",
      },
      {
        name: "Choline",
        targetLabel: "550 mg",
        why: "Demand goes up while nursing. Supports the baby's brain and your own postpartum focus.",
        sources: ["Whole eggs", "Beef", "Salmon", "Chicken liver", "Brussels sprouts"],
        tone: "gold",
      },
      {
        name: "Iodine",
        targetLabel: "290 mcg",
        why: "Highest demand of any season. Iodine passes into milk and shapes the baby's thyroid for years.",
        sources: ["Cod", "Yogurt", "Iodized salt", "Milk", "Eggs"],
        tone: "purple",
      },
      {
        name: "DHA omega-3",
        targetLabel: "200 to 300 mg",
        why: "Baby's brain keeps building rapidly through the first year. Mom's stores are the source.",
        sources: ["Salmon", "Sardines", "Algae oil", "Walnuts", "Chia seeds"],
        tone: "forest",
      },
      {
        name: "Calcium",
        targetLabel: "1,000 mg",
        why: "Milk is calcium-rich. If you do not eat enough, your bones will give it. Replenish daily.",
        sources: ["Yogurt", "Cottage cheese", "Sardines with bones", "Kale", "Fortified almond milk"],
        tone: "fiber",
      },
      {
        name: "Hydration",
        targetLabel: "~16 cups water",
        why: "Milk is mostly water. Thirst is a late signal, so drink to a steady rhythm rather than to need.",
        sources: ["Water", "Herbal tea", "Bone broth", "Coconut water", "Watery fruits"],
        tone: "coral",
      },
    ],
    footer: "Eat for two bodies, sister. Your tired is data, not failure.",
  },

  general: {
    key: "general",
    title: "Daily nutrient rhythm",
    subtitle: "For ordinary days. Steady inputs, gentle attention.",
    scripture: "Give us this day our daily bread.",
    scriptureRef: "Matthew 6:11",
    items: [
      {
        name: "Protein",
        targetLabel: "0.7 to 1 g per lb",
        why: "Holds muscle, steadies hunger, and lets the rest of the plate take care of itself.",
        sources: ["Chicken", "Greek yogurt", "Eggs", "Cottage cheese", "Beans"],
        tone: "forest",
      },
      {
        name: "Iron",
        targetLabel: "18 mg",
        why: "Cycling sisters lose iron monthly. Low iron looks like fatigue and feels like fog.",
        sources: ["Beef", "Lentils", "Pumpkin seeds", "Dark turkey meat", "Spinach"],
        tone: "coral",
        pairing: "Pair with vitamin C (citrus, peppers) for absorption.",
      },
      {
        name: "Calcium",
        targetLabel: "1,000 mg",
        why: "Builds the bone you will rely on for decades. Best deposited daily, not in bursts.",
        sources: ["Yogurt", "Cottage cheese", "Sardines", "Kale", "Fortified almond milk"],
        tone: "fiber",
      },
      {
        name: "Magnesium",
        targetLabel: "320 mg",
        why: "Supports sleep, muscle recovery, and cycle steadiness. Many sisters run low.",
        sources: ["Pumpkin seeds", "Dark chocolate", "Almonds", "Black beans", "Spinach"],
        tone: "purple",
      },
      {
        name: "Vitamin D",
        targetLabel: "600 IU (15 mcg)",
        why: "Bone, mood, immune. Hard to get from food alone in colder months.",
        sources: ["Salmon", "Egg yolks", "Fortified milk", "Mushrooms (sun-grown)", "Sunlight"],
        tone: "gold",
      },
      {
        name: "Fiber",
        targetLabel: "25 g",
        why: "Feeds the gut, evens out blood sugar, and keeps things moving without striving.",
        sources: ["Berries", "Oats", "Black beans", "Chia seeds", "Avocado"],
        tone: "fiber",
      },
    ],
    footer: "You are nourishing a temple. Slow, repeated faithfulness wins.",
  },
};

/** Maps a sister's season to the right nutrient panel. TTC, pregnant,
 *  and postpartum all use the prenatal panel; nursing has its own;
 *  everyone else gets the general panel. */
export function micronutrientPanelKeyFor(
  season: SeasonState | null,
): MicronutrientPanelKey {
  if (!season) return "general";
  switch (season.kind) {
    case "nursing":
      return "nursing";
    case "ttc":
    case "pregnant":
    case "postpartum":
      return "prenatal";
    default:
      return "general";
  }
}

/* ----------------------- Pregnancy/Nursing Safety Data -------------------- */

/**
 * Curated, gently-worded advisories per safety flag. Severity is scoped to
 * which season is affected (a hard "avoid" for pregnancy may downgrade to
 * "caution" for nursing, etc.). Explanations stay plain and short. Coach
 * Kiley's plan and a sister's provider always override.
 */
export const SAFETY_ADVISORIES: Record<SafetyFlag, SafetyAdvisory> = {
  "high-mercury": {
    flag: "high-mercury",
    label: "High mercury",
    severity: "avoid",
    seasonsAffected: ["ttc", "pregnant", "nursing"],
    explanation: "Some large fish carry mercury that crosses to the baby and lingers in milk. Set these aside this season.",
    alternative: "Wild salmon, sardines, or canned light tuna stay on the table.",
  },
  "raw-fish": {
    flag: "raw-fish",
    label: "Raw fish",
    severity: "avoid",
    seasonsAffected: ["pregnant"],
    explanation: "Sushi, sashimi, and ceviche can carry parasites and bacteria your immune system handles less easily right now.",
    alternative: "Cooked-roll sushi, smoked salmon heated through, or seared tuna are gentle stand-ins.",
  },
  "raw-eggs": {
    flag: "raw-eggs",
    label: "Raw eggs",
    severity: "avoid",
    seasonsAffected: ["pregnant"],
    explanation: "Runny yolks, homemade mayo, and uncooked sauces can carry salmonella. Cook eggs until both white and yolk are firm.",
    alternative: "Pasteurized eggs in a carton work in dressings and over-easy stand-ins.",
  },
  "deli-meat": {
    flag: "deli-meat",
    label: "Deli meat",
    severity: "caution",
    seasonsAffected: ["pregnant"],
    explanation: "Cold cuts can carry listeria, which is rare but rough on a baby. Heat them until steaming before eating.",
    alternative: "Warm the slices in a pan or build the sandwich on rotisserie chicken instead.",
  },
  "soft-cheese": {
    flag: "soft-cheese",
    label: "Unpasteurized cheese",
    severity: "caution",
    seasonsAffected: ["pregnant"],
    explanation: "Soft cheeses made from unpasteurized milk (some brie, queso fresco, blue cheese) can carry listeria. Pasteurized versions are safe.",
    alternative: "Check the label for pasteurized, or swap in feta, mozzarella, or cottage cheese.",
  },
  "alcohol": {
    flag: "alcohol",
    label: "Alcohol",
    severity: "avoid",
    seasonsAffected: ["ttc", "pregnant", "nursing"],
    explanation: "Even cooked-in wine and beer keep some alcohol, which passes to the baby. The safest pour is none in this season.",
    alternative: "Swap wine for broth and a splash of grape juice or vinegar to round the flavor.",
  },
  "high-caffeine": {
    flag: "high-caffeine",
    label: "High caffeine",
    severity: "caution",
    seasonsAffected: ["pregnant", "nursing"],
    explanation: "Most providers cap caffeine at about 200 mg a day in pregnancy. Some passes into milk and can stir a fussy baby.",
    alternative: "Half-caf, decaf, or herbal blends like rooibos and ginger keep the ritual without the load.",
  },
  "liver": {
    flag: "liver",
    label: "Organ meat",
    severity: "caution",
    seasonsAffected: ["pregnant"],
    explanation: "Liver is rich in vitamin A, and too much vitamin A early in pregnancy is hard on the baby. A small portion now and then is the line.",
    alternative: "Beef, lamb, or dark poultry give iron without the vitamin A spike.",
  },
};

/**
 * Returns the subset of advisories that apply to a sister in her current season.
 * Filters by `seasonsAffected` so a nursing sister only sees flags relevant to
 * nursing (mercury and alcohol), not the pregnancy-only ones (raw eggs, deli).
 * If she has no active season, returns an empty array.
 */
export function recipeSafetyForSeason(
  recipe: Pick<Recipe, "safetyFlags">,
  season: SeasonState | null,
): SafetyAdvisory[] {
  if (!season || season.kind === "none") return [];
  if (!recipe.safetyFlags || recipe.safetyFlags.length === 0) return [];
  return recipe.safetyFlags
    .map((f) => SAFETY_ADVISORIES[f])
    .filter((a) => a.seasonsAffected.includes(season.kind));
}

/**
 * Highest severity in a list of advisories. Used to pick the badge tone
 * (avoid is forest red leaning; caution is gentler gold).
 */
export function topSafetySeverity(
  advisories: SafetyAdvisory[],
): SafetySeverity | null {
  if (advisories.length === 0) return null;
  return advisories.some((a) => a.severity === "avoid") ? "avoid" : "caution";
}

/* ----------------------- Spiritual Discipline Streaks --------------------- */

/**
 * A sister's spiritual disciplines tracked as parallel streaks. Each
 * discipline counts a different daily practice that nourishes the temple.
 * The badge tier she earns reflects the longest current streak across all
 * disciplines, never the sum, and never an average. One faithful practice
 * is enough to bear fruit.
 */
export type DisciplineKind = "meal" | "verse" | "prayer" | "sabbath";

/**
 * Tier earned for a given streak count. Tiers are absolute (not relative
 * to other sisters) and are: Planted at 7 days, Rooted at 21, Bearing Fruit
 * at 90. "none" means under 7 days, which is still a beginning, not a failure.
 */
export type BadgeTier = "none" | "planted" | "rooted" | "bearing-fruit";

export interface BadgeTierMeta {
  key: BadgeTier;
  label: string;
  threshold: number;
  scripture: string;
  scriptureRef: string;
  description: string;
  /** Color token name used as accent. */
  tone: "forest" | "gold" | "coral" | "fiber";
  emoji: string;
}

export interface DisciplineMeta {
  kind: DisciplineKind;
  label: string;
  shortLabel: string;
  practice: string;
  emoji: string;
}

export interface DisciplineState {
  kind: DisciplineKind;
  meta: DisciplineMeta;
  currentStreak: number;
  tier: BadgeTier;
  tierMeta: BadgeTierMeta;
  /** Days remaining to the next tier, or null if already at the top. */
  daysToNextTier: number | null;
  /** Next tier label (e.g. "Rooted"), or null if at top. */
  nextTierLabel: string | null;
  /** True if she practiced this discipline today (used for the "warm" dot). */
  practicedToday: boolean;
}

/**
 * Tier definitions. Order matters here, smallest threshold first. The
 * "none" tier sits at threshold 0 and is the gentle starting state, not
 * a punishment for new sisters.
 */
export const BADGE_TIERS: BadgeTierMeta[] = [
  {
    key: "none",
    label: "Beginning",
    threshold: 0,
    scripture: "For the Lord is the one who goes ahead of you.",
    scriptureRef: "Deuteronomy 31:8",
    description: "Every faithful practice begins here. Show up tomorrow.",
    tone: "forest",
    emoji: "\ud83c\udf31",
  },
  {
    key: "planted",
    label: "Planted",
    threshold: 7,
    scripture: "She is like a tree planted by streams of water.",
    scriptureRef: "Psalm 1:3",
    description: "Seven days of returning. The seed is in the soil.",
    tone: "fiber",
    emoji: "\ud83c\udf3f",
  },
  {
    key: "rooted",
    label: "Rooted",
    threshold: 21,
    scripture: "Rooted and built up in him, strengthened in the faith.",
    scriptureRef: "Colossians 2:7",
    description: "Twenty-one days. The roots are reaching down.",
    tone: "forest",
    emoji: "\ud83c\udf33",
  },
  {
    key: "bearing-fruit",
    label: "Bearing Fruit",
    threshold: 90,
    scripture: "By their fruit you will recognize them.",
    scriptureRef: "Matthew 7:16",
    description: "Ninety days of faithfulness. The harvest is real.",
    tone: "gold",
    emoji: "\ud83c\udf42",
  },
];

export const DISCIPLINE_META: Record<DisciplineKind, DisciplineMeta> = {
  meal: {
    kind: "meal",
    label: "Nourishing the temple",
    shortLabel: "Meal logging",
    practice: "Log at least one meal a day in the Garden",
    emoji: "\ud83c\udf57",
  },
  verse: {
    kind: "verse",
    label: "Sitting with the Word",
    shortLabel: "Verse check-in",
    practice: "Open the verse of the week and sit with it",
    emoji: "\ud83d\udcd6",
  },
  prayer: {
    kind: "prayer",
    label: "Lifting up sisters",
    shortLabel: "Prayer wall",
    practice: "Pray over a sister or share your own seed",
    emoji: "\ud83d\ude4f",
  },
  sabbath: {
    kind: "sabbath",
    label: "Honoring the Sabbath",
    shortLabel: "Sabbath rest",
    practice: "Take your designated rest day each week",
    emoji: "\ud83d\udd4a\ufe0f",
  },
};

/** Maps a streak count to a tier. */
export function tierForStreak(streak: number): BadgeTierMeta {
  if (streak >= 90) return BADGE_TIERS[3];
  if (streak >= 21) return BADGE_TIERS[2];
  if (streak >= 7) return BADGE_TIERS[1];
  return BADGE_TIERS[0];
}

/** The next tier above the given streak, or null if already at the top. */
export function nextTierAfter(streak: number): BadgeTierMeta | null {
  if (streak >= 90) return null;
  if (streak >= 21) return BADGE_TIERS[3];
  if (streak >= 7) return BADGE_TIERS[2];
  return BADGE_TIERS[1];
}

/* =================== Autoflow 7-day Welcome Sequence ====================== */

/**
 * The seven welcome days. These are static content authored by Coach
 * Kiley and never written to the DB. Per-user state lives in
 * `db.welcomeProgress`.
 */
export const WELCOME_STEPS: WelcomeStep[] = [
  {
    id: "day-1",
    day: 1,
    eyebrow: "Plant the seed",
    title: "Welcome home, sister",
    scripture:
      "You will be like a well-watered garden, like a spring whose waters never fail.",
    scriptureRef: "Isaiah 58:11",
    body:
      "You are not late. You are not behind. The garden does not rush, and neither will we.\n\nFor your first quiet step, choose your Sabbath day. One day a week the streaks rest with you, and so do you.",
    tone: "forest",
    hero: { primary: "🌱", secondary: "✨" },
    primaryAction: {
      key: "day-1.set-sabbath",
      kind: "navigate",
      label: "Set my Sabbath",
      href: "/flourish",
      completedLabel: "Sabbath chosen",
      helper: "You can change it any time, beloved.",
    },
    secondaryAction: {
      key: "day-1.complete",
      kind: "complete-only",
      label: "Mark as planted",
      completedLabel: "Seed in the soil",
    },
    completedReflection: "The seed is in the soil. Rest tonight.",
  },
  {
    id: "day-2",
    day: 2,
    eyebrow: "Your why",
    title: "What is the Lord stirring in you?",
    scripture:
      "For we are his workmanship, created in Christ Jesus for good works.",
    scriptureRef: "Ephesians 2:10",
    body:
      "Before any meal log, before any movement, name the why. Energy for your children, peace in your body, a heart that hears Him clearly. Whatever it is, write one line in your Garden.",
    tone: "fiber",
    hero: { primary: "🌿", secondary: "📖" },
    primaryAction: {
      key: "day-2.write-why",
      kind: "navigate",
      label: "Open my Garden",
      href: "/garden",
      completedLabel: "Why written",
      helper: "One sentence is enough.",
    },
    secondaryAction: {
      key: "day-2.complete",
      kind: "complete-only",
      label: "Mark as planted",
      completedLabel: "Why planted",
    },
    completedReflection: "He sees the why before the work.",
  },
  {
    id: "day-3",
    day: 3,
    eyebrow: "Nourish basics",
    title: "Your first gentle log",
    scripture:
      "So whether you eat or drink, do it all for the glory of God.",
    scriptureRef: "1 Corinthians 10:31",
    body:
      "Logging is not weighing your worth, sister. It is paying attention. Today, log one meal. Just one. The kcal will land softly and so will a verse for you.",
    tone: "coral",
    hero: { primary: "🥗", secondary: "💫" },
    primaryAction: {
      key: "day-3.log-meal",
      kind: "navigate",
      label: "Log one meal",
      href: "/macros",
      completedLabel: "First meal logged",
      helper: "Coach Kiley will see this and cheer.",
    },
    secondaryAction: {
      key: "day-3.complete",
      kind: "complete-only",
      label: "Mark as planted",
      completedLabel: "Today’s meal noticed",
    },
    completedReflection: "Eaten with attention. Eaten with grace.",
  },
  {
    id: "day-4",
    day: 4,
    eyebrow: "Move with grace",
    title: "Five minutes is faithful",
    scripture:
      "She girds herself with strength and makes her arms strong.",
    scriptureRef: "Proverbs 31:17",
    body:
      "Today is not about a workout, it is about showing up. Browse the Move library and tap one short session. If today is your Sabbath, rest. Tomorrow waits patiently.",
    tone: "forest",
    hero: { primary: "🏃‍♀️", secondary: "🌿" },
    primaryAction: {
      key: "day-4.open-move",
      kind: "navigate",
      label: "Open Move",
      href: "/move",
      completedLabel: "Move browsed",
      helper: "Five minutes counts.",
    },
    secondaryAction: {
      key: "day-4.complete",
      kind: "complete-only",
      label: "Mark as planted",
      completedLabel: "Body honored",
    },
    completedReflection: "Strong arms, soft heart.",
  },
  {
    id: "day-5",
    day: 5,
    eyebrow: "Scripture as soil",
    title: "Sit with this week’s verse",
    scripture:
      "Come to me, all you who are weary and burdened, and I will give you rest.",
    scriptureRef: "Matthew 11:28",
    body:
      "Every week, the whole sisterhood sits with the same verse. Read it, breathe it, and if a sister is on your heart, lift her on the Prayer Wall.",
    tone: "purple",
    hero: { primary: "📖", secondary: "🙏" },
    primaryAction: {
      key: "day-5.open-prayer",
      kind: "navigate",
      label: "Open Prayer Wall",
      href: "/sisters/prayer-wall",
      completedLabel: "Wall visited",
      helper: "You can lift quietly without posting.",
    },
    secondaryAction: {
      key: "day-5.complete",
      kind: "complete-only",
      label: "Mark as planted",
      completedLabel: "Verse held",
    },
    completedReflection: "His word, settling like dew.",
  },
  {
    id: "day-6",
    day: 6,
    eyebrow: "Sister beside you",
    title: "You were not made to walk alone",
    scripture:
      "Two are better than one, because they have a good return for their labor.",
    scriptureRef: "Ecclesiastes 4:9",
    body:
      "Today, peek at the Nourish Together circles. The launch circle keeps half-off pricing for life for founding members, and there is no leaderboard to fear, just sisters walking the same threshold.",
    tone: "gold",
    hero: { primary: "🤝", secondary: "🌾" },
    primaryAction: {
      key: "day-6.open-together",
      kind: "navigate",
      label: "Visit the circles",
      href: "/together",
      completedLabel: "Circles visited",
      helper: "Joining is optional. Looking is welcome.",
    },
    secondaryAction: {
      key: "day-6.complete",
      kind: "complete-only",
      label: "Mark as planted",
      completedLabel: "Sisterhood seen",
    },
    completedReflection: "A sister beside, not above.",
  },
  {
    id: "day-7",
    day: 7,
    eyebrow: "Bearing fruit",
    title: "One week in the garden",
    scripture:
      "By their fruit you will recognize them.",
    scriptureRef: "Matthew 7:16",
    body:
      "Look back, beloved. You set a Sabbath, named your why, logged a meal, honored your body, sat with His word, and saw your sisters. That is fruit. That is faithfulness.\n\nKeep planting. The Garden is yours now.",
    tone: "gold",
    hero: { primary: "🌾", secondary: "✨" },
    primaryAction: {
      key: "day-7.open-flourish",
      kind: "navigate",
      label: "See my Flourish",
      href: "/flourish",
      completedLabel: "Flourish visited",
      helper: "This is where your discipline badges live.",
    },
    secondaryAction: {
      key: "day-7.complete",
      kind: "complete-only",
      label: "Bearing fruit",
      completedLabel: "Welcome complete",
    },
    completedReflection: "You are bearing fruit, sister. We see you.",
  },
];

export function welcomeStepByDay(day: number): WelcomeStep | null {
  return WELCOME_STEPS.find((s) => s.day === day) ?? null;
}

/* =============================== Queries ================================== */

/**
 * Convex-shaped query namespaces. Each function returns a snapshot of the
 * current DB. Pair with `useQuery(queries.x, args)` to make it reactive.
 */
export const queries = {
  /** Every legacy in-memory meal log for a sister, all dates. Exists solely so
   *  the one-time Convex migration can carry her history across; nothing in the
   *  UI should read this. */
  allLegacyMealLogsForUser: (userId: string): MealLog[] =>
    db.mealLogs.filter((m) => m.userId === userId),

  user: (id: string) => db.users.find((u) => u.id === id),
  /**
   * The active sister. In dev (no Convex auth) this is the seeded
   * u_hannah demo user. In prod, AuthBoot calls setCurrentUserOverride
   * with the real domain id + name once Convex auth resolves, so saves,
   * comments, and ownership comparisons key to the actual user. Demo
   * fields (lifestage, cycleDay, mentor links) still come from seed.
   */
  currentUser: (): User | undefined => {
    const base = db.users[0];
    if (!base) return base;
    if (currentUserOverride) {
      // Real user: keep only identity fields and reset demo-leak fields
      // (streak/lifeStage/cycleDay/goals/zipCode/city) so a fresh signup
      // doesn't inherit Hannah's seed personality. Onboarding fills these
      // in via mutations keyed to this id.
      //
      // These previously used `undefined as unknown as T` casts, which is
      // `any`-laundering: the type claimed a value was present while the
      // runtime value was undefined. The fields are now genuinely optional
      // on User, so the absence is visible to every reader.
      return {
        ...base,
        id: currentUserOverride.id,
        // NEVER inherit identity or entitlement from the seed account.
        // `?? base.name` greeted every real user whose Convex record had no
        // name as "Hannah Doyle", with her initials — a new sister appeared to
        // be inside someone else's account. `?? base.tier` was worse than
        // cosmetic: the seed user is "Rooted", a PAID tier (convex/stripe.ts
        // maps STRIPE_PRICE_ROOTED -> "Rooted"), so every signed-in user
        // silently inherited paid entitlements. Missing values now degrade to
        // the free/neutral defaults instead of to Hannah's.
        name: currentUserOverride.name ?? "Sister",
        initials: deriveInitials(currentUserOverride.name ?? "Sister") ?? "S",
        tier: (currentUserOverride.tier as Tier | null) ?? "Seedling",
        role: (currentUserOverride.role as UserRole | null) ?? "member",
        streak: 0,
        goals: [],
        lifeStage: undefined,
        cycleDay: undefined,
        zipCode: null,
        city: null,
      };
    }
    return base;
  },

  /** All sets a sister has logged, newest-first. */
  workoutSetsForUser: (userId: string) =>
    db.workoutSets
      .filter((s) => s.userId === userId)
      .sort((a, b) => b.loggedAt - a.loggedAt),

  /** Sets logged today for a given workout, grouped by exerciseId. */
  setsLoggedToday: (userId: string, workoutId: string) => {
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    return db.workoutSets.filter(
      (s) => s.userId === userId && s.workoutId === workoutId && s.loggedAt >= startOfDay.getTime(),
    );
  },

  /** Most recent prior set for a given exercise + setIndex, used to pre-fill
   *  the input and show the delta. */
  lastSetFor: (userId: string, exerciseId: string, setIndex: number) => {
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    const prior = db.workoutSets
      .filter((s) => s.userId === userId && s.exerciseId === exerciseId && s.setIndex === setIndex && s.loggedAt < startOfDay.getTime())
      .sort((a, b) => b.loggedAt - a.loggedAt);
    return prior[0] ?? null;
  },

  /** Saved (favorited) workouts for a sister, newest-first. */
  savedWorkoutsForUser: (userId: string) =>
    db.savedWorkouts
      .filter((s) => s.userId === userId)
      .sort((a, b) => b.savedAt - a.savedAt),

  /** Whether a sister has saved a given workout. */
  isWorkoutSaved: (userId: string, workoutId: string) =>
    db.savedWorkouts.some((s) => s.userId === userId && s.workoutId === workoutId),

  /** Aggregate the past N days into a strength card. Default 30. */
  strengthCard: (userId: string, days = 30) => {
    const dayMs = 1000 * 60 * 60 * 24;
    const cutoff = now() - dayMs * days;
    const window = db.workoutSets.filter(
      (s) => s.userId === userId && s.loggedAt >= cutoff,
    );
    if (window.length === 0) {
      return null;
    }
    const sessionDays = new Set<string>();
    let totalLb = 0;
    let totalReps = 0;
    let totalHoldSec = 0;
    const prsBySet = window.filter((s) => s.isPR);
    // Top-three working lifts by max weight × reps
    const lifts: Record<string, { name: string; weeks: Map<number, number> }> = {};
    const weekOf = (ts: number) => Math.floor((cutoff + dayMs * days - ts) / (dayMs * 7));
    for (const s of window) {
      const dKey = new Date(s.loggedAt).toISOString().slice(0, 10);
      sessionDays.add(dKey);
      if (s.kind === "weighted" && s.weightLb && s.reps) {
        totalLb += s.weightLb * s.reps;
        totalReps += s.reps;
        if (!lifts[s.exerciseId]) lifts[s.exerciseId] = { name: s.exerciseName, weeks: new Map() };
        const w = weekOf(s.loggedAt);
        const prev = lifts[s.exerciseId].weeks.get(w) ?? 0;
        if (s.weightLb > prev) lifts[s.exerciseId].weeks.set(w, s.weightLb);
      } else if (s.kind === "bodyweight" && s.reps) {
        totalReps += s.reps;
      } else if (s.kind === "hold" && s.holdSec) {
        totalHoldSec += s.holdSec;
      }
    }
    const liftSummaries = Object.entries(lifts).map(([id, l]) => {
      const sorted = Array.from(l.weeks.entries()).sort((a, b) => a[0] - b[0]);
      const series = sorted.map(([, v]) => v);
      const start = series[0] ?? 0;
      const end = series[series.length - 1] ?? 0;
      return { id, name: l.name, series, startLb: start, endLb: end, deltaLb: end - start };
    });
    liftSummaries.sort((a, b) => b.endLb - a.endLb);
    const topLifts = liftSummaries.slice(0, 3);
    // PR exercises (one per exercise, take heaviest)
    const prByExercise = new Map<string, { name: string; weightLb: number; reps: number }>();
    for (const s of prsBySet) {
      if (s.weightLb && s.reps) {
        const cur = prByExercise.get(s.exerciseId);
        if (!cur || s.weightLb > cur.weightLb) {
          prByExercise.set(s.exerciseId, { name: s.exerciseName, weightLb: s.weightLb, reps: s.reps });
        }
      }
    }
    const prs = Array.from(prByExercise.values());
    return {
      monthLabel: new Date(now()).toLocaleDateString("en-US", { month: "long" }),
      totalLb: Math.round(totalLb),
      sessions: sessionDays.size,
      totalReps,
      totalHoldSec,
      prs,
      topLifts,
      isLowVolume: sessionDays.size < 4,
    };
  },
  foodLogsForToday: (userId: string) =>
    db.foodLogs.filter((f) => f.userId === userId),
  prayers: () => db.prayers.slice().sort((a, b) => b.createdAt - a.createdAt),

  /** The full prayer wall feed, optionally filtered by theme. Each row carries
   *  a denormalized prayer-count and a viewer-side flag for whether the
   *  current sister has lifted this prayer. */
  prayerWallFeed: (
    viewerId: string,
    theme?: PrayerTheme,
  ): Array<
    Prayer & {
      lifted: boolean;
      liftCount: number;
      replyCount: number;
      authorName: string;
      authorInitials: string;
      isOwn: boolean;
    }
  > => {
    const lifts = db.prayerLifts;
    const replies = db.prayerReplies;
    const rows = db.prayers
      .filter((p) => (theme ? p.theme === theme : true))
      .slice()
      .sort((a, b) => b.createdAt - a.createdAt);
    return rows.map((p) => {
      const liftCount = lifts.filter((l) => l.prayerId === p.id).length;
      const replyCount = replies.filter((r) => r.prayerId === p.id).length;
      const lifted = lifts.some((l) => l.prayerId === p.id && l.userId === viewerId);
      // Resolve author display: anonymous prayers always render "A sister".
      const profile = p.anonymous ? null : queries.sisterProfile(p.userId);
      const authorName = p.anonymous ? "A sister" : (profile?.name ?? "A sister");
      const authorInitials = p.anonymous ? "✨" : (profile?.initials ?? "S");
      return {
        ...p,
        // waters is the seeded floor; show whichever is higher of seeded count
        // or actual lifts so the seed UI feels alive on first paint.
        waters: Math.max(p.waters, liftCount),
        liftCount: Math.max(p.waters, liftCount),
        replyCount,
        lifted,
        authorName,
        authorInitials,
        isOwn: p.userId === viewerId,
      };
    });
  },

  /** A small slice of the prayer wall used for the Today screen pulse. */
  prayerWallPulse: (viewerId: string, limit = 2) =>
    queries.prayerWallFeed(viewerId).slice(0, limit),

  /** Replies for a given prayer, oldest first. */
  prayerRepliesFor: (prayerId: string): Array<
    PrayerReply & { authorName: string; authorInitials: string }
  > =>
    db.prayerReplies
      .filter((r) => r.prayerId === prayerId)
      .sort((a, b) => a.createdAt - b.createdAt)
      .map((r) => {
        const profile = queries.sisterProfile(r.userId);
        return {
          ...r,
          authorName: profile?.name ?? "A sister",
          authorInitials: profile?.initials ?? "S",
        };
      }),

  /* ========================== Scripture-at-log =========================== */

  /**
   * Pick a contextual MealVerse for a meal-log moment.
   *
   * Scoring favors verses whose context tags match the moment, then rotates
   * away from any verse the sister has seen in the last 30 days. "general"
   * is the floor so we always return something.
   */
  verseForMealContext: (input: {
    userId: string;
    block: MealBlock;
    proteinG: number;
    kcal: number;
    isFirstMealOfDay: boolean;
  }): MealVerse | null => {
    const tags: VerseContext[] = [];
    if (input.isFirstMealOfDay) tags.push("first-meal");
    if (input.block === "breakfast") tags.push("morning");
    else if (input.block === "lunch") tags.push("midday");
    else if (input.block === "dinner") tags.push("evening");
    else if (input.block === "snack") tags.push("snack");
    if (input.proteinG >= 25) tags.push("protein-rich");
    if (input.proteinG < 12 || input.kcal < 250) tags.push("gentle");
    tags.push("general");

    const thirtyDays = 1000 * 60 * 60 * 24 * 30;
    const recentIds = new Set(
      db.verseHistory
        .filter((v) => v.userId === input.userId && now() - v.shownAt < thirtyDays)
        .map((v) => v.verseId),
    );

    const scored = db.mealVerses.map((v) => {
      // Score by how many of the moment tags overlap (excluding "general").
      const overlap = v.contexts.filter((c) => tags.includes(c) && c !== "general").length;
      // Tiny bonus for general-tagged verses so the floor stays populated.
      const generalBonus = v.contexts.includes("general") ? 0.1 : 0;
      // Strong penalty if recently shown so we rotate.
      const seenPenalty = recentIds.has(v.id) ? -10 : 0;
      return { v, score: overlap + generalBonus + seenPenalty };
    });

    const best = scored.sort((a, b) => b.score - a.score)[0];
    if (!best || best.score <= -10) {
      // Everything was recently seen, fall back to a true random pick.
      return db.mealVerses[Math.floor(Math.random() * db.mealVerses.length)] ?? null;
    }
    // From the top-scoring band, pick at random for variety.
    const topBand = scored.filter((s) => s.score === best.score).map((s) => s.v);
    return topBand[Math.floor(Math.random() * topBand.length)] ?? null;
  },

  /** Pick a celebratory verse when the daily kcal goal is crossed. */
  verseForDailyComplete: (userId: string): MealVerse | null => {
    const thirtyDays = 1000 * 60 * 60 * 24 * 30;
    const recentIds = new Set(
      db.verseHistory
        .filter((v) => v.userId === userId && now() - v.shownAt < thirtyDays)
        .map((v) => v.verseId),
    );
    const pool = db.mealVerses.filter((v) => v.contexts.includes("daily-complete"));
    const fresh = pool.filter((v) => !recentIds.has(v.id));
    const choices = fresh.length > 0 ? fresh : pool;
    return choices[Math.floor(Math.random() * choices.length)] ?? null;
  },

  /**
   * Did this sister already cross her daily kcal goal today? Used so the
   * daily-complete blessing fires exactly once per day, not on every later log.
   */
  hasShownDailyCompleteToday: (userId: string): boolean => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    return db.verseHistory.some(
      (v) => v.userId === userId && v.reason === "daily-complete" && v.shownAt >= startOfDay.getTime(),
    );
  },

  /* ---------------------------- Sabbath Rest ---------------------------- */

  /** Sister's Sabbath settings, or null if she has not chosen a day. */
  sabbathFor: (userId: string): SabbathSettings | null => {
    return db.sabbathSettings.find((s) => s.userId === userId) ?? null;
  },

  /** True only when the sister has Sabbath enabled AND today is her chosen day. */
  isSabbathToday: (userId: string): boolean => {
    const s = db.sabbathSettings.find((x) => x.userId === userId);
    if (!s || !s.enabled) return false;
    return new Date().getDay() === s.dayOfWeek;
  },

  /** Devotional content for the sister's chosen Sabbath day. Null if not set. */
  sabbathDevotionalForToday: (userId: string): SabbathDevotional | null => {
    const s = db.sabbathSettings.find((x) => x.userId === userId);
    if (!s) return null;
    return db.sabbathDevotionals.find((d) => d.dayOfWeek === s.dayOfWeek) ?? null;
  },

  /** When is the next Sabbath? Returns null if the sister has no Sabbath set. */
  nextSabbath: (userId: string): { dayOfWeek: DayOfWeek; daysAway: number } | null => {
    const s = db.sabbathSettings.find((x) => x.userId === userId);
    if (!s || !s.enabled) return null;
    const today = new Date().getDay();
    const daysAway = (s.dayOfWeek - today + 7) % 7;
    return { dayOfWeek: s.dayOfWeek, daysAway };
  },

  /* -------------------------- Verse of the Week ------------------------- */

  /** YYYY-MM-DD of the Sunday that begins the current local week. */
  currentWeekStartIso: (): string => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - d.getDay()); // back up to Sunday
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  },

  /** The curated verse for this week. Falls back to the most recent past
   *  verse if no exact match (graceful for off-cycle test dates). */
  currentVerseOfWeek: (): VerseOfWeek | null => {
    const wk = queries.currentWeekStartIso();
    const exact = db.versesOfWeek.find((v) => v.weekStartIso === wk);
    if (exact) return exact;
    const past = db.versesOfWeek
      .filter((v) => v.weekStartIso <= wk)
      .sort((a, b) => (a.weekStartIso < b.weekStartIso ? 1 : -1));
    return past[0] ?? null;
  },

  /** This sister's check-in for the current week, if any. */
  verseCheckInForUser: (userId: string): VerseCheckIn | null => {
    const wk = queries.currentWeekStartIso();
    return (
      db.verseCheckIns.find(
        (c) => c.userId === userId && c.weekStartIso === wk,
      ) ?? null
    );
  },

  /** All check-ins for the current week, newest first. Used for community
   *  feed of "Sisters sitting with this verse" on the Sisters page. */
  verseCheckInsThisWeek: (): Array<
    VerseCheckIn & { authorName: string; authorInitials: string }
  > => {
    const wk = queries.currentWeekStartIso();
    const rows = db.verseCheckIns
      .filter((c) => c.weekStartIso === wk)
      .slice()
      .sort((a, b) => b.createdAt - a.createdAt);
    return rows.map((c) => {
      const profile = queries.sisterProfile(c.userId);
      return {
        ...c,
        authorName: profile?.name ?? "A sister",
        authorInitials: profile?.initials ?? "S",
      };
    });
  },

  /** Count of sisters who have checked in for this week. */
  verseCheckInCountThisWeek: (): number => {
    const wk = queries.currentWeekStartIso();
    return db.verseCheckIns.filter((c) => c.weekStartIso === wk).length;
  },

  /**
   * Synthesize the four spiritual discipline streaks for a sister. The meal
   * streak comes from her existing user.streak field (the canonical Garden
   * meal-log streak). The other three are derived from per-user offsets so
   * the demo shows a believable mix of tiers without us hand-tuning each
   * sister. The result is deterministic for the same userId.
   */
  disciplinesForUser: (userId: string): DisciplineState[] => {
    const u = db.users.find((x) => x.id === userId);
    if (!u) return [];

    // Cheap, deterministic offset based on the userId so each sister gets a
    // distinct but stable mix of streaks.
    let h = 0;
    for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
    const off = (n: number) => (h >>> (n * 4)) & 0xff;

    const baseMeal = u.resting ? 0 : u.streak;
    // Verse: typically a bit shorter than meal, capped to a believable range.
    const baseVerse = Math.max(0, baseMeal - (off(0) % 5));
    // Prayer: more variable. Some sisters lift up daily, others weekly.
    const basePrayer = Math.max(0, Math.round(baseMeal * 0.6) + (off(1) % 9) - 3);
    // Sabbath: counts in weeks-of-rest, multiplied by 7 for visual parity.
    // A sister with a long meal streak likely also has a long sabbath rhythm.
    const baseSabbath = Math.max(0, Math.round(baseMeal / 7) * 7 + (off(2) % 14));

    const wk = queries.currentWeekStartIso();
    const verseCheckedToday = db.verseCheckIns.some(
      (c) => c.userId === userId && c.weekStartIso === wk,
    );
    const prayedToday = db.prayers.some(
      (p) => p.userId === userId && now() - p.createdAt < 1000 * 60 * 60 * 24,
    );
    const todayIso = new Date(now()).toISOString().slice(0, 10);
    const loggedToday = db.mealLogs.some(
      (m) => m.userId === userId && m.date === todayIso,
    );
    const sabbathToday = queries.isSabbathToday(userId);

    const streaks: Record<DisciplineKind, number> = {
      meal: baseMeal,
      verse: baseVerse,
      prayer: basePrayer,
      sabbath: baseSabbath,
    };
    const practiced: Record<DisciplineKind, boolean> = {
      meal: loggedToday,
      verse: verseCheckedToday,
      prayer: prayedToday,
      sabbath: sabbathToday,
    };

    const order: DisciplineKind[] = ["meal", "verse", "prayer", "sabbath"];
    return order.map((kind) => {
      const currentStreak = streaks[kind];
      const tierMeta = tierForStreak(currentStreak);
      const next = nextTierAfter(currentStreak);
      return {
        kind,
        meta: DISCIPLINE_META[kind],
        currentStreak,
        tier: tierMeta.key,
        tierMeta,
        daysToNextTier: next ? next.threshold - currentStreak : null,
        nextTierLabel: next ? next.label : null,
        practicedToday: practiced[kind],
      };
    });
  },

  /**
   * The sister's highest tier across all four disciplines. Used as her
   * "top badge" on profile and in the Today card header. If no discipline
   * has crossed Planted yet, returns the "none" tier.
   */
  topDisciplineTier: (userId: string): BadgeTierMeta => {
    const states = queries.disciplinesForUser(userId);
    if (states.length === 0) return BADGE_TIERS[0];
    // Pick the tier with the largest threshold among the four states.
    return states.reduce<BadgeTierMeta>(
      (best, s) => (s.tierMeta.threshold > best.threshold ? s.tierMeta : best),
      BADGE_TIERS[0],
    );
  },

  /* ---------------- Nourish Together challenge queries -------------- */

  /** All challenges, newest-created first. */
  challenges: (): Challenge[] =>
    db.challenges.slice().sort((a, b) => b.createdAt - a.createdAt),

  /** Featured challenges, intended for Today and Sisters cards. */
  featuredChallenges: (): Challenge[] =>
    db.challenges
      .filter((c) => c.featured && c.endsAt > now())
      .slice()
      .sort((a, b) => {
        // Launch challenge always pinned to the top while open.
        if (a.isLaunchChallenge !== b.isLaunchChallenge) {
          return a.isLaunchChallenge ? -1 : 1;
        }
        return a.endsAt - b.endsAt;
      }),

  /** The launch challenge, if open. Null if it has ended or is missing. */
  launchChallenge: (): Challenge | null => {
    const c = db.challenges.find(
      (x) => x.isLaunchChallenge && x.endsAt > now(),
    );
    return c ?? null;
  },

  challengeBySlug: (slug: string): Challenge | null =>
    db.challenges.find((c) => c.slug === slug) ?? null,

  /** Participant record for a sister in a given challenge, or null. */
  challengeParticipant: (
    challengeId: string,
    userId: string,
  ): ChallengeParticipant | null =>
    db.challengeParticipants.find(
      (p) => p.challengeId === challengeId && p.userId === userId,
    ) ?? null,

  /** Participant count, no per-sister leaderboard exposed. */
  challengeParticipantCount: (challengeId: string): number =>
    db.challengeParticipants.filter((p) => p.challengeId === challengeId).length,

  /** Reflections for a challenge, newest first. */
  challengeReflections: (
    challengeId: string,
  ): Array<
    ChallengeReflection & { authorName: string; authorInitials: string; isCoach: boolean }
  > =>
    db.challengeReflections
      .filter((r) => r.challengeId === challengeId)
      .slice()
      .sort((a, b) => b.createdAt - a.createdAt)
      .map((r) => {
        const profile = queries.sisterProfile(r.userId);
        const u = db.users.find((x) => x.id === r.userId);
        return {
          ...r,
          authorName: profile?.name ?? "A sister",
          authorInitials: profile?.initials ?? "S",
          isCoach: u?.id === "u_kiley",
        };
      }),

  /** Compute progress for a sister in a challenge. */
  challengeProgress: (
    challengeId: string,
    userId: string,
  ): {
    challenge: Challenge | null;
    participant: ChallengeParticipant | null;
    daysLogged: number;
    targetDays: number;
    windowDays: number;
    daysElapsed: number;
    daysRemaining: number;
    percent: number;
    completed: boolean;
    canJoin: boolean;
    isOpen: boolean;
  } => {
    const challenge = db.challenges.find((c) => c.id === challengeId) ?? null;
    if (!challenge) {
      return {
        challenge: null,
        participant: null,
        daysLogged: 0,
        targetDays: 0,
        windowDays: 0,
        daysElapsed: 0,
        daysRemaining: 0,
        percent: 0,
        completed: false,
        canJoin: false,
        isOpen: false,
      };
    }
    const t = now();
    const participant = queries.challengeParticipant(challenge.id, userId);
    const dayMs = 1000 * 60 * 60 * 24;
    const totalWindow = Math.max(
      1,
      Math.round((challenge.endsAt - challenge.startsAt) / dayMs),
    );
    const elapsed = Math.max(
      0,
      Math.min(
        totalWindow,
        Math.floor((t - challenge.startsAt) / dayMs),
      ),
    );
    const remaining = Math.max(0, totalWindow - elapsed);
    const daysLogged = participant?.daysCounted.length ?? 0;
    const target = challenge.threshold.targetDays;
    const percent = Math.max(0, Math.min(1, daysLogged / target));
    const completed =
      !!participant?.completedAt || daysLogged >= target;
    const isOpen = t < challenge.endsAt;
    const canJoin = isOpen && !participant;
    return {
      challenge,
      participant,
      daysLogged,
      targetDays: target,
      windowDays: totalWindow,
      daysElapsed: elapsed,
      daysRemaining: remaining,
      percent,
      completed,
      canJoin,
      isOpen,
    };
  },

  /** Has the sister already logged a day toward this challenge today? */
  challengeLoggedToday: (challengeId: string, userId: string): boolean => {
    const today = new Date(now()).toISOString().slice(0, 10);
    const p = queries.challengeParticipant(challengeId, userId);
    return !!p && p.daysCounted.includes(today);
  },

  /** Number of founding members for the launch challenge. */
  foundingMemberCount: (): number => {
    const launch = queries.launchChallenge();
    if (!launch) return 0;
    return db.challengeParticipants.filter(
      (p) => p.challengeId === launch.id && p.isFoundingMember,
    ).length;
  },

  /** Is this sister a founding member (joined the open launch challenge)? */
  isFoundingMember: (userId: string): boolean => {
    const launch = queries.launchChallenge();
    if (!launch) return false;
    const p = queries.challengeParticipant(launch.id, userId);
    return !!p && p.isFoundingMember;
  },

  /* ---------------- Autoflow welcome sequence queries -------------- */

  /** Static seven-day welcome content. */
  welcomeSteps: (): WelcomeStep[] => WELCOME_STEPS,

  /** Per-user progress record (or null if she has not been seeded yet). */
  welcomeProgress: (userId: string): WelcomeProgress | null =>
    db.welcomeProgress.find((w) => w.userId === userId) ?? null,

  /**
   * Compute the active day for the autoflow.
   *
   * If a demo override is set on her progress, return that day.
   * Otherwise clamp `daysSince(user.createdAt) + 1` into [1, 7].
   * Sabbath days do not count down: if today is her Sabbath, the active
   * day stays the same as yesterday (gentle pause, not skip).
   */
  welcomeCurrentDay: (userId: string): number => {
    const progress = queries.welcomeProgress(userId);
    if (progress?.demoOverrideDay != null) {
      return Math.min(7, Math.max(1, progress.demoOverrideDay));
    }
    const u = db.users.find((x) => x.id === userId);
    if (!u) return 1;
    const dayMs = 1000 * 60 * 60 * 24;
    const daysSince = Math.floor((now() - u.createdAt) / dayMs);
    let active = daysSince + 1;
    // Sabbath gentle clamp: if today is her Sabbath, hold yesterday’s day.
    const sab = db.sabbathSettings.find((s) => s.userId === userId);
    if (sab?.enabled) {
      const todayDow = new Date(now()).getDay();
      if (todayDow === sab.dayOfWeek && active > 1) active -= 1;
    }
    return Math.min(7, Math.max(1, active));
  },

  /** The active step content for a sister. */
  welcomeCurrentStep: (userId: string): WelcomeStep | null => {
    const day = queries.welcomeCurrentDay(userId);
    return welcomeStepByDay(day);
  },

  /** Has she completed the seven-day journey? */
  isWelcomeComplete: (userId: string): boolean => {
    const p = queries.welcomeProgress(userId);
    if (!p) return false;
    return p.completedDays.includes(7);
  },

  /** Has she dismissed the autoflow card from Today? */
  isWelcomeDismissed: (userId: string): boolean => {
    const p = queries.welcomeProgress(userId);
    return !!p && p.dismissedAt != null;
  },

  /**
   * Should the AutoflowCard be visible on Today right now?
   * Visible when:
   *   - the sister has not dismissed AND
   *   - the journey is not yet complete
   * Sisters seeded without a progress record see the card on Day 1.
   */
  shouldShowAutoflowOnToday: (userId: string): boolean => {
    if (queries.isWelcomeComplete(userId)) return false;
    if (queries.isWelcomeDismissed(userId)) return false;
    return true;
  },

  /**
   * Number of days she has marked complete out of 7. Drives the timeline
   * meter on /welcome-journey.
   */
  welcomeCompletedCount: (userId: string): number => {
    const p = queries.welcomeProgress(userId);
    return p?.completedDays.length ?? 0;
  },

  /** Has a specific action key been completed? */
  isWelcomeActionDone: (userId: string, actionKey: string): boolean => {
    const p = queries.welcomeProgress(userId);
    return !!p && p.completedActions.includes(actionKey);
  },

  /** Has a specific day been completed? */
  isWelcomeDayDone: (userId: string, day: number): boolean => {
    const p = queries.welcomeProgress(userId);
    return !!p && p.completedDays.includes(day);
  },

  menteesForCoach: (coachId: string) =>
    db.mentees.filter((m) => m.coachId === coachId),

  /* === Coach Compliance Dashboard ====================================
   * Aggregates each mentee's recent activity into a single record so
   * Kiley can see at a glance who is thriving, who is steady, and who
   * needs love this week. Adherence is computed against the sister's
   * own MacroPlan kcal target across the last 7 days. We never compare
   * sisters to each other, only to their own targets, in keeping with
   * the grace-paced, no-leaderboard ethos of MNF. */

  /** Aggregate one mentee's last 7 days into a compliance snapshot. */
  menteeAdherence: (userId: string): {
    daysLogged: number;          // 0..7
    avgKcalPctOfTarget: number;  // average % across logged days, 0..125
    onTargetDays: number;        // days where kcal landed inside +/- 15% of target
    lastMealLogAt: number | null;
    prayerLiftCount7d: number;
    prayerPostCount7d: number;
    welcomeDay: number;          // 1..7, 0 if not started
    welcomeComplete: boolean;
    streak: number;
    resting: boolean;
    restingDays: number;
    season: SeasonState | null;
    plan: MacroPlan | null;
    derivedStatus: "thriving" | "steady" | "needs love";
  } => {
    const dayMs = 1000 * 60 * 60 * 24;
    const cutoff = now() - dayMs * 7;
    const recent = queries.recentMacroDays(userId, 7);
    const plan = queries.macroPlan(userId);
    const target = plan?.kcal ?? 2000;
    const loggedDays = recent.filter((d) => d.kcal > 0);
    const daysLogged = loggedDays.length;
    const avgKcalPctOfTarget = daysLogged === 0
      ? 0
      : Math.round(
          loggedDays.reduce((sum, d) => sum + (d.kcal / target) * 100, 0) / daysLogged,
        );
    const onTargetDays = loggedDays.filter((d) => {
      const pct = (d.kcal / target) * 100;
      return pct >= 85 && pct <= 115;
    }).length;
    const lastLog = db.mealLogs
      .filter((m) => m.userId === userId)
      .sort((a, b) => b.loggedAt - a.loggedAt)[0];
    const prayerLiftCount7d = db.prayerLifts.filter(
      (l) => l.userId === userId && l.liftedAt >= cutoff,
    ).length;
    const prayerPostCount7d = db.prayers.filter(
      (p) => p.userId === userId && p.createdAt >= cutoff,
    ).length;
    const welcomeDay = queries.welcomeCurrentDay(userId);
    const welcomeComplete = queries.isWelcomeComplete(userId);
    /* The mentee row carries the canonical streak, resting, and tier for
     * the coach view (it mirrors what Hannah and friends would write to
     * their own user row in production). Fall back to the user row if a
     * mentee record does not exist for this id. */
    const mentee = db.mentees.find((m) => m.id === userId);
    const u = queries.user(userId);
    const streak = mentee?.streak ?? u?.streak ?? 0;
    const resting = mentee?.resting ?? u?.resting ?? false;
    const restingDays = mentee?.restingDays ?? u?.restingDays ?? 0;
    const season = queries.seasonState(userId);

    /* Status is derived from grace-paced signals, not a competitive score.
     * "Needs love" if she has logged less than 3 of 7 days OR has not
     * touched a meal log in 48 hours and is not resting; "thriving" if she
     * has logged 6+ days AND has at least one prayer engagement; otherwise
     * "steady". Resting sisters are surfaced separately from "needs love"
     * so Kiley can see who is honoring Sabbath vs who is silently hurting. */
    const recentlyActive =
      lastLog ? now() - lastLog.loggedAt < dayMs * 2 : false;
    let derivedStatus: "thriving" | "steady" | "needs love" = "steady";
    if (!resting && (daysLogged < 3 || !recentlyActive)) {
      derivedStatus = "needs love";
    } else if (daysLogged >= 6 && prayerLiftCount7d + prayerPostCount7d >= 1) {
      derivedStatus = "thriving";
    }

    return {
      daysLogged,
      avgKcalPctOfTarget,
      onTargetDays,
      lastMealLogAt: lastLog?.loggedAt ?? null,
      prayerLiftCount7d,
      prayerPostCount7d,
      welcomeDay,
      welcomeComplete,
      streak,
      resting,
      restingDays,
      season,
      plan,
      derivedStatus,
    };
  },

  /** Roster-wide aggregates Kiley sees at the top of the dashboard. */
  coachComplianceSummary: (coachId: string) => {
    const mentees = queries.menteesForCoach(coachId);
    const rows = mentees.map((m) => ({
      mentee: m,
      adh: queries.menteeAdherence(m.id),
    }));
    const thriving = rows.filter((r) => r.adh.derivedStatus === "thriving").length;
    const steady = rows.filter((r) => r.adh.derivedStatus === "steady").length;
    const needsLove = rows.filter((r) => r.adh.derivedStatus === "needs love").length;
    const resting = rows.filter((r) => r.adh.resting).length;
    const totalDaysLogged = rows.reduce((s, r) => s + r.adh.daysLogged, 0);
    const possibleDays = rows.length * 7;
    const rosterLogRate = possibleDays === 0
      ? 0
      : Math.round((totalDaysLogged / possibleDays) * 100);
    const totalPrayerEngagement = rows.reduce(
      (s, r) => s + r.adh.prayerLiftCount7d + r.adh.prayerPostCount7d,
      0,
    );
    const avgAdherence = rows.length === 0
      ? 0
      : Math.round(
          rows.reduce((s, r) => s + r.adh.avgKcalPctOfTarget, 0) / rows.length,
        );
    return {
      total: rows.length,
      thriving,
      steady,
      needsLove,
      resting,
      rosterLogRate,
      avgAdherence,
      totalPrayerEngagement,
    };
  },

  /* === Share with My Provider PDF export ============================
   * Aggregates a sister's macros, season, last 14 days, and the
   * micronutrient panel her season points to into a single dataset
   * tuned for a printable, clinical-friendly summary. The export is
   * always wellness guidance; clinicians are the authority. */
  providerExportData: (userId: string) => {
    const user = queries.user(userId);
    if (!user) return null;
    const plan = queries.macroPlan(userId);
    const profile = queries.macroProfile(userId);
    const season = queries.seasonState(userId);
    const recent14 = queries.recentMacroDays(userId, 14);

    /* Adherence is computed against the sister's own kcal target.
     * If she has no plan yet the export still renders, but adherence
     * shows as "no plan set". */
    const target = plan?.kcal ?? 0;
    const loggedDays = recent14.filter((d) => d.kcal > 0);
    const avgKcal = loggedDays.length === 0
      ? 0
      : Math.round(loggedDays.reduce((s, d) => s + d.kcal, 0) / loggedDays.length);
    const avgProtein = loggedDays.length === 0
      ? 0
      : Math.round(loggedDays.reduce((s, d) => s + d.proteinG, 0) / loggedDays.length);
    const avgCarbs = loggedDays.length === 0
      ? 0
      : Math.round(loggedDays.reduce((s, d) => s + d.carbsG, 0) / loggedDays.length);
    const avgFat = loggedDays.length === 0
      ? 0
      : Math.round(loggedDays.reduce((s, d) => s + d.fatG, 0) / loggedDays.length);
    const avgKcalPctOfTarget = target === 0 || loggedDays.length === 0
      ? 0
      : Math.round((avgKcal / target) * 100);
    const onTargetDays = target === 0
      ? 0
      : loggedDays.filter((d) => {
          const pct = (d.kcal / target) * 100;
          return pct >= 85 && pct <= 115;
        }).length;

    /* Pick the right micronutrient panel for the sister's season. The
     * panel shape is read from MICRONUTRIENT_PANELS at render time so
     * the document reflects the season at print, not at log. */
    const panelKey = micronutrientPanelKeyFor(season);

    return {
      user,
      plan,
      profile,
      season,
      panelKey,
      recent14,
      summary: {
        daysLogged: loggedDays.length,
        avgKcal,
        avgProtein,
        avgCarbs,
        avgFat,
        avgKcalPctOfTarget,
        onTargetDays,
        target,
      },
      generatedAt: now(),
    };
  },

  /** Sorted mentee compliance rows (needs love first, then steady, then thriving). */
  menteeComplianceRows: (coachId: string) => {
    const mentees = queries.menteesForCoach(coachId);
    const rows = mentees.map((m) => ({
      mentee: m,
      adh: queries.menteeAdherence(m.id),
    }));
    const order = { "needs love": 0, steady: 1, thriving: 2 } as const;
    return rows.sort((a, b) => {
      const oa = order[a.adh.derivedStatus];
      const ob = order[b.adh.derivedStatus];
      if (oa !== ob) return oa - ob;
      return a.adh.daysLogged - b.adh.daysLogged;
    });
  },

  journalForUser: (userId: string) =>
    db.journalEntries.filter((j) => j.userId === userId).sort((a, b) => b.createdAt - a.createdAt),
  chatThread: (threadId: string) =>
    db.chatMessages.filter((m) => m.threadId === threadId).sort((a, b) => a.sentAt - b.sentAt),
  seedsForInviter: (inviterId: string) =>
    db.seeds.filter((s) => s.inviterId === inviterId),
  liveNow: () =>
    db.liveClasses.filter((c) => c.status === "live"),
  liveUpcoming: () =>
    db.liveClasses
      .filter((c) => c.status === "upcoming")
      .sort((a, b) => a.scheduledFor - b.scheduledFor),
  liveReplays: () =>
    db.liveClasses
      .filter((c) => c.status === "replay")
      .sort((a, b) => b.scheduledFor - a.scheduledFor),
  liveClass: (id: string) =>
    db.liveClasses.find((c) => c.id === id),
  liveChatFor: (classId: string) =>
    db.liveChat
      .filter((m) => m.classId === classId)
      .sort((a, b) => a.sentAt - b.sentAt),
  communityForZip: (zipCode: string | null, source?: CommunitySource) => {
    let list = db.communityEvents;
    if (source) list = list.filter((e) => e.source === source);
    // In production this would be a geo radius lookup keyed off zip;
    // for the demo we return all events sorted by distance regardless of zip.
    return list.slice().sort((a, b) => a.distanceMi - b.distanceMi);
  },
  feedPosts: (kind?: FeedPostKind) => {
    let list = db.feedPosts;
    if (kind) list = list.filter((p) => p.kind === kind);
    return list.slice().sort((a, b) => b.createdAt - a.createdAt);
  },
  classReminders: (userId: string) =>
    db.classReminders.filter((r) => r.userId === userId),
  hasReminder: (userId: string, classId: string) =>
    db.classReminders.some((r) => r.userId === userId && r.classId === classId),
  nextUpcomingClass: () =>
    db.liveClasses
      .filter((c) => c.status === "upcoming")
      .sort((a, b) => a.scheduledFor - b.scheduledFor)[0],
  feedComments: (postId: string) =>
    db.feedComments
      .filter((c) => c.postId === postId)
      .sort((a, b) => a.createdAt - b.createdAt),
  isWatchingLive: (userId: string, classId: string) =>
    db.liveAttendance.some(
      (a) => a.userId === userId && a.classId === classId && a.leftAt == null
    ),
  liveAttendees: (classId: string) =>
    db.liveAttendance.filter((a) => a.classId === classId && a.leftAt == null),
  isRsvped: (userId: string, eventId: string) =>
    db.eventRsvps.some((r) => r.userId === userId && r.eventId === eventId),
  myRsvps: (userId: string) =>
    db.eventRsvps.filter((r) => r.userId === userId),
  myHostedMeetups: (userId: string) =>
    db.communityEvents
      .filter((e) => e.source === "sister-meetup" && e.hostId === userId)
      .sort((a, b) => a.startsAt - b.startsAt),
  /**
   * Inbox view for Hannah: collects every thread she's a participant in,
   * returns latest preview, time, and unread count (messages NOT from Hannah
   * that landed after her last sent message in that thread).
   * threadKey is also the route slug.
   */
  messageThreads: () => {
    type ThreadMeta = { name: string; initials: string; ringColor: string; subtitle: string; route: string; hannahIsCoach: boolean; kind: "bot" | "coach" | "sister" };
    const META: Record<string, ThreadMeta> = {
      lydia:   { name: "Sister Lydia",    initials: "SL", ringColor: "rgba(127,119,221,0.55)", subtitle: "Form & nourishment companion",   route: "/chat/lydia",    hannahIsCoach: false, kind: "bot"     },
      kiley:   { name: "Coach Kiley",     initials: "KM", ringColor: "rgba(160,120,32,0.55)", subtitle: "Your coach \u00b7 Flourishing",  route: "/kiley",         hannahIsCoach: false, kind: "coach"   },
      bethany: { name: "Bethany Cole",    initials: "BC", ringColor: "rgba(192,120,74,0.55)", subtitle: "Postpartum \u00b7 Seedling",     route: "/chat/bethany",  hannahIsCoach: true,  kind: "sister"  },
      rebecca: { name: "Rebecca Hayes",   initials: "RH", ringColor: "rgba(192,120,74,0.55)", subtitle: "Cycling \u00b7 Flourishing",     route: "/chat/rebecca",  hannahIsCoach: true,  kind: "sister"  },
      joanna:  { name: "Joanna Pierce",   initials: "JP", ringColor: "rgba(168,212,184,0.55)", subtitle: "Perimenopause \u00b7 Rooted",   route: "/chat/joanna",   hannahIsCoach: true,  kind: "sister"  },
    };
    return Object.entries(META).map(([threadId, meta]) => {
      const msgs = db.chatMessages
        .filter((m) => m.threadId === threadId)
        .sort((a, b) => a.sentAt - b.sentAt);
      const last = msgs[msgs.length - 1];
      // Hannah is the non-coach in kiley thread, but the coach/mentor in sister threads
      const lastSentByHannah = [...msgs].reverse().find((m) => m.fromCoach === meta.hannahIsCoach);
      const lastFromHannahAt = lastSentByHannah?.sentAt ?? 0;
      const unread = msgs.filter(
        (m) => m.fromCoach !== meta.hannahIsCoach && m.sentAt > lastFromHannahAt
      ).length;
      const preview = last
        ? last.type === "voice"
          ? `\ud83c\udf99 Voice note \u00b7 ${last.durationSec ?? 0}s`
          : last.type === "verse"
          ? `\u2728 ${last.verseRef ?? last.body}`
          : last.body
        : "Say hello";
      return {
        threadId,
        name: meta.name,
        initials: meta.initials,
        ringColor: meta.ringColor,
        subtitle: meta.subtitle,
        route: meta.route,
        kind: meta.kind,
        lastPreview: preview,
        lastSentAt: last?.sentAt ?? 0,
        lastFromHannah: last ? last.fromCoach === meta.hannahIsCoach : false,
        unread,
      };
    }).sort((a, b) => {
      // Lydia and Coach pinned to top regardless of recency, then by recency
      const rank = (k: string) => (k === "bot" ? 0 : k === "coach" ? 1 : 2);
      const ra = rank(a.kind), rb = rank(b.kind);
      if (ra !== rb) return ra - rb;
      return b.lastSentAt - a.lastSentAt;
    });
  },
  /**
   * Tier gate. Seedlings cannot DM Coach Kiley directly; they're routed to
   * Sister Lydia. Rooted and Flourishing have direct access (queued FIFO
   * on the coach side).
   */
  canMessageCoachDirectly: (userId: string) => {
    const u = db.users.find((u) => u.id === userId)
      ?? db.mentees.find((m) => m.id === userId);
    if (!u) return false;
    return u.tier === "Rooted" || u.tier === "Flourishing";
  },
  /**
   * Coach inbox queue. For each sister thread that Coach Kiley is part of,
   * find the latest unanswered sister message and report awaiting time.
   * Returned in FIFO order (oldest waiting first).
   */
  coachQueue: () => {
    const sisterThreads = [
      { threadId: "kiley",   userId: "u_hannah",  name: "Hannah Doyle",   initials: "HD" },
      { threadId: "mara",    userId: "u_mara",    name: "Mara Whitfield", initials: "MW" },
      { threadId: "ruth",    userId: "u_ruth",    name: "Ruth Adler",     initials: "RA" },
      { threadId: "rebecca", userId: "u_rebecca", name: "Rebecca Hayes",  initials: "RH" },
      { threadId: "joanna",  userId: "u_joanna",  name: "Joanna Pierce",  initials: "JP" },
    ];
    const out = sisterThreads.flatMap(({ threadId, userId, name, initials }) => {
      // Mentees (sisters Kiley coaches) carry the tier; the primary user
      // record only exists for u_hannah, so fall back to mentees for others.
      const u = db.users.find((u) => u.id === userId)
        ?? db.mentees.find((m) => m.id === userId);
      if (!u) return [];
      // Only Rooted/Flourishing land in Kiley's queue. Seedlings go to Lydia.
      if (u.tier === "Seedling") return [];
      const msgs = db.chatMessages
        .filter((m) => m.threadId === threadId)
        .sort((a, b) => a.sentAt - b.sentAt);
      const last = msgs[msgs.length - 1];
      if (!last) return [];
      // Coach is fromCoach=true in these threads
      if (last.fromCoach) return []; // Kiley already replied
      return [{
        threadId,
        userId,
        name,
        initials,
        tier: u.tier,
        body: last.body,
        type: last.type,
        awaitingSinceMs: now() - last.sentAt,
        receivedAt: last.sentAt,
      }];
    });
    // FIFO: oldest waiting first
    return out.sort((a, b) => a.receivedAt - b.receivedAt);
  },
  totalUnreadMessages: () => {
    return queries.messageThreads().reduce((sum, t) => sum + t.unread, 0);
  },
  coachLiveStats: (coachId: string) => {
    const all = db.liveClasses.filter((c) => c.coachId === coachId);
    return {
      live: all.filter((c) => c.status === "live").length,
      upcoming: all.filter((c) => c.status === "upcoming").length,
      replays: all.filter((c) => c.status === "replay").length,
      totalAttendees: all.reduce((sum, c) => sum + (c.attendees ?? 0), 0),
      nowWatching: all
        .filter((c) => c.status === "live")
        .reduce((sum, c) => sum + (c.watching ?? 0), 0),
    };
  },

  /* ---------------------- Accountability partnerships ------------------- */

  /** The sister's currently active partnership (if any). */
  activePairForUser: (userId: string) =>
    db.pairs.find(
      (p) => p.status === "active" && (p.inviterId === userId || p.inviteeId === userId),
    ) ?? null,

  /** All pending invites where this user is the recipient. */
  pendingInvitesForUser: (userId: string) =>
    db.pairs.filter((p) => p.status === "pending" && p.inviteeId === userId),

  /** All pending invites this user has sent. */
  pendingInvitesByUser: (userId: string) =>
    db.pairs.filter((p) => p.status === "pending" && p.inviterId === userId),

  pairById: (pairId: string) => db.pairs.find((p) => p.id === pairId) ?? null,

  /** Sister profile (from users or mentees) for showing partner side-by-side. */
  partnerProfile: (pair: AccountabilityPair, viewerId: string) => {
    const otherId = pair.inviterId === viewerId ? pair.inviteeId : pair.inviterId;
    const u = db.users.find((u) => u.id === otherId);
    if (u) {
      return {
        id: u.id,
        name: u.name,
        initials: u.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase(),
        tier: u.tier,
        streak: u.streak,
        resting: u.resting,
      };
    }
    const m = db.mentees.find((m) => m.id === otherId);
    if (m) {
      return { id: m.id, name: m.name, initials: m.initials, tier: m.tier, streak: m.streak, resting: m.resting };
    }
    return null;
  },

  checkInsForPair: (pairId: string) =>
    db.pairCheckIns
      .filter((c) => c.pairId === pairId)
      .sort((a, b) => b.date.localeCompare(a.date)),

  todayCheckIn: (pairId: string) => {
    const today = new Date(now()).toISOString().slice(0, 10);
    return db.pairCheckIns.find((c) => c.pairId === pairId && c.date === today) ?? null;
  },

  pingsForPair: (pairId: string, limit = 12) =>
    db.pairPings
      .filter((p) => p.pairId === pairId)
      .sort((a, b) => b.sentAt - a.sentAt)
      .slice(0, limit),

  /** Sisters this user could potentially pair with (excluding self,
   *  current partner, and pending invites). */
  availableSistersToPair: (userId: string) => {
    const taken = new Set<string>();
    db.pairs.forEach((p) => {
      if (p.status === "active" || p.status === "pending") {
        if (p.inviterId === userId) taken.add(p.inviteeId);
        if (p.inviteeId === userId) taken.add(p.inviterId);
      }
    });
    return db.mentees
      .filter((m) => m.id !== userId && !taken.has(m.id))
      .map((m) => ({ id: m.id, name: m.name, initials: m.initials, tier: m.tier, streak: m.streak }));
  },

  /** Pair-season progress 0..1. */
  pairProgress: (pair: AccountabilityPair) => {
    if (!pair.startedAt || !pair.endsAt) return 0;
    const total = pair.endsAt - pair.startedAt;
    const elapsed = Math.min(total, Math.max(0, now() - pair.startedAt));
    return total > 0 ? elapsed / total : 0;
  },

  /* ----------------------------- Macros engine ------------------------- */

  /** Resolve any sister-by-id from users or mentees, returning a uniform shape
   *  used by both the sister and coach views. */
  sisterById: (id: string) => {
    const u = db.users.find((u) => u.id === id);
    if (u) {
      const initials = u.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
      return { id: u.id, name: u.name, initials, tier: u.tier, streak: u.streak, resting: u.resting };
    }
    const m = db.mentees.find((m) => m.id === id);
    if (m) {
      return { id: m.id, name: m.name, initials: m.initials, tier: m.tier, streak: m.streak, resting: m.resting };
    }
    return null;
  },

  macroProfile: (userId: string): MacroProfile | null =>
    db.macroProfiles.find((p) => p.userId === userId) ?? null,

  /** Sister's current season (TTC, pregnant, postpartum, nursing, none). */
  seasonState: (userId: string): SeasonState | null =>
    db.seasonStates.find((s) => s.userId === userId) ?? null,

  macroPlan: (userId: string): MacroPlan | null =>
    db.macroPlans.find((p) => p.userId === userId) ?? null,

  /** Today's meal logs for a sister (YYYY-MM-DD local). */
  todayMealLogs: (userId: string): MealLog[] => {
    const today = new Date(now()).toISOString().slice(0, 10);
    return db.mealLogs
      .filter((m) => m.userId === userId && m.date === today)
      .sort((a, b) => a.loggedAt - b.loggedAt);
  },

  /** Logs for a specific date. */
  mealLogsForDate: (userId: string, date: string): MealLog[] =>
    db.mealLogs
      .filter((m) => m.userId === userId && m.date === date)
      .sort((a, b) => a.loggedAt - b.loggedAt),

  /** Sum today's logs into kcal + P/C/F totals. */
  todayMacroTotals: (userId: string) => {
    const today = new Date(now()).toISOString().slice(0, 10);
    const logs = db.mealLogs.filter((m) => m.userId === userId && m.date === today);
    return logs.reduce(
      (acc, m) => ({
        kcal: acc.kcal + m.kcal,
        proteinG: acc.proteinG + m.proteinG,
        carbsG: acc.carbsG + m.carbsG,
        fatG: acc.fatG + m.fatG,
      }),
      { kcal: 0, proteinG: 0, carbsG: 0, fatG: 0 },
    );
  },

  /** Last N days of totals, oldest first. Used for the weekly trend rail. */
  recentMacroDays: (userId: string, days = 7) => {
    const out: Array<{ date: string; kcal: number; proteinG: number; carbsG: number; fatG: number }> = [];
    const dayMs = 1000 * 60 * 60 * 24;
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now() - i * dayMs).toISOString().slice(0, 10);
      const logs = db.mealLogs.filter((m) => m.userId === userId && m.date === date);
      const totals = logs.reduce(
        (acc, m) => ({
          kcal: acc.kcal + m.kcal,
          proteinG: acc.proteinG + m.proteinG,
          carbsG: acc.carbsG + m.carbsG,
          fatG: acc.fatG + m.fatG,
        }),
        { kcal: 0, proteinG: 0, carbsG: 0, fatG: 0 },
      );
      out.push({ date, ...totals });
    }
    return out;
  },

  /* =============================== Recipes ================================ */

  /** Tier rule. Recompute on every loveRecipe so chips stay honest. */
  awardTierForCount: (n: number): RecipeAwardTier => {
    if (n >= 500) return "garden-favorite";
    if (n >= 100) return "flourishing";
    if (n >= 25)  return "rooted";
    if (n >= 5)   return "sprouted";
    return "none";
  },

  /** Lookup a recipe by id. */
  recipeById: (id: string): Recipe | null =>
    db.recipes.find((r) => r.id === id) ?? null,

  /** Today's Harvest hero. Kiley's pick, deterministic by date so the same
   *  hero stays on screen all day across reloads. Falls back to top-loved if
   *  none of Kiley's recipes is public. */
  featuredRecipe: (): Recipe | null => {
    const kileyPicks = db.recipes
      .filter((r) => r.authorId === "u_kiley" && r.public)
      .sort((a, b) => b.lovedCount - a.lovedCount);
    if (kileyPicks.length > 0) {
      // Rotate by day-of-year so the hero changes daily but is stable today.
      const day = Math.floor(now() / (1000 * 60 * 60 * 24));
      return kileyPicks[day % kileyPicks.length];
    }
    return db.recipes.slice().sort((a, b) => b.lovedCount - a.lovedCount)[0] ?? null;
  },

  /** Trending in the garden. Public recipes ranked by lovedCount. Optional
   *  exclude lets the hub skip whichever recipe is already shown as hero. */
  trendingRecipes: (excludeId?: string, limit = 6): Recipe[] =>
    db.recipes
      .filter((r) => r.public && (!excludeId || r.id !== excludeId))
      .sort((a, b) => b.lovedCount - a.lovedCount)
      .slice(0, limit),

  /** Recipes saved by a sister, newest save first. */
  savedRecipesForUser: (userId: string): Recipe[] => {
    const saves = db.savedRecipes
      .filter((s) => s.userId === userId)
      .sort((a, b) => b.savedAt - a.savedAt);
    return saves
      .map((s) => db.recipes.find((r) => r.id === s.recipeId))
      .filter((r): r is Recipe => Boolean(r));
  },

  /** Recipes filtered by season tag. Sorted by lovedCount. */
  recipesBySeasonTag: (tag: RecipeSeasonTag, limit = 8): Recipe[] =>
    db.recipes
      .filter((r) => r.public && r.seasonTags.includes(tag))
      .sort((a, b) => b.lovedCount - a.lovedCount)
      .slice(0, limit),

  /** All recipes by an author (for Kiley's collection or a sister's profile). */
  recipesByAuthor: (authorId: string): Recipe[] =>
    db.recipes
      .filter((r) => r.authorId === authorId)
      .sort((a, b) => b.createdAt - a.createdAt),

  /** Recipes the sister has captured from outside the app (Recime-style
   *  imports). These are her own personal cookbook entries with `source ===
   *  "imported"`. Newest first. */
  capturedRecipesForUser: (userId: string): Recipe[] =>
    db.recipes
      .filter((r) => r.authorId === userId && r.source === "imported")
      .sort((a, b) => b.createdAt - a.createdAt),

  /** Has the sister saved this recipe? */
  isRecipeSaved: (userId: string, recipeId: string): boolean =>
    db.savedRecipes.some((s) => s.userId === userId && s.recipeId === recipeId),

  /** Has the sister loved this recipe (after eating)? */
  hasLovedRecipe: (userId: string, recipeId: string): boolean =>
    db.recipeLoves.some((l) => l.userId === userId && l.recipeId === recipeId),

  /** Display name + initials for a recipe author. Kiley first, then mentees. */
  recipeAuthor: (authorId: string): { name: string; initials: string; isCoach: boolean } => {
    if (authorId === "u_kiley") return { name: "Coach Kiley", initials: "K", isCoach: true };
    const mentee = db.mentees.find((m) => m.id === authorId);
    if (mentee) return { name: mentee.name, initials: mentee.initials, isCoach: false };
    const u = db.users.find((u) => u.id === authorId);
    if (u) {
      const parts = u.name.split(" ");
      const initials = (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
      return { name: u.name, initials: initials.toUpperCase(), isCoach: false };
    }
    return { name: "A sister", initials: "S", isCoach: false };
  },

  /* ----------------------------- Pair rituals -------------------------- */

  /** Returns the Monday of the current week as a YYYY-MM-DD string. */
  weekStartYmd: (): string => {
    const d = new Date();
    const dow = d.getDay(); // Sunday = 0
    const offset = dow === 0 ? 6 : dow - 1;
    d.setDate(d.getDate() - offset);
    return d.toISOString().slice(0, 10);
  },

  /** This week's shared scripture for a pair, if one exists. */
  pairScriptureOfWeek: (pairId: string): PairSharedScripture | null => {
    const ws = queries.weekStartYmd();
    return db.pairSharedScriptures.find((s) => s.pairId === pairId && s.weekStart === ws) ?? null;
  },

  /** This week's cook-together pick for a pair, if one exists. */
  pairSharedRecipeOfWeek: (pairId: string): PairSharedRecipe | null => {
    const ws = queries.weekStartYmd();
    return db.pairSharedRecipes.find((r) => r.pairId === pairId && r.weekStart === ws) ?? null;
  },

  /** The next ritual a sister should attend to, used by the Today PartnerCard
   *  to surface a single context-aware nudge. Order of priority:
   *    1. Daily check-in not yet shared by viewer
   *    2. Shared scripture not yet reacted to by viewer
   *    3. Cook-together not yet picked OR not yet marked cooked by viewer
   *  Returns null when everything for today is handled. */
  nextRitualForViewer: (
    pairId: string,
    viewerId: string,
  ): {
    kind: "check_in" | "scripture" | "cook_pick" | "cook_mark" | "cheer";
    label: string;
  } | null => {
    const pair = db.pairs.find((p) => p.id === pairId);
    if (!pair) return null;
    const isInviter = pair.inviterId === viewerId;
    const today = queries.todayCheckIn(pairId);
    const myAnswer = today ? (isInviter ? today.inviterAnswer : today.inviteeAnswer) : null;
    if (!myAnswer) return { kind: "check_in", label: "Share today's check-in" };
    const scripture = queries.pairScriptureOfWeek(pairId);
    const myReaction = scripture ? (isInviter ? scripture.inviterReaction : scripture.inviteeReaction) : null;
    if (scripture && !myReaction) return { kind: "scripture", label: "Reflect on this week's verse" };
    const sr = queries.pairSharedRecipeOfWeek(pairId);
    if (!sr) return { kind: "cook_pick", label: "Pick a recipe to cook together" };
    const myCooked = isInviter ? sr.inviterCookedAt : sr.inviteeCookedAt;
    if (!myCooked) return { kind: "cook_mark", label: "Cook this week's shared meal" };
    return { kind: "cheer", label: "Cheer her on with a quick whisper" };
  },

  /** An open auto-match suggestion for the viewer. The seed engine creates one
   *  by season tag whenever the user is unpaired and a candidate is available. */
  matchSuggestionForUser: (userId: string): MatchSuggestion | null => {
    const open = db.matchSuggestions.find((m) => m.forUserId === userId && m.status === "open");
    if (open) return open;
    // Lazy generation: if the user is unpaired and has no open suggestion,
    // mint one from the candidate pool. We do not persist it until the user
    // accepts or skips so reads stay idempotent.
    const active = queries.activePairForUser(userId);
    const incoming = queries.pendingInvitesForUser(userId);
    if (active || incoming.length > 0) return null;
    const candidates = queries.availableSistersToPair(userId);
    if (candidates.length === 0) return null;
    // Pick the first untouched candidate, prefer those with the highest streak.
    const skippedIds = new Set(
      db.matchSuggestions.filter((m) => m.forUserId === userId && m.status === "skipped").map((m) => m.candidateId),
    );
    const fresh = candidates.filter((c) => !skippedIds.has(c.id));
    const pick = (fresh.length > 0 ? fresh : candidates).slice().sort((a, b) => b.streak - a.streak)[0];
    if (!pick) return null;
    return {
      id: `ms_pending_${userId}_${pick.id}`,
      forUserId: userId,
      candidateId: pick.id,
      reason: "You're walking through the same season, sister.",
      createdAt: now(),
      status: "open",
    };
  },

  /* ===================== Monthly Garden Walk queries ===================== */

  /** All Garden Walk months ever, ordered newest first. */
  gardenWalkMonths: (): GardenWalkMonth[] =>
    db.gardenWalkMonths
      .slice()
      .sort((a, b) => b.year * 12 + b.month - (a.year * 12 + a.month)),

  /** The active Garden Walk month for today. Falls back to the most
   *  recent month if today is outside any seeded window. */
  currentGardenWalkMonth: (): GardenWalkMonth | null => {
    const d = new Date();
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const exact = db.gardenWalkMonths.find((x) => x.year === y && x.month === m);
    if (exact) return exact;
    const sorted = db.gardenWalkMonths
      .slice()
      .sort((a, b) => b.year * 12 + b.month - (a.year * 12 + a.month));
    return sorted[0] ?? null;
  },

  /** All walks for a given month, newest-first, across every sister. */
  gardenWalkLogsForMonth: (monthId: string): GardenWalkLog[] =>
    db.gardenWalkLogs
      .filter((w) => w.monthId === monthId)
      .sort((a, b) => b.loggedAt - a.loggedAt),

  /** A single sister's walks in a month, newest-first. */
  gardenWalkLogsForUser: (userId: string, monthId: string): GardenWalkLog[] =>
    db.gardenWalkLogs
      .filter((w) => w.userId === userId && w.monthId === monthId)
      .sort((a, b) => b.loggedAt - a.loggedAt),

  /** Whether this sister has already logged a walk today (any month). */
  hasGardenWalkLoggedToday: (userId: string): boolean => {
    const d = new Date();
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return db.gardenWalkLogs.some(
      (w) => w.userId === userId && w.dateKey === key,
    );
  },

  /** Aggregate stats for a sister in a month: walks, minutes, threshold
   *  hit, days walked. Returns sane zeroes if she has no logs. */
  gardenWalkStats: (
    userId: string,
    monthId: string,
  ): {
    walks: number;
    minutes: number;
    daysWalked: number;
    target: number;
    thresholdMet: boolean;
    todayPrompt: string;
    fruit: FruitOfSpirit | null;
    tone: GardenWalkMonth["tone"] | null;
  } => {
    const month = db.gardenWalkMonths.find((x) => x.id === monthId);
    const target = month?.targetWalks ?? 20;
    const logs = db.gardenWalkLogs.filter(
      (w) => w.userId === userId && w.monthId === monthId,
    );
    const walks = logs.length;
    const minutes = logs.reduce((acc, w) => acc + w.minutes, 0);
    const daysWalked = new Set(logs.map((w) => w.dateKey)).size;
    const thresholdMet = walks >= target;
    const dayOfMonth = new Date().getDate();
    const prompts = month?.dailyPrompts ?? [];
    const todayPrompt =
      prompts.length > 0 ? prompts[(dayOfMonth - 1) % prompts.length]! : "";
    return {
      walks,
      minutes,
      daysWalked,
      target,
      thresholdMet,
      todayPrompt,
      fruit: month?.fruit ?? null,
      tone: month?.tone ?? null,
    };
  },

  /** Aggregate participation for the current month, used on the Together
   *  surface and the Garden Walk page header. */
  gardenWalkMonthSummary: (
    monthId: string,
  ): { sisters: number; walks: number; minutes: number } => {
    const logs = db.gardenWalkLogs.filter((w) => w.monthId === monthId);
    const sisters = new Set(logs.map((w) => w.userId)).size;
    const minutes = logs.reduce((acc, w) => acc + w.minutes, 0);
    return { sisters, walks: logs.length, minutes };
  },

  /** Coach Kiley's hand-picked recipe for the user's current season.
   *  Picks rotate by day-of-year so the same recommendation stays put across
   *  reloads on the same day. Curated by hand, not generated. */
  kileySeasonPickForUser: (
    userId: string,
  ): { pick: KileySeasonPick; recipe: Recipe; panelKey: MicronutrientPanelKey } | null => {
    const season = db.seasonStates.find((s) => s.userId === userId) ?? null;
    const panelKey = micronutrientPanelKeyFor(season);
    const pool = db.kileySeasonPicks.filter((p) => p.panelKey === panelKey);
    if (pool.length === 0) return null;
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const dayOfYear = Math.floor((now.getTime() - start.getTime()) / 86400000);
    const pick = pool[dayOfYear % pool.length];
    const recipe = db.recipes.find((r) => r.id === pick.recipeId);
    if (!recipe) return null;
    return { pick, recipe, panelKey };
  },

  /** Tomorrow's pick for the same user, used by the "peek tomorrow" toggle. */
  tomorrowsKileyPick: (
    userId: string,
  ): { pick: KileySeasonPick; recipe: Recipe; panelKey: MicronutrientPanelKey } | null => {
    const season = db.seasonStates.find((s) => s.userId === userId) ?? null;
    const panelKey = micronutrientPanelKeyFor(season);
    const pool = db.kileySeasonPicks.filter((p) => p.panelKey === panelKey);
    if (pool.length === 0) return null;
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const dayOfYear = Math.floor((now.getTime() - start.getTime()) / 86400000);
    const pick = pool[(dayOfYear + 1) % pool.length];
    const recipe = db.recipes.find((r) => r.id === pick.recipeId);
    if (!recipe) return null;
    return { pick, recipe, panelKey };
  },

  /** All of Kiley's picks for a given season panel, hydrated with recipes.
   *  Used to render the "other picks for your season" list. */
  kileySeasonPicksByPanel: (
    panelKey: MicronutrientPanelKey,
  ): { pick: KileySeasonPick; recipe: Recipe }[] => {
    return db.kileySeasonPicks
      .filter((p) => p.panelKey === panelKey)
      .map((pick) => {
        const recipe = db.recipes.find((r) => r.id === pick.recipeId);
        return recipe ? { pick, recipe } : null;
      })
      .filter((x): x is { pick: KileySeasonPick; recipe: Recipe } => x !== null);
  },

  /** Active subscription for a user, or null if she has none on file. */
  subscriptionForUser: (userId: string): Subscription | null => {
    return db.subscriptions.find((s) => s.userId === userId) ?? null;
  },

  /** All renewal reminder rows for a user, newest scheduled first. Includes
   *  both pending and already-sent. */
  renewalRemindersForUser: (userId: string): RenewalReminder[] => {
    return db.renewalReminders
      .filter((r) => r.userId === userId)
      .slice()
      .sort((a, b) => b.scheduledFor - a.scheduledFor);
  },

  /** Next reminder for a user that has not yet been sent and is scheduled
   *  in the future. Powers the "upcoming reminder" card on the billing screen. */
  nextPendingReminder: (userId: string): RenewalReminder | null => {
    const pending = db.renewalReminders
      .filter(
        (r) =>
          r.userId === userId &&
          r.sentAt === null &&
          r.scheduledFor >= now() - 1000 * 60 * 60,
      )
      .slice()
      .sort((a, b) => a.scheduledFor - b.scheduledFor);
    return pending[0] ?? null;
  },

  /** Coach notifications authored by Kiley (or any author), newest first. */
  coachNotificationsByAuthor: (authorId: string): CoachNotification[] => {
    return db.coachNotifications
      .filter((c) => c.authorId === authorId)
      .slice()
      .sort((a, b) => b.createdAt - a.createdAt);
  },

  /** Compute the recipient count for a given audience selection. Used by
   *  the composer to show "this will reach N sisters" before send. */
  audienceCount: (input: {
    audience: CoachNotificationAudience;
    audienceTier?: Tier;
    audienceUserId?: string;
  }): number => {
    // Union users + mentees so Coach Kiley's coachees are counted even though
    // the demo only seeds Hannah into db.users. Mentees carry id, tier,
    // streak, and resting fields that mirror what we need from User.
    type AudienceMember = { id: string; tier: Tier; streak: number; resting: boolean };
    const seen = new Set<string>();
    const all: AudienceMember[] = [];
    for (const u of db.users) {
      if (seen.has(u.id)) continue;
      seen.add(u.id);
      all.push({ id: u.id, tier: u.tier, streak: u.streak, resting: u.resting });
    }
    for (const m of db.mentees) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      all.push({ id: m.id, tier: m.tier, streak: m.streak, resting: m.resting });
    }
    if (input.audience === "all") return all.length;
    if (input.audience === "single") {
      return input.audienceUserId
        ? all.some((u) => u.id === input.audienceUserId)
          ? 1
          : 0
        : 0;
    }
    if (input.audience === "tier") {
      return all.filter((u) => u.tier === input.audienceTier).length;
    }
    if (input.audience === "founding-members") {
      // Founding members in seed data: anyone with a founding subscription
      // OR (for the demo) all of Coach Kiley's mentees, who are all founders.
      const founderIds = new Set(
        db.subscriptions
          .filter((s) => s.isFoundingMember && s.status !== "canceled")
          .map((s) => s.userId),
      );
      for (const m of db.mentees) founderIds.add(m.id);
      return all.filter((u) => founderIds.has(u.id)).length;
    }
    if (input.audience === "needs-love") {
      return all.filter((u) => u.streak === 0 || u.resting).length;
    }
    return 0;
  },

  /** Notification preferences for a user, or a sensible default with
   *  permissionStatus "unknown" if no row exists yet. The fallback object is
   *  not persisted, callers must invoke updateNotificationPrefs to write. */
  /** Lifetime stats shown on the Profile hero. We deliberately surface
   *  consistency-focused numbers (streak, lifetime movements, days in the
   *  garden, weeks rooted) instead of body-comp metrics, to keep the
   *  flourish posture. */
  profileStats: (userId: string): {
    streak: number;
    lifetimeMovements: number;
    daysInGarden: number;
    weeksRooted: number;
  } => {
    const u = db.users.find((u) => u.id === userId);
    const lifetimeMovements = db.workoutSets.filter((s) => s.userId === userId).length;
    const createdAt = u?.createdAt ?? Date.now();
    const daysInGarden = Math.max(
      1,
      Math.floor((Date.now() - createdAt) / (1000 * 60 * 60 * 24)),
    );
    const weeksRooted = Math.floor(daysInGarden / 7);
    return {
      streak: u?.streak ?? 0,
      lifetimeMovements,
      daysInGarden,
      weeksRooted,
    };
  },

  notificationPrefsForUser: (userId: string): NotificationPrefs => {
    const row = db.notificationPrefs.find((p) => p.userId === userId);
    if (row) return row;
    return {
      userId,
      permissionStatus: "unknown",
      scriptureReminders: true,
      scriptureTime: "07:00",
      prayerWallReplies: true,
      weeklyCheckIns: true,
      coachingMessages: true,
      communityAndLive: true,
      updatedAt: 0,
    };
  },

  /** Most recent legal consent row for a user, or null if she has not
   *  accepted any version yet. */
  legalConsentForUser: (userId: string): LegalConsent | null => {
    const rows = db.legalConsents.filter((c) => c.userId === userId);
    if (rows.length === 0) return null;
    return rows.reduce((latest, row) =>
      row.acceptedAt > latest.acceptedAt ? row : latest,
    );
  },

  /** True when the user has accepted the current binding bundle
   *  (matches CURRENT_CONSENT_VERSION and all required flags are true). */
  hasAcceptedCurrentConsent: (userId: string): boolean => {
    const latest = queries.legalConsentForUser(userId);
    if (!latest) return false;
    return (
      latest.version === CURRENT_CONSENT_VERSION &&
      latest.acceptedTermsOfService &&
      latest.acceptedPrivacyPolicy &&
      latest.acceptedMedicalDisclaimer &&
      latest.acceptedCommunityGuidelines &&
      latest.confirmedAdult
    );
  },

  /** Look up a sister-or-coach profile for the match suggestion card. */
  sisterProfile: (userId: string): { name: string; initials: string; tier?: string; streak?: number } | null => {
    const m = db.mentees.find((x) => x.id === userId);
    if (m) return { name: m.name, initials: m.initials, tier: m.tier, streak: m.streak };
    const u = db.users.find((x) => x.id === userId);
    if (u) {
      const parts = u.name.split(" ");
      const initials = (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
      return { name: u.name, initials: initials.toUpperCase() };
    }
    return null;
  },

  /* ============================ Sisterhood ================================ */

  /** Live posts only, reverse chronological. Tier-mate slicing is applied for
   *  the viewer so her tier sees their own posts a half-step higher in the
   *  feed; everyone else still appears, just lower. */
  sisterhoodFeed: (viewerId: string): SisterhoodPost[] => {
    const viewer = db.users.find((u) => u.id === viewerId);
    const live = db.sisterhoodPosts
      .filter((p) => p.status === "live")
      .slice()
      .sort((a, b) => b.createdAt - a.createdAt);
    if (!viewer) return live;
    const tierOf = (authorId: string): string | null => {
      const u = db.users.find((x) => x.id === authorId);
      if (u) return u.tier;
      const m = db.mentees.find((x) => x.id === authorId);
      return m?.tier ?? null;
    };
    const sameTier: SisterhoodPost[] = [];
    const otherTier: SisterhoodPost[] = [];
    live.forEach((p) => {
      if (tierOf(p.authorId) === viewer.tier) sameTier.push(p);
      else otherTier.push(p);
    });
    // Soft interleave: every same-tier post followed by one other-tier post,
    // then any remainder. Keeps the feed feeling mixed rather than segregated.
    const out: SisterhoodPost[] = [];
    const max = Math.max(sameTier.length, otherTier.length);
    for (let i = 0; i < max; i++) {
      if (sameTier[i]) out.push(sameTier[i]);
      if (otherTier[i]) out.push(otherTier[i]);
    }
    return out;
  },

  sisterhoodPostById: (id: string): SisterhoodPost | null =>
    db.sisterhoodPosts.find((p) => p.id === id) ?? null,

  /** Live comments only, oldest-first so a thread reads top to bottom. */
  sisterhoodCommentsForPost: (postId: string): SisterhoodComment[] =>
    db.sisterhoodComments
      .filter((c) => c.postId === postId && c.status === "live")
      .slice()
      .sort((a, b) => a.createdAt - b.createdAt),

  /** Counts of each reaction kind for a post. Display caps at "25+". */
  sisterhoodReactionsForPost: (postId: string): {
    amen: number; praying: number; meToo: number;
    amenLabel: string; prayingLabel: string; meTooLabel: string;
  } => {
    const reactions = db.sisterhoodReactions.filter((r) => r.postId === postId);
    const amen = reactions.filter((r) => r.kind === "amen").length;
    const praying = reactions.filter((r) => r.kind === "praying").length;
    const meToo = reactions.filter((r) => r.kind === "me-too").length;
    const cap = (n: number) => (n >= 25 ? "25+" : String(n));
    return {
      amen, praying, meToo,
      amenLabel: cap(amen), prayingLabel: cap(praying), meTooLabel: cap(meToo),
    };
  },

  /** The viewer's current reaction kind on a post, or null. */
  mySisterhoodReactionForPost: (userId: string, postId: string): SisterhoodReactionKind | null => {
    const r = db.sisterhoodReactions.find((x) => x.userId === userId && x.postId === postId);
    return r?.kind ?? null;
  },

  /** New sisters spend their first week reading. They can react and comment
   *  but cannot author posts yet. Drives the feed banner. */
  isInListeningPeriod: (userId: string): boolean => {
    const u = db.users.find((x) => x.id === userId);
    if (!u) return false;
    const day = 1000 * 60 * 60 * 24;
    return now() - u.createdAt < day * 7;
  },

  /** Days remaining in the listening period, or 0 if cleared. */
  listeningPeriodDaysLeft: (userId: string): number => {
    const u = db.users.find((x) => x.id === userId);
    if (!u) return 0;
    const day = 1000 * 60 * 60 * 24;
    const elapsed = now() - u.createdAt;
    const remainingMs = day * 7 - elapsed;
    if (remainingMs <= 0) return 0;
    return Math.ceil(remainingMs / day);
  },

  /** Posts queued for coach review. Drives the moderation badge in coach UI. */
  coachModerationQueue: (): SisterhoodPost[] =>
    db.sisterhoodPosts
      .filter((p) => p.status === "queued")
      .slice()
      .sort((a, b) => b.createdAt - a.createdAt),

  /** Helper that walks both users and mentees so any author can be looked up. */
  userById: (id: string): { id: string; name: string; initials: string; avatarDataUrl?: string | null; tier?: string; streak?: number } | null => {
    const u = db.users.find((x) => x.id === id);
    if (u) {
      const parts = u.name.split(" ");
      const initials = ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
      return { id: u.id, name: u.name, initials, avatarDataUrl: u.avatarDataUrl, tier: u.tier, streak: u.streak };
    }
    const m = db.mentees.find((x) => x.id === id);
    if (m) return { id: m.id, name: m.name, initials: m.initials, tier: m.tier, streak: m.streak };
    return null;
  },

  /* ========================= Sister Network =============================== */

  /** Days the viewer has walked with her current accountability sister. Looks
   *  at the active pair where she is inviter or invitee. Returns 0 if no
   *  active pair. */
  daysWithCurrentSister: (userId: string): number => {
    const pair = db.pairs.find(
      (p) => p.status === "active" && (p.inviterId === userId || p.inviteeId === userId),
    );
    if (!pair || !pair.startedAt) return 0;
    const day = 1000 * 60 * 60 * 24;
    return Math.floor((now() - pair.startedAt) / day);
  },

  /** Eligibility for the Sister Network. Requires 90+ consecutive days with
   *  the same accountability sister AND a non-zero streak. */
  networkEligible: (userId: string): boolean => {
    const u = db.users.find((x) => x.id === userId);
    if (!u) return false;
    if (u.streak <= 0) return false;
    const pair = db.pairs.find(
      (p) => p.status === "active" && (p.inviterId === userId || p.inviteeId === userId),
    );
    if (!pair || !pair.startedAt) return false;
    const day = 1000 * 60 * 60 * 24;
    const daysIn = Math.floor((now() - pair.startedAt) / day);
    return daysIn >= 90;
  },

  /** The viewer's own network profile, if she has opted in at any point. */
  myNetworkProfile: (userId: string): NetworkProfile | null =>
    db.networkProfiles.find((p) => p.userId === userId) ?? null,

  /** Browse list of veterans currently open to receive intros, excluding self.
   *  Optional filters by life stage and goal. */
  networkMembers: (
    viewerId: string,
    filters?: { lifeStage?: NetworkLifeStage; goal?: NetworkGoal },
  ): NetworkProfile[] => {
    return db.networkProfiles
      .filter((p) => p.isOpenIn && p.userId !== viewerId)
      .filter((p) => !filters?.lifeStage || p.lifeStage === filters.lifeStage)
      .filter((p) => !filters?.goal || p.goals.includes(filters.goal))
      .slice()
      .sort((a, b) => b.joinedNetworkAt - a.joinedNetworkAt);
  },

  /** All intros (incoming pending, sent pending, accepted) for the inbox. */
  myActiveIntros: (userId: string): {
    incomingPending: NetworkIntro[];
    sentPending: NetworkIntro[];
    accepted: NetworkIntro[];
  } => {
    const day = 1000 * 60 * 60 * 24;
    const cutoff = now() - day * 14;
    const all = db.networkIntros.filter(
      (i) => i.fromUserId === userId || i.toUserId === userId,
    );
    return {
      incomingPending: all
        .filter((i) => i.toUserId === userId && i.status === "pending")
        .sort((a, b) => b.createdAt - a.createdAt),
      sentPending: all
        .filter((i) => i.fromUserId === userId && i.status === "pending")
        .sort((a, b) => b.createdAt - a.createdAt),
      accepted: all
        .filter(
          (i) =>
            (i.status === "accepted" || i.status === "one-time") &&
            i.lastMessageAt >= cutoff,
        )
        .sort((a, b) => b.lastMessageAt - a.lastMessageAt),
    };
  },

  /** Count of active sent intros (pending + accepted within 14 days). Used
   *  to enforce the 5-active-connection cap. */
  activeSentIntroCount: (userId: string): number => {
    const day = 1000 * 60 * 60 * 24;
    const cutoff = now() - day * 14;
    return db.networkIntros.filter(
      (i) =>
        i.fromUserId === userId &&
        (i.status === "pending" ||
          ((i.status === "accepted" || i.status === "one-time") &&
            i.lastMessageAt >= cutoff)),
    ).length;
  },

  /** Messages on a single intro thread, oldest first. */
  networkMessagesForIntro: (introId: string): NetworkMessage[] =>
    db.networkMessages
      .filter((m) => m.introId === introId)
      .slice()
      .sort((a, b) => a.sentAt - b.sentAt),

  /* ===================== Movement personalization ====================== */

  /** Pull a sister's MovementProfile, or null if she hasn't onboarded yet. */
  movementProfile: (userId: string): MovementProfile | null =>
    db.movementProfiles.find((p) => p.userId === userId) ?? null,

  /** Build today's NASM/GGS-personalized session for a sister. Returns null on rest day. */
  todaysSession: (userId: string, dateIso: string): GeneratedSession | null => {
    const profile = db.movementProfiles.find((p) => p.userId === userId);
    if (!profile || !profile.completed) return null;
    const seasonRow = db.seasonStates.find((s) => s.userId === userId);
    const season: SeasonState | null = seasonRow ?? null;
    return buildSession(profile, season, dateIso);
  },

  /** Generate sessions for the 7 days starting weekStartIso (Sunday). */
  weekSessions: (userId: string, weekStartIso: string): (GeneratedSession | null)[] => {
    const profile = db.movementProfiles.find((p) => p.userId === userId);
    if (!profile || !profile.completed) return [null, null, null, null, null, null, null];
    const seasonRow = db.seasonStates.find((s) => s.userId === userId);
    const season: SeasonState | null = seasonRow ?? null;
    const start = new Date(weekStartIso);
    const out: (GeneratedSession | null)[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      out.push(buildSession(profile, season, d.toISOString()));
    }
    return out;
  },

  /** Snapshot of where a sister is in her mesocycle, for badge rendering. */
  mesocycleSummary: (userId: string): { week: 1 | 2 | 3 | 4; block: "A" | "B"; startedAt: number } | null => {
    const profile = db.movementProfiles.find((p) => p.userId === userId);
    if (!profile || !profile.completed) return null;
    const week = mesocycleWeek(profile, now());
    return { week, block: accessoryBlock(week), startedAt: profile.mesocycleStartedAt };
  },

  /** Compute a sister's three Bloom rings for today: nourish, move, grace.
   *  Each is 0-100. Move is driven by the personalized session: count of
   *  exercises she's logged sets for today vs. session.exercises.length. */
  bloomToday: (userId: string): { nourish: number; move: number; grace: number; movePersonalized: boolean } => {
    const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = dayStart.getTime() + 24 * 60 * 60 * 1000;
    const startMs = dayStart.getTime();

    /* ---- Nourish: kcal logged today vs. 1800 target ---- */
    const meals = db.foodLogs.filter(
      (f) => f.userId === userId && f.loggedAt >= startMs && f.loggedAt < dayEnd
    );
    const kcalToday = meals.reduce((sum, m) => sum + m.kcal * m.servings, 0);
    const nourish = Math.min(100, Math.round((kcalToday / 1800) * 100));

    /* ---- Move: personalized session completion if available ---- */
    const profile = db.movementProfiles.find((p) => p.userId === userId);
    const sets = db.workoutSets.filter(
      (s) => s.userId === userId && s.loggedAt >= startMs && s.loggedAt < dayEnd
    );
    let move = 0;
    let movePersonalized = false;
    if (profile && profile.completed) {
      const seasonRow = db.seasonStates.find((s) => s.userId === userId);
      const session = buildSession(profile, seasonRow ?? null, new Date(startMs).toISOString());
      if (session && session.exercises.length > 0) {
        movePersonalized = true;
        const loggedExerciseIds = new Set(sets.map((s) => s.exerciseId));
        const done = session.exercises.filter((e) => loggedExerciseIds.has(e.exerciseId)).length;
        move = Math.round((done / session.exercises.length) * 100);
      } else {
        // Rest day: any movement (mobility, walk) counts toward grace floor
        move = sets.length > 0 ? 100 : 0;
      }
    } else {
      // Pre-onboarding fallback: scale by raw set count, 5 sets = 100%
      move = Math.min(100, Math.round((sets.length / 5) * 100));
    }

    /* ---- Grace: Word read + prayer + sabbath rest ---- */
    // Heuristic: each chat message sent today (to Kiley or sisters) contributes,
    // any prayer wall entry contributes, sabbath today caps at 100 immediately.
    const sabbathSettings = db.sabbathSettings.find((s) => s.userId === userId);
    const isSabbath = sabbathSettings?.enabled && sabbathSettings.dayOfWeek === new Date().getDay();
    if (isSabbath) {
      return { nourish, move, grace: 100, movePersonalized };
    }
    // Outgoing user messages are fromCoach=false on user-owned threads
    const chatToday = db.chatMessages.filter(
      (m) => !m.fromCoach && m.sentAt >= startMs && m.sentAt < dayEnd
    ).length;
    const prayersToday = db.prayers.filter(
      (p) => p.userId === userId && p.createdAt >= startMs && p.createdAt < dayEnd
    ).length;
    const graceRaw = chatToday * 20 + prayersToday * 35;
    // Floor at 30 if she opened the app and at least logged anything
    const opened = meals.length > 0 || sets.length > 0 || chatToday > 0 || prayersToday > 0;
    const grace = Math.max(opened ? 30 : 0, Math.min(100, graceRaw));

    return { nourish, move, grace, movePersonalized };
  },
};

/* =============================== Mutations ================================ */

export const mutations = {
  /** Toggle cancel-at-period-end on a sister's subscription. Per ToS
   *  Section 6, cancellation takes effect at the end of the current paid
   *  period; she retains access until then. We never delete the sub row
   *  immediately so the audit trail and access window stay intact. */
  setCancelAtPeriodEnd: (userId: string, cancel: boolean): Subscription | null => {
    const sub = db.subscriptions.find((s) => s.userId === userId);
    if (!sub) return null;
    sub.cancelAtPeriodEnd = cancel;
    emit();
    return sub;
  },

  /** Schedule the renewal reminder appropriate to a subscription, idempotent.
   *  Annual subs get a row 7 days before currentPeriodEnd. Trials >= 32 days
   *  get a row 3 days before trialEndsAt. Returns the row that exists or was
   *  created. Per Subscription & Refund Policy Section 4.3 and California
   *  Auto-Renewal Law (§ 17600 et seq.). */
  ensureRenewalReminder: (userId: string): RenewalReminder | null => {
    const sub = db.subscriptions.find((s) => s.userId === userId);
    if (!sub) return null;
    if (sub.status === "canceled" || sub.cancelAtPeriodEnd) return null;

    let kind: RenewalReminderKind | null = null;
    let scheduledFor = 0;
    let renewalDate = 0;
    if (sub.trialEndsAt) {
      const trialDays =
        (sub.trialEndsAt - sub.currentPeriodStart) / (1000 * 60 * 60 * 24);
      if (trialDays >= 32) {
        kind = "trial-3day";
        scheduledFor = sub.trialEndsAt - 1000 * 60 * 60 * 24 * 3;
        renewalDate = sub.trialEndsAt;
      }
    } else if (sub.cycle === "annual") {
      kind = "annual-7day";
      scheduledFor = sub.currentPeriodEnd - 1000 * 60 * 60 * 24 * 7;
      renewalDate = sub.currentPeriodEnd;
    }
    if (!kind) return null;

    const existing = db.renewalReminders.find(
      (r) => r.userId === userId && r.kind === kind && r.renewalDate === renewalDate,
    );
    if (existing) return existing;

    const row: RenewalReminder = {
      id: `rr_${userId}_${renewalDate}`,
      userId,
      kind,
      scheduledFor,
      renewalDate,
      amountCents: sub.priceCents,
      channel: "both",
      sentAt: null,
      createdAt: now(),
    };
    db.renewalReminders.push(row);
    emit();
    return row;
  },

  /** Mark a reminder as sent. The actual delivery happens server-side; this
   *  records the audit trail. */
  markReminderSent: (id: string): RenewalReminder | null => {
    const row = db.renewalReminders.find((r) => r.id === id);
    if (!row) return null;
    row.sentAt = now();
    emit();
    return row;
  },

  /** Coach Kiley sends a notification. Audience is computed at send time so
   *  the recipientCount reflects who actually received it. Returns the
   *  persisted row. */
  sendCoachNotification: (input: {
    authorId: string;
    audience: CoachNotificationAudience;
    audienceTier?: Tier;
    audienceUserId?: string;
    title: string;
    body: string;
    category: CoachNotificationCategory;
    scheduledFor?: number | null;
  }): CoachNotification => {
    const recipientCount = queries.audienceCount({
      audience: input.audience,
      audienceTier: input.audienceTier,
      audienceUserId: input.audienceUserId,
    });
    const send = input.scheduledFor ?? null;
    const row: CoachNotification = {
      id: `cn_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      authorId: input.authorId,
      audience: input.audience,
      audienceTier: input.audienceTier,
      audienceUserId: input.audienceUserId,
      title: input.title.trim(),
      body: input.body.trim(),
      category: input.category,
      scheduledFor: send,
      sentAt: send ? null : now(),
      recipientCount,
      createdAt: now(),
    };
    db.coachNotifications.push(row);
    emit();
    return row;
  },

  /** Set the notification permission state and, when granting, ensure a prefs
   *  row exists so the per-category toggles persist. Mirrors the OS prompt:
   *  iOS and Android each surface a single allow/deny dialog; we record the
   *  answer here so the rest of the app can branch on it. */
  setNotificationPermission: (
    userId: string,
    status: NotificationPermissionStatus,
  ): NotificationPrefs => {
    const existing = db.notificationPrefs.find((p) => p.userId === userId);
    if (existing) {
      existing.permissionStatus = status;
      existing.updatedAt = now();
      emit();
      return existing;
    }
    const row: NotificationPrefs = {
      userId,
      permissionStatus: status,
      scriptureReminders: true,
      scriptureTime: "07:00",
      prayerWallReplies: true,
      weeklyCheckIns: true,
      coachingMessages: true,
      communityAndLive: true,
      updatedAt: now(),
    };
    db.notificationPrefs.push(row);
    emit();
    return row;
  },

  /** Patch any subset of notification preferences. Creates a row with sane
   *  defaults if one does not exist yet. */
  updateNotificationPrefs: (
    userId: string,
    patch: Partial<Omit<NotificationPrefs, "userId" | "updatedAt">>,
  ): NotificationPrefs => {
    const existing = db.notificationPrefs.find((p) => p.userId === userId);
    if (existing) {
      Object.assign(existing, patch, { updatedAt: now() });
      emit();
      return existing;
    }
    const row: NotificationPrefs = {
      userId,
      permissionStatus: "unknown",
      scriptureReminders: true,
      scriptureTime: "07:00",
      prayerWallReplies: true,
      weeklyCheckIns: true,
      coachingMessages: true,
      communityAndLive: true,
      updatedAt: now(),
      ...patch,
    };
    db.notificationPrefs.push(row);
    emit();
    return row;
  },

  /** Append a new legal consent record. We never overwrite previous rows so
   *  the audit trail is intact. */
  recordLegalConsent: (input: {
    userId: string;
    version: string;
    acceptedTermsOfService: boolean;
    acceptedPrivacyPolicy: boolean;
    acceptedMedicalDisclaimer: boolean;
    acceptedCommunityGuidelines: boolean;
    confirmedAdult: boolean;
  }): LegalConsent => {
    const row: LegalConsent = {
      id: `lc_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      acceptedAt: now(),
      ...input,
    };
    db.legalConsents.push(row);
    emit();
    return row;
  },

  /** Log one set. PR detection: if weight × reps exceeds the user's previous
   *  max for this exerciseId+setIndex, mark isPR. */
  logWorkoutSet: (input: Omit<WorkoutSet, "id" | "loggedAt" | "isPR">) => {
    const prior = db.workoutSets.filter(
      (s) => s.userId === input.userId && s.exerciseId === input.exerciseId,
    );
    let isPR = false;
    if (input.kind === "weighted" && input.weightLb && input.reps) {
      const score = input.weightLb * input.reps;
      const priorMax = prior.reduce(
        (m, s) => (s.weightLb && s.reps ? Math.max(m, s.weightLb * s.reps) : m),
        0,
      );
      isPR = prior.length > 0 && score > priorMax;
    } else if (input.kind === "hold" && input.holdSec) {
      const priorMax = prior.reduce(
        (m, s) => (s.holdSec ? Math.max(m, s.holdSec) : m), 0,
      );
      isPR = prior.length > 0 && input.holdSec > priorMax;
    }
    const set: WorkoutSet = { ...input, id: `ws_${now()}_${Math.random().toString(36).slice(2, 6)}`, loggedAt: now(), isPR };
    db.workoutSets.push(set);
    emit();
    return set;
  },
  /** Remove a logged set (undo). */
  unlogWorkoutSet: (setId: string) => {
    db.workoutSets = db.workoutSets.filter((s) => s.id !== setId);
    emit();
  },
  /* ---------------------- Accountability partnerships ------------------- */

  /** Send a covenant invite to another sister. */
  invitePartner: (input: { inviterId: string; inviteeId: string; seasonDays: 30 | 60 | 90; covenant: CovenantGoal[] }) => {
    const pair: AccountabilityPair = {
      id: `pair_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      inviterId: input.inviterId,
      inviteeId: input.inviteeId,
      status: "pending",
      seasonDays: input.seasonDays,
      covenant: input.covenant,
      startedAt: null,
      endsAt: null,
      createdAt: now(),
    };
    db.pairs.push(pair);
    emit();
    return pair;
  },

  /** Accept a pending invite. Sets startedAt to now and endsAt by season. */
  acceptPair: (pairId: string) => {
    const day = 1000 * 60 * 60 * 24;
    const start = now();
    db.pairs = db.pairs.map((p) =>
      p.id === pairId
        ? { ...p, status: "active", startedAt: start, endsAt: start + day * p.seasonDays }
        : p,
    );
    // Seed today's check-in so the dashboard has a prompt waiting.
    const today = new Date(start).toISOString().slice(0, 10);
    if (!db.pairCheckIns.some((c) => c.pairId === pairId && c.date === today)) {
      db.pairCheckIns.push({
        id: `ci_${now()}`,
        pairId,
        prompt: "How is your heart this morning, sister?",
        date: today,
        inviterAnswer: null,
        inviteeAnswer: null,
        createdAt: start,
      });
    }
    emit();
  },

  declinePair: (pairId: string) => {
    db.pairs = db.pairs.filter((p) => p.id !== pairId);
    emit();
  },

  /** Release the season with grace. Marks the pair completed; preserves history. */
  releasePair: (pairId: string) => {
    db.pairs = db.pairs.map((p) =>
      p.id === pairId ? { ...p, status: "released" } : p,
    );
    emit();
  },

  /** Renew a pair for a fresh season starting now. */
  renewPair: (pairId: string, seasonDays: 30 | 60 | 90) => {
    const day = 1000 * 60 * 60 * 24;
    const start = now();
    db.pairs = db.pairs.map((p) =>
      p.id === pairId
        ? { ...p, status: "active", seasonDays, startedAt: start, endsAt: start + day * seasonDays }
        : p,
    );
    emit();
  },

  /** Submit (or update) the viewer's answer to today's shared prompt. */
  submitCheckIn: (input: { pairId: string; viewerId: string; answer: string }) => {
    const pair = db.pairs.find((p) => p.id === input.pairId);
    if (!pair) return;
    const today = new Date(now()).toISOString().slice(0, 10);
    const isInviter = pair.inviterId === input.viewerId;
    let row = db.pairCheckIns.find((c) => c.pairId === input.pairId && c.date === today);
    if (!row) {
      row = {
        id: `ci_${now()}`,
        pairId: input.pairId,
        prompt: "How is your heart this morning, sister?",
        date: today,
        inviterAnswer: null,
        inviteeAnswer: null,
        createdAt: now(),
      };
      db.pairCheckIns.push(row);
    }
    db.pairCheckIns = db.pairCheckIns.map((c) =>
      c.id === row!.id
        ? { ...c, inviterAnswer: isInviter ? input.answer : c.inviterAnswer, inviteeAnswer: isInviter ? c.inviteeAnswer : input.answer }
        : c,
    );
    emit();
  },

  /** Send a one-tap encouragement ping to the partner. */
  sendPing: (input: { pairId: string; fromUserId: string; kind: PingKind }) => {
    const pair = db.pairs.find((p) => p.id === input.pairId);
    if (!pair) return;
    const toUserId = pair.inviterId === input.fromUserId ? pair.inviteeId : pair.inviterId;
    const ping: PairPing = {
      id: `pp_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      pairId: input.pairId,
      fromUserId: input.fromUserId,
      toUserId,
      kind: input.kind,
      sentAt: now(),
    };
    db.pairPings.push(ping);
    emit();
  },

  /** Toggle a workout into / out of the saved library. */
  toggleSaveWorkout: (input: { userId: string; workoutId: string; title: string; focus: string; durationMin: number; movements: number }) => {
    const existing = db.savedWorkouts.find(
      (s) => s.userId === input.userId && s.workoutId === input.workoutId,
    );
    if (existing) {
      db.savedWorkouts = db.savedWorkouts.filter((s) => s.id !== existing.id);
      emit();
      return false;
    }
    const saved: SavedWorkout = {
      id: `sw_${now()}`, userId: input.userId, workoutId: input.workoutId,
      title: input.title, focus: input.focus, durationMin: input.durationMin, movements: input.movements,
      savedAt: now(), lastCompletedAt: null, timesCompleted: 0,
    };
    db.savedWorkouts.push(saved);
    emit();
    return true;
  },
  logFood: (input: Omit<FoodLog, "id" | "loggedAt">) => {
    const f: FoodLog = { ...input, id: `f_${now()}`, loggedAt: now() };
    db.foodLogs.push(f);
    emit();
    return f;
  },
  setStreakResting: (userId: string, restingDays: number) => {
    db = {
      ...db,
      users: db.users.map((u) =>
        u.id === userId ? { ...u, resting: true, restingDays, streak: 0 } : u
      ),
    };
    emit();
  },
  resumeStreak: (userId: string, streak: number) => {
    db = {
      ...db,
      users: db.users.map((u) =>
        u.id === userId ? { ...u, resting: false, restingDays: 0, streak } : u
      ),
    };
    emit();
  },
  sendChatMessage: (input: Omit<ChatMessage, "id" | "sentAt">) => {
    const msg: ChatMessage = { ...input, id: `m_${now()}`, sentAt: now() };
    db.chatMessages.push(msg);
    emit();
    // Sister Lydia auto-reply: when a sister sends to the lydia thread,
    // schedule a short typing pause then push Lydia's response.
    if (input.threadId === "lydia" && input.fromCoach === false && input.type === "text") {
      const reply = lydiaReply(input.body);
      const delay = Math.min(1800, 600 + input.body.length * 10);
      setTimeout(() => {
        const replyMsg: ChatMessage = {
          id: `ly_${now()}`, threadId: "lydia", fromCoach: true,
          type: "text", body: reply, sentAt: now(),
        };
        db.chatMessages.push(replyMsg);
        emit();
      }, delay);
    }
    return msg;
  },
  saveJournalEntry: (input: Omit<JournalEntry, "id" | "createdAt">) => {
    const j: JournalEntry = { ...input, id: `j_${now()}`, createdAt: now() };
    db.journalEntries.push(j);
    emit();
    return j;
  },
  upsertCycleLog: (input: Omit<CycleLog, "id" | "loggedAt">) => {
    const existing = db.cycleLogs.findIndex(
      (c) => c.userId === input.userId && c.day === input.day
    );
    const log: CycleLog = { ...input, id: `c_${input.userId}_${input.day}`, loggedAt: now() };
    if (existing >= 0) db.cycleLogs[existing] = log;
    else db.cycleLogs.push(log);
    emit();
    return log;
  },
  plantSeed: (input: Omit<Seed, "id" | "joined">) => {
    const s: Seed = { ...input, id: `s_${now()}`, joined: now() };
    db.seeds.push(s);
    emit();
    return s;
  },
  setUserTier: (userId: string, tier: Tier) => {
    db = {
      ...db,
      users: db.users.map((u) =>
        u.id === userId ? { ...u, tier } : u
      ),
    };
    emit();
  },
  setUserLocation: (userId: string, zipCode: string, city: string) => {
    db = {
      ...db,
      users: db.users.map((u) =>
        u.id === userId ? { ...u, zipCode, city } : u
      ),
    };
    emit();
  },

  /** Update editable profile fields (name, email, city, instagram). Empty
   *  strings for instagram or city are stored as null. zipCode is left alone
   *  here so we don't accidentally clobber it. */
  updateProfile: (
    userId: string,
    input: {
      name?: string;
      email?: string;
      city?: string | null;
      instagram?: string | null;
    },
  ) => {
    db = {
      ...db,
      users: db.users.map((u) => {
        if (u.id !== userId) return u;
        const next = { ...u };
        if (input.name !== undefined) next.name = input.name;
        if (input.email !== undefined) next.email = input.email;
        if (input.city !== undefined) next.city = input.city || null;
        if (input.instagram !== undefined) {
          // Strip a leading @ if the sister types one; store empty as null.
          const trimmed = (input.instagram ?? "").trim().replace(/^@/, "");
          next.instagram = trimmed.length > 0 ? trimmed : null;
        }
        return next;
      }),
    };
    emit();
  },

  /** Persist a square JPEG/PNG data URL as the sister's avatar. The Profile
   *  page does the cropping client-side before calling this. */
  setAvatar: (userId: string, dataUrl: string | null) => {
    db = {
      ...db,
      users: db.users.map((u) =>
        u.id === userId ? { ...u, avatarDataUrl: dataUrl } : u,
      ),
    };
    emit();
  },

  /** Mock password change. Validates the current password against the stored
   *  hash and rejects (returns { ok: false, reason }) if it doesn't match.
   *  Real auth would never live in client storage, this is for the demo. */
  changePassword: (
    userId: string,
    input: { currentPassword: string; newPassword: string },
  ): { ok: true } | { ok: false; reason: string } => {
    const u = db.users.find((u) => u.id === userId);
    if (!u) return { ok: false, reason: "Account not found." };
    // Demo rule: any current password works as long as the user types
    // something (so the seed account doesn't lock anyone out), but a real
    // build would compare against a bcrypt hash on the server.
    if (!input.currentPassword.trim()) {
      return { ok: false, reason: "Please enter your current password." };
    }
    if (input.newPassword.length < 8) {
      return { ok: false, reason: "New password must be at least 8 characters." };
    }
    db = {
      ...db,
      users: db.users.map((u) =>
        u.id === userId ? { ...u, passwordHash: `demo-hash:${input.newPassword.length}` } : u,
      ),
    };
    emit();
    return { ok: true };
  },
  scheduleLiveClass: (input: Omit<LiveClass, "id" | "watching" | "attendees">) => {
    const c: LiveClass = { ...input, id: `lc_${now()}`, watching: 0, attendees: 0 };
    db.liveClasses.push(c);
    emit();
    return c;
  },
  startLiveClass: (id: string) => {
    db = {
      ...db,
      liveClasses: db.liveClasses.map((c) =>
        c.id === id ? { ...c, status: "live", scheduledFor: now() } : c
      ),
    };
    emit();
  },
  endLiveClass: (id: string, replayDurationMin: number) => {
    db = {
      ...db,
      liveClasses: db.liveClasses.map((c) =>
        c.id === id ? { ...c, status: "replay", replayDurationMin } : c
      ),
    };
    emit();
  },
  postLiveChat: (input: Omit<LiveChatMessage, "id" | "sentAt">) => {
    const m: LiveChatMessage = { ...input, id: `lcm_${now()}`, sentAt: now() };
    db.liveChat.push(m);
    emit();
    return m;
  },
  /**
   * Legacy one-way RSVP. New callers should prefer `toggleEventRsvp` which
   * tracks per-user RSVP membership in the eventRsvps ledger. This shim
   * keeps existing call sites working by routing them to the toggle for
   * the current user (Hannah by default).
   */
  rsvpToEvent: (eventId: string, userId: string = "u_hannah") => {
    const existing = db.eventRsvps.find(
      (r) => r.eventId === eventId && r.userId === userId
    );
    if (existing) return; // idempotent
    db = {
      ...db,
      eventRsvps: [
        ...db.eventRsvps,
        { id: `er_${now()}`, eventId, userId, createdAt: now() },
      ],
      communityEvents: db.communityEvents.map((e) =>
        e.id === eventId ? { ...e, rsvpCount: (e.rsvpCount ?? 0) + 1 } : e
      ),
    };
    emit();
  },
  toggleClassReminder: (userId: string, classId: string) => {
    const existing = db.classReminders.find((r) => r.userId === userId && r.classId === classId);
    if (existing) {
      db = { ...db, classReminders: db.classReminders.filter((r) => r.id !== existing.id) };
    } else {
      db = {
        ...db,
        classReminders: [
          ...db.classReminders,
          { id: `cr_${now()}`, userId, classId, createdAt: now() },
        ],
      };
    }
    emit();
  },
  toggleAmen: (postId: string) => {
    db = {
      ...db,
      feedPosts: db.feedPosts.map((p) =>
        p.id === postId
          ? { ...p, isAmened: !p.isAmened, amens: p.amens + (p.isAmened ? -1 : 1) }
          : p
      ),
    };
    emit();
  },
  toggleWater: (postId: string) => {
    db = {
      ...db,
      feedPosts: db.feedPosts.map((p) =>
        p.id === postId
          ? { ...p, isWatered: !p.isWatered, waters: p.waters + (p.isWatered ? -1 : 1) }
          : p
      ),
    };
    emit();
  },
  postFeedEntry: (input: Omit<FeedPost, "id" | "createdAt" | "amens" | "waters" | "comments">) => {
    const post: FeedPost = {
      ...input,
      id: `fp_${now()}`,
      amens: 0,
      waters: 0,
      comments: 0,
      createdAt: now(),
    };
    db = { ...db, feedPosts: [post, ...db.feedPosts] };
    emit();
    return post;
  },
  addFeedComment: (input: Omit<FeedComment, "id" | "createdAt">) => {
    const c: FeedComment = { ...input, id: `fc_${now()}`, createdAt: now() };
    db = {
      ...db,
      feedComments: [...db.feedComments, c],
      feedPosts: db.feedPosts.map((p) =>
        p.id === input.postId ? { ...p, comments: p.comments + 1 } : p
      ),
    };
    emit();
    return c;
  },
  joinLiveClass: (classId: string, userId: string) => {
    const already = db.liveAttendance.find(
      (a) => a.classId === classId && a.userId === userId && a.leftAt == null
    );
    if (already) return;
    db = {
      ...db,
      liveAttendance: [
        ...db.liveAttendance,
        { id: `la_${now()}`, classId, userId, joinedAt: now(), leftAt: null },
      ],
      liveClasses: db.liveClasses.map((c) =>
        c.id === classId
          ? { ...c, watching: (c.watching ?? 0) + 1, attendees: (c.attendees ?? 0) + 1 }
          : c
      ),
    };
    emit();
  },
  leaveLiveClass: (classId: string, userId: string) => {
    const active = db.liveAttendance.find(
      (a) => a.classId === classId && a.userId === userId && a.leftAt == null
    );
    if (!active) return;
    db = {
      ...db,
      liveAttendance: db.liveAttendance.map((a) =>
        a.id === active.id ? { ...a, leftAt: now() } : a
      ),
      liveClasses: db.liveClasses.map((c) =>
        c.id === classId ? { ...c, watching: Math.max(0, (c.watching ?? 0) - 1) } : c
      ),
    };
    emit();
  },
  toggleEventRsvp: (eventId: string, userId: string) => {
    const existing = db.eventRsvps.find(
      (r) => r.eventId === eventId && r.userId === userId
    );
    if (existing) {
      db = {
        ...db,
        eventRsvps: db.eventRsvps.filter((r) => r.id !== existing.id),
        communityEvents: db.communityEvents.map((e) =>
          e.id === eventId
            ? { ...e, rsvpCount: Math.max(0, (e.rsvpCount ?? 0) - 1) }
            : e
        ),
      };
    } else {
      db = {
        ...db,
        eventRsvps: [
          ...db.eventRsvps,
          { id: `er_${now()}`, eventId, userId, createdAt: now() },
        ],
        communityEvents: db.communityEvents.map((e) =>
          e.id === eventId ? { ...e, rsvpCount: (e.rsvpCount ?? 0) + 1 } : e
        ),
      };
    }
    emit();
  },
  hostSisterMeetup: (
    input: Omit<CommunityEvent, "id" | "source" | "rsvpCount">
  ) => {
    const event: CommunityEvent = {
      ...input,
      id: `ce_sm_${now()}`,
      source: "sister-meetup",
      rsvpCount: 1,
    };
    db = {
      ...db,
      communityEvents: [...db.communityEvents, event],
      eventRsvps: input.hostId
        ? [
            ...db.eventRsvps,
            { id: `er_${now()}`, eventId: event.id, userId: input.hostId, createdAt: now() },
          ]
        : db.eventRsvps,
    };
    emit();
    return event;
  },

  /* ----------------------------- Macros engine ------------------------- */

  /** Save the sister's biometric/activity profile. Recomputes auto plan if
   *  the current plan is in auto mode (so changes flow through immediately). */
  saveMacroProfile: (input: Omit<MacroProfile, "updatedAt">) => {
    const next: MacroProfile = { ...input, updatedAt: now() };
    const exists = db.macroProfiles.some((p) => p.userId === input.userId);
    db.macroProfiles = exists
      ? db.macroProfiles.map((p) => (p.userId === input.userId ? next : p))
      : [...db.macroProfiles, next];
    // If their plan is auto, refresh targets to reflect new stats.
    const plan = db.macroPlans.find((p) => p.userId === input.userId);
    if (plan && plan.mode === "auto" && !plan.locked) {
      const a = autoMacros(next);
      db.macroPlans = db.macroPlans.map((p) =>
        p.userId === input.userId
          ? { ...p, kcal: a.kcal, proteinG: a.proteinG, carbsG: a.carbsG, fatG: a.fatG, ratios: a.ratios, updatedAt: now() }
          : p,
      );
    }
    emit();
    return next;
  },

  /** Set or replace the sister's macro plan. Source defaults to "self". */
  saveMacroPlan: (input: {
    userId: string;
    mode: MacroMode;
    kcal: number;
    proteinG: number;
    carbsG: number;
    fatG: number;
    ratios: { p: number; c: number; f: number };
    source?: MacroSource;
    locked?: boolean;
    coachNote?: string | null;
  }) => {
    const existing = db.macroPlans.find((p) => p.userId === input.userId);
    const next: MacroPlan = {
      userId: input.userId,
      mode: input.mode,
      kcal: Math.round(input.kcal),
      proteinG: Math.round(input.proteinG),
      carbsG: Math.round(input.carbsG),
      fatG: Math.round(input.fatG),
      ratios: input.ratios,
      source: input.source ?? existing?.source ?? "self",
      locked: input.locked ?? existing?.locked ?? false,
      coachNote: input.coachNote ?? existing?.coachNote ?? null,
      updatedAt: now(),
    };
    db.macroPlans = existing
      ? db.macroPlans.map((p) => (p.userId === input.userId ? next : p))
      : [...db.macroPlans, next];
    emit();
    return next;
  },

  /** Coach action: set targets on a sister's plan and lock them. */
  coachOverrideMacros: (input: {
    userId: string;
    mode: MacroMode;
    kcal: number;
    proteinG: number;
    carbsG: number;
    fatG: number;
    ratios: { p: number; c: number; f: number };
    coachNote: string;
  }) => {
    return mutations.saveMacroPlan({
      ...input,
      source: "coach",
      locked: true,
      coachNote: input.coachNote,
    });
  },

  /** Coach action: release the lock so the sister can edit again. */
  releaseCoachOverride: (userId: string) => {
    db.macroPlans = db.macroPlans.map((p) =>
      p.userId === userId
        ? { ...p, source: "self", locked: false, coachNote: null, updatedAt: now() }
        : p,
    );
    emit();
  },

  /** Add or replace a meal block log for today. One row per (user, date,
   *  block) so re-logging breakfast updates rather than duplicates. */
  logMeal: (input: {
    userId: string;
    block: MealBlock;
    kcal: number;
    proteinG: number;
    carbsG: number;
    fatG: number;
    note?: string | null;
  }) => {
    const date = new Date(now()).toISOString().slice(0, 10);
    const existing = db.mealLogs.find(
      (m) => m.userId === input.userId && m.date === date && m.block === input.block,
    );
    if (existing) {
      db.mealLogs = db.mealLogs.map((m) =>
        m.id === existing.id
          ? {
              ...m,
              kcal: Math.round(input.kcal),
              proteinG: Math.round(input.proteinG),
              carbsG: Math.round(input.carbsG),
              fatG: Math.round(input.fatG),
              note: input.note ?? null,
              loggedAt: now(),
            }
          : m,
      );
    } else {
      const log: MealLog = {
        id: `ml_${now()}_${Math.random().toString(36).slice(2, 6)}`,
        userId: input.userId,
        date,
        block: input.block,
        kcal: Math.round(input.kcal),
        proteinG: Math.round(input.proteinG),
        carbsG: Math.round(input.carbsG),
        fatG: Math.round(input.fatG),
        note: input.note ?? null,
        loggedAt: now(),
      };
      db.mealLogs = [...db.mealLogs, log];
    }
    emit();
  },

  /** Remove a meal log (undo). */
  unlogMeal: (mealId: string) => {
    db.mealLogs = db.mealLogs.filter((m) => m.id !== mealId);
    emit();
  },

  /** Record that a sister was shown a verse. Used to rotate the verse pool
   *  and to ensure the daily-complete blessing fires once per day. */
  recordVerseShown: (input: { userId: string; verseId: string; reason: VerseContext | "daily-complete" }) => {
    db.verseHistory = [
      ...db.verseHistory,
      {
        id: `vh_${now()}_${Math.random().toString(36).slice(2, 6)}`,
        userId: input.userId,
        verseId: input.verseId,
        reason: input.reason,
        shownAt: now(),
      },
    ];
    // History is reactive but no UI subscribes to it directly. Skip emit().
  },

  /** Set or update a sister's Sabbath day and toggles. Creates the row if
   *  none exists. Sabbath was made for her, not the other way round. */
  setSabbathDay: (input: {
    userId: string;
    enabled: boolean;
    dayOfWeek: DayOfWeek;
    pauseStreaks: boolean;
    devotionalReminder: boolean;
  }) => {
    const next: SabbathSettings = {
      userId: input.userId,
      enabled: input.enabled,
      dayOfWeek: input.dayOfWeek,
      pauseStreaks: input.pauseStreaks,
      devotionalReminder: input.devotionalReminder,
      updatedAt: now(),
    };
    const existing = db.sabbathSettings.find((s) => s.userId === input.userId);
    if (existing) {
      db.sabbathSettings = db.sabbathSettings.map((s) =>
        s.userId === input.userId ? next : s,
      );
    } else {
      db.sabbathSettings = [...db.sabbathSettings, next];
    }
    emit();
  },

  /** Sister marks "I sat with this verse" for the current week. Replaces
   *  any prior check-in for the week. Reflection is optional. Sharing
   *  flips a flag for the community feed. Grace, not performance. */
  markVerseCheckIn: (input: {
    userId: string;
    reflection?: string;
    sharedToFeed?: boolean;
  }) => {
    const wk = queries.currentWeekStartIso();
    const existing = db.verseCheckIns.find(
      (c) => c.userId === input.userId && c.weekStartIso === wk,
    );
    const next: VerseCheckIn = {
      id: existing?.id ?? `vci_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      userId: input.userId,
      weekStartIso: wk,
      reflection: (input.reflection ?? "").trim(),
      sharedToFeed: input.sharedToFeed ?? false,
      createdAt: existing?.createdAt ?? now(),
    };
    if (existing) {
      db.verseCheckIns = db.verseCheckIns.map((c) =>
        c.id === existing.id ? next : c,
      );
    } else {
      db.verseCheckIns = [...db.verseCheckIns, next];
    }
    emit();
  },

  /** Sister rescinds her current-week check-in (rare, but offered for grace). */
  unmarkVerseCheckIn: (userId: string) => {
    const wk = queries.currentWeekStartIso();
    db.verseCheckIns = db.verseCheckIns.filter(
      (c) => !(c.userId === userId && c.weekStartIso === wk),
    );
    emit();
  },

  /** Sister sets her own season (TTC, pregnant, postpartum, nursing, none).
   *  When set to pregnant or nursing, the macros plan recomputes to honor
   *  GGS V2 adjustments. Postpartum movement clearance is preserved. */
  saveSeasonState: (input: Omit<SeasonState, "updatedAt">) => {
    const next: SeasonState = { ...input, updatedAt: now() };
    const existing = db.seasonStates.find((s) => s.userId === input.userId);
    if (existing) {
      db.seasonStates = db.seasonStates.map((s) => (s.userId === input.userId ? next : s));
    } else {
      db.seasonStates = [...db.seasonStates, next];
    }
    // Also update the legacy seasonKind on macroProfile so older readers
    // see the same season label.
    db.macroProfiles = db.macroProfiles.map((p) =>
      p.userId === input.userId ? { ...p, seasonKind: input.kind, updatedAt: now() } : p,
    );
    // Recompute auto plan if not locked and currently in auto mode.
    const profile = db.macroProfiles.find((p) => p.userId === input.userId);
    const plan = db.macroPlans.find((p) => p.userId === input.userId);
    if (profile && plan && plan.mode === "auto" && !plan.locked) {
      const recomputed = autoMacrosWithSeason(profile, next);
      db.macroPlans = db.macroPlans.map((p) =>
        p.userId === input.userId
          ? {
              ...p,
              kcal: recomputed.kcal,
              proteinG: recomputed.proteinG,
              carbsG: recomputed.carbsG,
              fatG: recomputed.fatG,
              ratios: recomputed.ratios,
              updatedAt: now(),
            }
          : p,
      );
    }
    emit();
  },

  /** Coach action: flip the postpartum return-to-movement clearance. */
  setMovementClearance: (userId: string, cleared: boolean) => {
    db.seasonStates = db.seasonStates.map((s) =>
      s.userId === userId ? { ...s, clearedForMovement: cleared, updatedAt: now() } : s,
    );
    emit();
  },

  /* =============================== Recipes ================================ */

  /** Save a recipe to a sister's garden ("Add to Garden 🌱" entry on cards). */
  saveRecipe: (userId: string, recipeId: string) => {
    if (db.savedRecipes.some((s) => s.userId === userId && s.recipeId === recipeId)) return;
    db.savedRecipes = [
      ...db.savedRecipes,
      { id: `sr_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, userId, recipeId, savedAt: now() },
    ];
    emit();
  },

  /** Remove a recipe from a sister's saved list. */
  unsaveRecipe: (userId: string, recipeId: string) => {
    db.savedRecipes = db.savedRecipes.filter(
      (s) => !(s.userId === userId && s.recipeId === recipeId),
    );
    emit();
  },

  /** Loved this. Only counted AFTER cooked. Increments lovedCount and recomputes
   *  awardTier so chips stay honest in real time. Idempotent per sister. */
  loveRecipe: (userId: string, recipeId: string) => {
    if (db.recipeLoves.some((l) => l.userId === userId && l.recipeId === recipeId)) return;
    db.recipeLoves = [
      ...db.recipeLoves,
      { id: `rl_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, userId, recipeId, lovedAt: now() },
    ];
    db.recipes = db.recipes.map((r) => {
      if (r.id !== recipeId) return r;
      const next = r.lovedCount + 1;
      const nextTier: RecipeAwardTier =
        next >= 500 ? "garden-favorite" :
        next >= 100 ? "flourishing" :
        next >= 25  ? "rooted" :
        next >= 5   ? "sprouted" : "none";
      return { ...r, lovedCount: next, awardTier: nextTier };
    });
    emit();
  },

  /** Sister writes her own recipe in the AddRecipe modal (Write Your Own tab). */
  createRecipe: (input: {
    authorId: string;
    title: string;
    description: string;
    servings: number;
    durationMin: number;
    kcal: number;
    proteinG: number;
    carbsG: number;
    fatG: number;
    fiberG: number;
    ingredients: RecipeIngredient[];
    steps: { title: string; body: string }[];
    seasonTags: RecipeSeasonTag[];
    scripturePairing: string | null;
    isPublic: boolean;
    heroGradient?: string;
    heroEmojis?: string[];
  }): Recipe => {
    const id = `r_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const recipe: Recipe = {
      id,
      authorId: input.authorId,
      title: input.title,
      description: input.description,
      heroGradient: input.heroGradient ?? "radial-gradient(120% 80% at 50% 30%, #6b8e6f 0%, #2C6248 60%, #163828 100%)",
      heroEmojis: input.heroEmojis ?? ["\ud83c\udf3f", "\ud83e\udd57", "\ud83c\udf45"],
      heroImageUrl: pickGenericHeroImage(input.seasonTags, input.title),
      servings: input.servings,
      durationMin: input.durationMin,
      kcal: input.kcal,
      proteinG: input.proteinG,
      carbsG: input.carbsG,
      fatG: input.fatG,
      fiberG: input.fiberG,
      ingredients: input.ingredients,
      steps: input.steps,
      seasonTags: input.seasonTags,
      safetyFlags: [],
      scripturePairing: input.scripturePairing,
      source: "sister",
      sourceUrl: null,
      lovedCount: 0,
      awardTier: "none",
      public: input.isPublic,
      // Public recipes from sisters wait for moderation; pair-only stays clean.
      pendingReview: input.isPublic,
      createdAt: now(),
    };
    db.recipes = [...db.recipes, recipe];
    emit();
    return recipe;
  },

  /** Mock URL importer. In production this would hit a recipe parser backend.
   *  For now, returns a fixture pre-filled with sensible defaults so the
   *  Import tab feels real. The sister can edit before saving. */
  mockImportRecipe: (input: { authorId: string; url: string }): Recipe => {
    // Choose a fixture by hash of url so repeated imports of the same url
    // return the same draft. Cycle through three flavors.
    const hash = Array.from(input.url).reduce((acc, c) => acc + c.charCodeAt(0), 0);
    const fixtures: Array<Omit<Recipe, "id" | "authorId" | "createdAt" | "sourceUrl" | "lovedCount" | "awardTier" | "public" | "pendingReview" | "source">> = [
      {
        title: "Imported: Lemon herb chicken",
        description: "Pulled from the link you shared. Roasted chicken with lemon, thyme, and garlic.",
        heroGradient: "radial-gradient(120% 80% at 50% 30%, #d8c890 0%, #a89640 55%, #4a3a1f 100%)",
        heroEmojis: ["\ud83d\udc14", "\ud83c\udf4b", "\ud83c\udf3f"],
        heroImageUrl: "recipes/generic_dinner_chicken.webp",
        servings: 4, durationMin: 45,
        kcal: 480, proteinG: 36, carbsG: 18, fatG: 28, fiberG: 4,
        ingredients: [
          { qty: 1.5, unit: "lb", item: "bone-in chicken thighs" },
          { qty: 2,   unit: "", item: "lemons" },
          { qty: 4,   unit: "sprigs", item: "fresh thyme" },
          { qty: 4,   unit: "cloves", item: "garlic" },
          { qty: 3,   unit: "tbsp", item: "olive oil" },
        ],
        steps: [
          { title: "Marinate", body: "Lemon juice, oil, garlic, thyme. 30 minutes minimum." },
          { title: "Roast", body: "425\u00b0F skin-side up, 30 to 35 minutes." },
          { title: "Rest and serve", body: "Rest 5 minutes. Pour pan juices over the top." },
        ],
        seasonTags: ["family"],
        safetyFlags: [],
        scripturePairing: null,
      },
      {
        title: "Imported: Coconut curry chickpeas",
        description: "Vegetarian, freezer-friendly, ready in 25 minutes. Found at the link you shared.",
        heroGradient: "radial-gradient(120% 80% at 50% 30%, #e0a060 0%, #a06a30 55%, #3a2410 100%)",
        heroEmojis: ["\ud83e\uded8", "\ud83e\udd65", "\ud83c\udf3f"],
        heroImageUrl: "recipes/generic_dinner_curry.webp",
        servings: 4, durationMin: 25,
        kcal: 420, proteinG: 14, carbsG: 48, fatG: 20, fiberG: 11,
        ingredients: [
          { qty: 2,   unit: "cans", item: "chickpeas, drained" },
          { qty: 1,   unit: "can", item: "full-fat coconut milk" },
          { qty: 2,   unit: "tbsp", item: "yellow curry paste" },
          { qty: 4,   unit: "cups", item: "baby spinach" },
          { qty: 1,   unit: "", item: "yellow onion, sliced" },
        ],
        steps: [
          { title: "Sweat the onion", body: "In oil, until soft. Add curry paste, bloom 1 minute." },
          { title: "Simmer", body: "Coconut milk and chickpeas, 15 minutes low." },
          { title: "Wilt and serve", body: "Spinach in last, until just wilted. Over rice." },
        ],
        seasonTags: ["family", "quick"],
        safetyFlags: [],
        scripturePairing: null,
      },
      {
        title: "Imported: Berry oat bake",
        description: "Make-ahead breakfast for the whole family. Pulled from the link you shared.",
        heroGradient: "radial-gradient(120% 80% at 50% 30%, #c878a0 0%, #803060 55%, #2a1024 100%)",
        heroEmojis: ["\ud83e\udd64", "\ud83c\udf53", "\ud83c\udf6f"],
        heroImageUrl: "recipes/generic_breakfast_oatmeal.webp",
        servings: 6, durationMin: 50,
        kcal: 340, proteinG: 11, carbsG: 52, fatG: 10, fiberG: 7,
        ingredients: [
          { qty: 2.5, unit: "cups", item: "rolled oats" },
          { qty: 2,   unit: "cups", item: "milk of choice" },
          { qty: 2,   unit: "", item: "eggs" },
          { qty: 0.25, unit: "cup", item: "maple syrup" },
          { qty: 2,   unit: "cups", item: "mixed berries" },
        ],
        steps: [
          { title: "Whisk and pour", body: "All wet, then fold in oats and berries. 9x13 dish." },
          { title: "Bake", body: "350\u00b0F for 40 to 45 minutes until set and golden." },
        ],
        seasonTags: ["family", "quick"],
        safetyFlags: [],
        scripturePairing: null,
      },
    ];
    const fixture = fixtures[hash % fixtures.length];
    const id = `r_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const recipe: Recipe = {
      id,
      authorId: input.authorId,
      ...fixture,
      source: "imported",
      sourceUrl: input.url,
      lovedCount: 0,
      awardTier: "none",
      // Imported recipes start private and pendingReview off until the sister
      // chooses to share. Keeps her drafts safe.
      public: false,
      pendingReview: false,
      createdAt: now(),
    };
    db.recipes = [...db.recipes, recipe];
    emit();
    return recipe;
  },

  /** Capture a recipe from pasted text (Recime-style flow). The sister copies
   *  a caption, blog post, or shared text and we run the heuristic parser to
   *  prefill a draft. The caller can pass an optional sourceUrl to label the
   *  origin of the capture. The created recipe is private until the sister
   *  decides to share it. */
  captureRecipeFromText: (input: {
    authorId: string;
    parsed: {
      title: string;
      description: string;
      servings: number;
      durationMin: number;
      ingredients: RecipeIngredient[];
      steps: string[];
    };
    sourceUrl?: string | null;
  }): Recipe => {
    const id = `r_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const stepObjs = input.parsed.steps.map((body, i) => ({
      title: `Step ${i + 1}`,
      body,
    }));
    const recipe: Recipe = {
      id,
      authorId: input.authorId,
      title: input.parsed.title || "Captured recipe",
      description: input.parsed.description,
      heroGradient: "radial-gradient(120% 80% at 50% 30%, #b8d4a8 0%, #5a8a5e 55%, #1E4A35 100%)",
      heroEmojis: ["\ud83c\udf3f", "\u2728", "\ud83d\udcda"],
      heroImageUrl: pickGenericHeroImage(["family"], input.parsed.title || ""),
      servings: input.parsed.servings,
      durationMin: input.parsed.durationMin,
      // Captured recipes have no macros until the sister enters them.
      kcal: 0, proteinG: 0, carbsG: 0, fatG: 0, fiberG: 0,
      ingredients: input.parsed.ingredients,
      steps: stepObjs,
      seasonTags: ["family"],
      safetyFlags: [],
      scripturePairing: null,
      source: "imported",
      sourceUrl: input.sourceUrl ?? null,
      lovedCount: 0,
      awardTier: "none",
      public: false,
      pendingReview: false,
      createdAt: now(),
    };
    db.recipes = [...db.recipes, recipe];
    emit();
    return recipe;
  },

  /** Toggle the public flag on a sister's own recipe (used by ShareRecipeSheet). */
  setRecipePublic: (recipeId: string, isPublic: boolean) => {
    db.recipes = db.recipes.map((r) =>
      r.id === recipeId
        ? { ...r, public: isPublic, pendingReview: isPublic && r.source !== "kiley" }
        : r,
    );
    emit();
  },

  /* ----------------------------- Pair rituals -------------------------- */

  /** Record the viewer's reaction to this week's shared scripture. Creates the
   *  scripture row if one doesn't yet exist for this week. */
  reactToScripture: (input: {
    pairId: string;
    viewerId: string;
    reaction: ScriptureReactionKind;
    note?: string;
  }) => {
    const pair = db.pairs.find((p) => p.id === input.pairId);
    if (!pair) return;
    const isInviter = pair.inviterId === input.viewerId;
    const ws = queries.weekStartYmd();
    let row = db.pairSharedScriptures.find((s) => s.pairId === input.pairId && s.weekStart === ws);
    if (!row) {
      // Should not happen in seeded flow, but keep the mutation defensive.
      row = {
        id: `ps_${input.pairId}_${ws}`,
        pairId: input.pairId,
        weekStart: ws,
        reference: "Psalm 27:14",
        text: "Wait for the Lord; be strong and take heart and wait for the Lord.",
        inviterReaction: null,
        inviteeReaction: null,
        inviterNote: null,
        inviteeNote: null,
        createdAt: now(),
      };
      db.pairSharedScriptures.push(row);
    }
    db.pairSharedScriptures = db.pairSharedScriptures.map((s) =>
      s.id === row!.id
        ? {
            ...s,
            inviterReaction: isInviter ? input.reaction : s.inviterReaction,
            inviteeReaction: !isInviter ? input.reaction : s.inviteeReaction,
            inviterNote: isInviter ? (input.note ?? s.inviterNote) : s.inviterNote,
            inviteeNote: !isInviter ? (input.note ?? s.inviteeNote) : s.inviteeNote,
          }
        : s,
    );
    emit();
  },

  /** Pick the recipe a pair will cook together this week. Replaces an earlier
   *  unmarked pick if one existed; refuses to overwrite a pick where one of
   *  the sisters has already cooked. */
  pickSharedRecipe: (input: { pairId: string; viewerId: string; recipeId: string }) => {
    const ws = queries.weekStartYmd();
    const existing = db.pairSharedRecipes.find((r) => r.pairId === input.pairId && r.weekStart === ws);
    if (existing && (existing.inviterCookedAt || existing.inviteeCookedAt)) return existing;
    if (existing) {
      db.pairSharedRecipes = db.pairSharedRecipes.map((r) =>
        r.id === existing.id ? { ...r, recipeId: input.recipeId, pickedBy: input.viewerId } : r,
      );
    } else {
      db.pairSharedRecipes.push({
        id: `psr_${input.pairId}_${ws}`,
        pairId: input.pairId,
        weekStart: ws,
        recipeId: input.recipeId,
        pickedBy: input.viewerId,
        inviterCookedAt: null,
        inviteeCookedAt: null,
        createdAt: now(),
      });
    }
    emit();
  },

  /** Mark the viewer as having cooked this week's shared recipe. */
  markCookedTogether: (input: { pairId: string; viewerId: string }) => {
    const pair = db.pairs.find((p) => p.id === input.pairId);
    if (!pair) return;
    const isInviter = pair.inviterId === input.viewerId;
    const ws = queries.weekStartYmd();
    db.pairSharedRecipes = db.pairSharedRecipes.map((r) =>
      r.pairId === input.pairId && r.weekStart === ws
        ? {
            ...r,
            inviterCookedAt: isInviter ? now() : r.inviterCookedAt,
            inviteeCookedAt: !isInviter ? now() : r.inviteeCookedAt,
          }
        : r,
    );
    emit();
  },

  /** Accept an auto-match suggestion. Creates an active pair instantly with
   *  the four shared rituals as the implicit covenant; no covenant negotiation
   *  needed since this is the auto-match path. */
  acceptMatchSuggestion: (input: { suggestionId: string; viewerId: string; candidateId: string }) => {
    const day = 1000 * 60 * 60 * 24;
    // Persist the suggestion if it's a lazy one (id starts with ms_pending_).
    if (!db.matchSuggestions.find((m) => m.id === input.suggestionId)) {
      db.matchSuggestions.push({
        id: input.suggestionId,
        forUserId: input.viewerId,
        candidateId: input.candidateId,
        reason: "You're walking through the same season, sister.",
        createdAt: now(),
        status: "accepted",
      });
    } else {
      db.matchSuggestions = db.matchSuggestions.map((m) =>
        m.id === input.suggestionId ? { ...m, status: "accepted" } : m,
      );
    }
    const pairId = `pair_auto_${input.viewerId}_${input.candidateId}_${Date.now()}`;
    db.pairs.push({
      id: pairId,
      inviterId: input.viewerId,
      inviteeId: input.candidateId,
      status: "active",
      seasonDays: 30,
      covenant: [
        { kind: "scripture",   text: "Share a verse for the week" },
        { kind: "nourishment", text: "Cook one meal together each week" },
        { kind: "custom",      text: "Cheer each other's garden goals" },
      ],
      startedAt: now(),
      endsAt: now() + day * 30,
      createdAt: now(),
    });
    emit();
    return pairId;
  },

  /** Skip a suggested match. Persists the skip so the same candidate is not
   *  re-suggested on the next read. */
  skipMatchSuggestion: (input: { suggestionId: string; viewerId: string; candidateId: string }) => {
    if (!db.matchSuggestions.find((m) => m.id === input.suggestionId)) {
      db.matchSuggestions.push({
        id: input.suggestionId,
        forUserId: input.viewerId,
        candidateId: input.candidateId,
        reason: "You're walking through the same season, sister.",
        createdAt: now(),
        status: "skipped",
      });
    } else {
      db.matchSuggestions = db.matchSuggestions.map((m) =>
        m.id === input.suggestionId ? { ...m, status: "skipped" } : m,
      );
    }
    emit();
  },

  /* ============================ Prayer Wall ============================ */

  /** Post a prayer to the wall. The viewer can post anonymously or with a
   *  theme tag so other sisters can find it from their season filter. */
  postPrayer: (input: {
    userId: string;
    body: string;
    anonymous?: boolean;
    theme?: PrayerTheme;
  }) => {
    const trimmed = input.body.trim();
    if (!trimmed) return null;
    const prayer: Prayer = {
      id: `p_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      userId: input.userId,
      body: trimmed,
      waters: 0,
      createdAt: now(),
      anonymous: input.anonymous ?? false,
      theme: input.theme,
    };
    db.prayers.unshift(prayer);
    emit();
    return prayer;
  },

  /** Tap "I'm praying" on a prayer. Idempotent: tapping again removes the
   *  lift, so the UI can render this as a toggle. */
  liftPrayer: (input: { prayerId: string; viewerId: string }) => {
    const existing = db.prayerLifts.find(
      (l) => l.prayerId === input.prayerId && l.userId === input.viewerId,
    );
    if (existing) {
      db.prayerLifts = db.prayerLifts.filter((l) => l.id !== existing.id);
      // When un-lifting, also drop the seeded floor by 1 so the count moves.
      db.prayers = db.prayers.map((p) =>
        p.id === input.prayerId ? { ...p, waters: Math.max(0, p.waters - 1) } : p,
      );
    } else {
      db.prayerLifts.push({
        id: `pl_${now()}_${Math.random().toString(36).slice(2, 6)}`,
        prayerId: input.prayerId,
        userId: input.viewerId,
        liftedAt: now(),
      });
      db.prayers = db.prayers.map((p) =>
        p.id === input.prayerId ? { ...p, waters: p.waters + 1 } : p,
      );
    }
    emit();
  },

  /** Reply to a prayer with a short note and optional verse reference. */
  replyToPrayer: (input: {
    prayerId: string;
    viewerId: string;
    body: string;
    reference?: string;
  }) => {
    const trimmed = input.body.trim();
    if (!trimmed) return null;
    const reply: PrayerReply = {
      id: `pr_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      prayerId: input.prayerId,
      userId: input.viewerId,
      body: trimmed,
      reference: input.reference?.trim() || null,
      createdAt: now(),
    };
    db.prayerReplies.push(reply);
    emit();
    return reply;
  },

  /* ---------------- Nourish Together challenge mutations ----------- */

  /**
   * Join a challenge. If this is the launch challenge and it is still open,
   * the sister is automatically marked as a founding member with the tier
   * she chose at the moment of joining (defaults to her current tier).
   */
  joinChallenge: (input: {
    challengeId: string;
    userId: string;
    foundingTier?: Tier;
  }) => {
    const challenge = db.challenges.find((c) => c.id === input.challengeId);
    if (!challenge) return null;
    if (challenge.endsAt < now()) return null;
    const existing = db.challengeParticipants.find(
      (p) => p.challengeId === input.challengeId && p.userId === input.userId,
    );
    if (existing) return existing;
    const u = db.users.find((x) => x.id === input.userId);
    const tier: Tier | undefined =
      input.foundingTier ?? (u?.tier as Tier | undefined);
    const participant: ChallengeParticipant = {
      id: `cp_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      challengeId: input.challengeId,
      userId: input.userId,
      joinedAt: now(),
      daysCounted: [],
      completedAt: null,
      isFoundingMember: challenge.isLaunchChallenge,
      foundingTier: challenge.isLaunchChallenge ? tier : undefined,
    };
    db.challengeParticipants.push(participant);
    emit();
    return participant;
  },

  /**
   * Log a day toward a challenge. Idempotent: a sister can tap this many
   * times in a day and only the first counts. If she crosses the threshold
   * with this log, completedAt is set and she sees the celebration once.
   */
  logChallengeDay: (input: { challengeId: string; userId: string }) => {
    const challenge = db.challenges.find((c) => c.id === input.challengeId);
    if (!challenge) return null;
    const today = new Date(now()).toISOString().slice(0, 10);
    const idx = db.challengeParticipants.findIndex(
      (p) => p.challengeId === input.challengeId && p.userId === input.userId,
    );
    if (idx < 0) return null;
    const p = db.challengeParticipants[idx];
    if (p.daysCounted.includes(today)) return p;
    const nextDays = [...p.daysCounted, today];
    const justCompleted =
      !p.completedAt && nextDays.length >= challenge.threshold.targetDays;
    db.challengeParticipants = db.challengeParticipants.map((x, i) =>
      i === idx
        ? {
            ...x,
            daysCounted: nextDays,
            completedAt: justCompleted ? now() : x.completedAt,
          }
        : x,
    );
    emit();
    return db.challengeParticipants[idx];
  },

  /** Post a reflection to the Together feed for a challenge. */
  postChallengeReflection: (input: {
    challengeId: string;
    userId: string;
    body: string;
  }) => {
    const trimmed = input.body.trim();
    if (!trimmed) return null;
    const challenge = db.challenges.find((c) => c.id === input.challengeId);
    if (!challenge) return null;
    const p = db.challengeParticipants.find(
      (x) => x.challengeId === input.challengeId && x.userId === input.userId,
    );
    const dayMarker = p?.daysCounted.length ?? 0;
    const reflection: ChallengeReflection = {
      id: `cr_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      challengeId: input.challengeId,
      userId: input.userId,
      body: trimmed,
      dayMarker,
      createdAt: now(),
    };
    db.challengeReflections.push(reflection);
    emit();
    return reflection;
  },

  /* ---------------- Autoflow welcome sequence mutations ----------- */

  /**
   * Lazily ensure a sister has a welcomeProgress row. Called by the
   * other welcome mutations so a fresh signup never crashes a click.
   */
  ensureWelcomeProgress: (userId: string): WelcomeProgress => {
    const existing = db.welcomeProgress.find((w) => w.userId === userId);
    if (existing) return existing;
    const fresh: WelcomeProgress = {
      userId,
      completedActions: [],
      completedDays: [],
      dismissedAt: null,
      demoOverrideDay: null,
      createdAt: now(),
    };
    db.welcomeProgress.push(fresh);
    emit();
    return fresh;
  },

  /**
   * Mark an action as completed. If the action key matches a step’s
   * primary action, the day is also auto-marked complete (gentle
   * progression, no extra tap required).
   */
  completeWelcomeAction: (input: { userId: string; actionKey: string }) => {
    mutations.ensureWelcomeProgress(input.userId);
    const idx = db.welcomeProgress.findIndex(
      (w) => w.userId === input.userId,
    );
    if (idx < 0) return null;
    const p = db.welcomeProgress[idx];
    if (p.completedActions.includes(input.actionKey)) return p;
    const nextActions = [...p.completedActions, input.actionKey];

    // If this is a known step's primary action, mark the day complete too.
    const matchingStep = WELCOME_STEPS.find(
      (s) =>
        s.primaryAction.key === input.actionKey ||
        s.secondaryAction?.key === input.actionKey,
    );
    let nextDays = p.completedDays;
    if (matchingStep && !nextDays.includes(matchingStep.day)) {
      nextDays = [...nextDays, matchingStep.day].sort((a, b) => a - b);
    }
    db.welcomeProgress = db.welcomeProgress.map((w, i) =>
      i === idx
        ? { ...w, completedActions: nextActions, completedDays: nextDays }
        : w,
    );
    emit();
    return db.welcomeProgress[idx];
  },

  /**
   * Dismiss the autoflow card from Today. The journey itself is still
   * accessible at /welcome-journey, this just hides the home reminder.
   */
  dismissWelcomeAutoflow: (input: { userId: string }) => {
    mutations.ensureWelcomeProgress(input.userId);
    const idx = db.welcomeProgress.findIndex(
      (w) => w.userId === input.userId,
    );
    if (idx < 0) return null;
    db.welcomeProgress = db.welcomeProgress.map((w, i) =>
      i === idx ? { ...w, dismissedAt: now() } : w,
    );
    emit();
    return db.welcomeProgress[idx];
  },

  /**
   * Restore the autoflow card on Today after it was dismissed (used by
   * the journey page so a sister can bring it back any time).
   */
  restoreWelcomeAutoflow: (input: { userId: string }) => {
    mutations.ensureWelcomeProgress(input.userId);
    const idx = db.welcomeProgress.findIndex(
      (w) => w.userId === input.userId,
    );
    if (idx < 0) return null;
    db.welcomeProgress = db.welcomeProgress.map((w, i) =>
      i === idx ? { ...w, dismissedAt: null } : w,
    );
    emit();
    return db.welcomeProgress[idx];
  },

  /* ===================== Monthly Garden Walk mutations =================== */

  /** Log a walk. The monthId is computed from today's date so a sister
   *  who logs at 11:58 PM on the last day of the month still credits
   *  that month. Reflection is trimmed and capped at 140 chars to keep
   *  the feed scannable. */
  logGardenWalk: (input: {
    userId: string;
    minutes: number;
    reflection: string;
    anonymous: boolean;
  }) => {
    const d = new Date();
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const exact = db.gardenWalkMonths.find((x) => x.year === y && x.month === m);
    const fallback = db.gardenWalkMonths
      .slice()
      .sort((a, b) => b.year * 12 + b.month - (a.year * 12 + a.month))[0];
    const month = exact ?? fallback;
    if (!month) return null;
    const dateKey = `${y}-${String(m).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const minutes = Math.max(5, Math.min(120, Math.round(input.minutes)));
    const reflection = input.reflection.trim().slice(0, 140);
    const log: GardenWalkLog = {
      id: `gwl_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      userId: input.userId,
      monthId: month.id,
      dateKey,
      minutes,
      reflection,
      anonymous: input.anonymous,
      loggedAt: now(),
    };
    db.gardenWalkLogs.push(log);
    emit();
    return log;
  },

  /** Remove a walk. Used by the recent-walk row's tap to undo. */
  unlogGardenWalk: (logId: string) => {
    const before = db.gardenWalkLogs.length;
    db.gardenWalkLogs = db.gardenWalkLogs.filter((w) => w.id !== logId);
    if (db.gardenWalkLogs.length !== before) emit();
  },

  /**
   * Set or clear the demo override day. When set, the active step
   * shown on /welcome-journey ignores time-based clamping. Pass null
   * to clear.
   */
  setDemoWelcomeDay: (input: { userId: string; day: number | null }) => {
    mutations.ensureWelcomeProgress(input.userId);
    const idx = db.welcomeProgress.findIndex(
      (w) => w.userId === input.userId,
    );
    if (idx < 0) return null;
    const clamped =
      input.day == null ? null : Math.min(7, Math.max(1, input.day));
    db.welcomeProgress = db.welcomeProgress.map((w, i) =>
      i === idx ? { ...w, demoOverrideDay: clamped } : w,
    );
    emit();
    return db.welcomeProgress[idx];
  },

  /* ============================ Sisterhood ================================ */

  /** Compose a Sisterhood post. The body is checked against the sensitive
   *  keyword list; if it trips, the post lands in queued status for Coach
   *  Kiley to review before going live. Listening-period sisters cannot post. */
  createSisterhoodPost: (input: {
    authorId: string;
    kind: SisterhoodPostKind;
    body: string;
    photoDataUrl?: string | null;
    verseRef?: string | null;
  }): SisterhoodPost | null => {
    const author = db.users.find((u) => u.id === input.authorId);
    if (!author) return null;
    const day = 1000 * 60 * 60 * 24;
    if (now() - author.createdAt < day * 7) return null; // listening period
    const body = input.body.trim();
    if (!body) return null;
    const status: SisterhoodPostStatus = shouldQueueForReview(body) ? "queued" : "live";
    const createdAt = now();
    const post: SisterhoodPost = {
      id: `sp_${createdAt}_${Math.random().toString(36).slice(2, 6)}`,
      authorId: input.authorId,
      kind: input.kind,
      body,
      photoDataUrl: input.photoDataUrl ?? null,
      verseRef: input.verseRef ?? null,
      status,
      createdAt,
      editLockedAt: createdAt + day,
      flaggedBy: null,
      flaggedReason: null,
    };
    db.sisterhoodPosts.push(post);
    emit();
    return post;
  },

  /** Toggle a reaction on/off, or replace if she switches kinds. */
  togglePostReaction: (input: {
    userId: string;
    postId: string;
    kind: SisterhoodReactionKind;
  }): SisterhoodReaction | null => {
    const existing = db.sisterhoodReactions.find(
      (r) => r.userId === input.userId && r.postId === input.postId,
    );
    if (existing) {
      // Same kind tapped again -> remove. Different kind -> replace.
      db.sisterhoodReactions = db.sisterhoodReactions.filter((r) => r.id !== existing.id);
      if (existing.kind === input.kind) {
        emit();
        return null;
      }
    }
    const reaction: SisterhoodReaction = {
      id: `sr_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      postId: input.postId,
      userId: input.userId,
      kind: input.kind,
      reactedAt: now(),
    };
    db.sisterhoodReactions.push(reaction);
    emit();
    return reaction;
  },

  /** Add a comment to a post. Same sensitive-keyword filter applies; queued
   *  comments are hidden from the thread until Kiley reviews them. */
  addPostComment: (input: {
    userId: string;
    postId: string;
    body: string;
  }): SisterhoodComment | null => {
    const author = db.users.find((u) => u.id === input.userId);
    if (!author) return null;
    const day = 1000 * 60 * 60 * 24;
    if (now() - author.createdAt < day * 7) return null;
    const body = input.body.trim();
    if (!body) return null;
    const status: SisterhoodCommentStatus = shouldQueueForReview(body) ? "queued" : "live";
    const comment: SisterhoodComment = {
      id: `sc_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      postId: input.postId,
      userId: input.userId,
      body,
      status,
      createdAt: now(),
    };
    db.sisterhoodComments.push(comment);
    emit();
    return comment;
  },

  /** Edit a post body, only inside the 24-hour edit window. */
  editSisterhoodPost: (id: string, body: string): SisterhoodPost | null => {
    const idx = db.sisterhoodPosts.findIndex((p) => p.id === id);
    if (idx < 0) return null;
    const post = db.sisterhoodPosts[idx];
    if (now() >= post.editLockedAt) return null;
    const trimmed = body.trim();
    if (!trimmed) return null;
    db.sisterhoodPosts[idx] = { ...post, body: trimmed };
    emit();
    return db.sisterhoodPosts[idx];
  },

  /** File a report on a post or comment. Coach sees the queue. */
  reportPost: (input: {
    reporterId: string;
    postId: string;
    reason: string;
  }): SisterhoodReport => {
    const report: SisterhoodReport = {
      id: `srep_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      target: "post",
      targetId: input.postId,
      reporterId: input.reporterId,
      reason: input.reason,
      status: "open",
      createdAt: now(),
      resolvedAt: null,
    };
    db.sisterhoodReports.push(report);
    emit();
    return report;
  },

  /** Coach action: clear a queued post into live status. */
  approveQueuedPost: (postId: string): SisterhoodPost | null => {
    const idx = db.sisterhoodPosts.findIndex((p) => p.id === postId);
    if (idx < 0) return null;
    db.sisterhoodPosts[idx] = { ...db.sisterhoodPosts[idx], status: "live" };
    emit();
    return db.sisterhoodPosts[idx];
  },

  /** Coach action: remove a post entirely from the feed. */
  removeQueuedPost: (postId: string): SisterhoodPost | null => {
    const idx = db.sisterhoodPosts.findIndex((p) => p.id === postId);
    if (idx < 0) return null;
    db.sisterhoodPosts[idx] = { ...db.sisterhoodPosts[idx], status: "removed" };
    emit();
    return db.sisterhoodPosts[idx];
  },

  /* ========================= Sister Network =============================== */

  /** Opt into the Sister Network. Requires eligibility. Creates or updates
   *  the profile and flips isOpenIn to true. */
  joinNetwork: (input: {
    userId: string;
    bio: string;
    lifeStage: NetworkLifeStage;
    goals: NetworkGoal[];
  }): NetworkProfile | null => {
    if (!queries.networkEligible(input.userId)) return null;
    const idx = db.networkProfiles.findIndex((p) => p.userId === input.userId);
    if (idx >= 0) {
      db.networkProfiles[idx] = {
        ...db.networkProfiles[idx],
        isOpenIn: true,
        bio: input.bio,
        lifeStage: input.lifeStage,
        goals: input.goals,
        updatedAt: now(),
      };
      emit();
      return db.networkProfiles[idx];
    }
    const profile: NetworkProfile = {
      userId: input.userId,
      isOpenIn: true,
      bio: input.bio,
      lifeStage: input.lifeStage,
      goals: input.goals,
      joinedNetworkAt: now(),
      updatedAt: now(),
    };
    db.networkProfiles.push(profile);
    emit();
    return profile;
  },

  /** Step out of the Sister Network without losing the profile. */
  leaveNetwork: (userId: string): NetworkProfile | null => {
    const idx = db.networkProfiles.findIndex((p) => p.userId === userId);
    if (idx < 0) return null;
    db.networkProfiles[idx] = {
      ...db.networkProfiles[idx],
      isOpenIn: false,
      updatedAt: now(),
    };
    emit();
    return db.networkProfiles[idx];
  },

  /** Send a structured intro. Caps at 5 active sent intros. */
  sendIntro: (input: {
    fromUserId: string;
    toUserId: string;
    prompt: string;
    customNote: string;
  }): NetworkIntro | { error: string } => {
    if (input.fromUserId === input.toUserId) {
      return { error: "Cannot send to yourself." };
    }
    const sentCount = queries.activeSentIntroCount(input.fromUserId);
    if (sentCount >= 5) {
      return { error: "You already have 5 active sister connections." };
    }
    const note = input.customNote.slice(0, 240);
    const intro: NetworkIntro = {
      id: `ni_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      fromUserId: input.fromUserId,
      toUserId: input.toUserId,
      prompt: input.prompt,
      customNote: note,
      status: "pending",
      createdAt: now(),
      lastMessageAt: now(),
      respondedAt: null,
    };
    db.networkIntros.push(intro);
    emit();
    return intro;
  },

  /** Receiver accepts: thread opens for ongoing exchange. */
  acceptIntro: (introId: string): NetworkIntro | null => {
    const idx = db.networkIntros.findIndex((i) => i.id === introId);
    if (idx < 0) return null;
    db.networkIntros[idx] = {
      ...db.networkIntros[idx],
      status: "accepted",
      respondedAt: now(),
      lastMessageAt: now(),
    };
    emit();
    return db.networkIntros[idx];
  },

  /** Receiver declines silently. No notification fires back to the sender. */
  declineIntro: (introId: string): NetworkIntro | null => {
    const idx = db.networkIntros.findIndex((i) => i.id === introId);
    if (idx < 0) return null;
    db.networkIntros[idx] = {
      ...db.networkIntros[idx],
      status: "declined",
      respondedAt: now(),
    };
    emit();
    return db.networkIntros[idx];
  },

  /** Receiver opens the thread for a single one-time exchange. */
  acceptIntroOneTime: (introId: string): NetworkIntro | null => {
    const idx = db.networkIntros.findIndex((i) => i.id === introId);
    if (idx < 0) return null;
    db.networkIntros[idx] = {
      ...db.networkIntros[idx],
      status: "one-time",
      respondedAt: now(),
      lastMessageAt: now(),
    };
    emit();
    return db.networkIntros[idx];
  },

  /** Send a message in an open intro thread. Updates lastMessageAt for
   *  auto-lapse calculation. */
  sendNetworkMessage: (input: {
    introId: string;
    fromUserId: string;
    body: string;
  }): NetworkMessage | null => {
    const intro = db.networkIntros.find((i) => i.id === input.introId);
    if (!intro) return null;
    if (intro.status !== "accepted" && intro.status !== "one-time") return null;
    const body = input.body.trim();
    if (!body) return null;
    const message: NetworkMessage = {
      id: `nm_${now()}_${Math.random().toString(36).slice(2, 6)}`,
      introId: input.introId,
      fromUserId: input.fromUserId,
      body,
      sentAt: now(),
    };
    db.networkMessages.push(message);
    // Update lastMessageAt on the intro
    db.networkIntros = db.networkIntros.map((i) =>
      i.id === input.introId ? { ...i, lastMessageAt: now() } : i,
    );
    // One-time threads close after the first reply from the other side.
    if (intro.status === "one-time" && input.fromUserId !== intro.fromUserId) {
      db.networkIntros = db.networkIntros.map((i) =>
        i.id === input.introId ? { ...i, status: "lapsed" as const } : i,
      );
    }
    emit();
    return message;
  },

  /* ===================== Movement personalization ====================== */

  /** Replace the full injury list. Used at end of onboarding and from
   *  the Settings injury panel. */
  setInjuries: (userId: string, injuries: InjurySelection[]): MovementProfile => {
    return upsertMovementProfile(userId, (p) => ({
      ...p,
      injuries,
      updatedAt: now(),
    }));
  },

  /** Toggle a single injury between paused and active. Used by the
   *  Settings injury panel without a full save. */
  toggleInjuryPaused: (userId: string, flag: InjuryFlag): MovementProfile => {
    return upsertMovementProfile(userId, (p) => ({
      ...p,
      injuries: p.injuries.map((i) =>
        i.flag === flag ? { ...i, paused: !i.paused } : i,
      ),
      updatedAt: now(),
    }));
  },

  setFitnessGoals: (userId: string, goals: FitnessGoal[]): MovementProfile => {
    return upsertMovementProfile(userId, (p) => ({
      ...p,
      goals,
      updatedAt: now(),
    }));
  },

  setSchedule: (userId: string, schedule: WeeklySchedule): MovementProfile => {
    return upsertMovementProfile(userId, (p) => ({
      ...p,
      schedule,
      updatedAt: now(),
    }));
  },

  /** Reset the mesocycle anchor to today. Use when the sister wants a
   *  fresh 4-week block, e.g. after recovering from an injury or returning
   *  from a break. Compounds will hold for the next 4 weeks from now. */
  refreshMesocycle: (userId: string): MovementProfile => {
    return upsertMovementProfile(userId, (p) => ({
      ...p,
      mesocycleStartedAt: now(),
      updatedAt: now(),
    }));
  },

  /** Marks personalization onboarding complete and ensures a fresh anchor
   *  is set. Called from Onboarding's final step. */
  completeMovementOnboarding: (userId: string): MovementProfile => {
    return upsertMovementProfile(userId, (p) => ({
      ...p,
      completed: true,
      mesocycleStartedAt: p.mesocycleStartedAt || now(),
      updatedAt: now(),
    }));
  },
};

/** Internal helper: ensure a MovementProfile exists for a user, then apply
 *  a transform and persist. Returns the updated profile. */
function upsertMovementProfile(
  userId: string,
  patch: (p: MovementProfile) => MovementProfile,
): MovementProfile {
  const existing = db.movementProfiles.find((p) => p.userId === userId);
  const base: MovementProfile = existing ?? {
    userId,
    injuries: [],
    goals: [],
    schedule: { days: [], minutesPerDay: 30 },
    mesocycleStartedAt: 0,
    completed: false,
    updatedAt: now(),
  };
  const next = patch(base);
  if (existing) {
    db.movementProfiles = db.movementProfiles.map((p) =>
      p.userId === userId ? next : p,
    );
  } else {
    db.movementProfiles = [...db.movementProfiles, next];
  }
  emit();
  return next;
}

/* ============================ React bindings ============================== */

/**
 * Convex-shaped useQuery — reactive snapshot of any selector.
 * Usage:
 *   const me = useQuery(queries.currentUser);
 *   const seeds = useQuery(() => queries.seedsForInviter("u_hannah"));
 */
export function useStoreQuery<T>(selector: () => T): T {
  // Track the latest store version we have a cached result for. We must return
  // a referentially stable snapshot to `useSyncExternalStore` between store
  // emits, otherwise React 18 throws "getSnapshot should be cached" / loops.
  const cacheRef = useRef<{ version: number; value: T } | null>(null);
  // Selector identity may change between renders (especially when callers wrap
  // with `useMemo`). Re-evaluate when selector OR version changes.
  const lastSelectorRef = useRef<typeof selector | null>(null);

  const getSnapshot = (): T => {
    const version = storeVersion;
    const cached = cacheRef.current;
    if (cached && cached.version === version && lastSelectorRef.current === selector) {
      return cached.value;
    }
    const next = selector();
    // Preserve previous reference if shallow-equal so React bails out on
    // re-renders that produce identical content (e.g. filtered arrays).
    if (cached && shallowEqual(cached.value, next)) {
      cacheRef.current = { version, value: cached.value };
      lastSelectorRef.current = selector;
      return cached.value;
    }
    cacheRef.current = { version, value: next };
    lastSelectorRef.current = selector;
    return next;
  };

  // Use a version counter as the snapshot "key" for SSR fallback.
  useSyncExternalStore(subscribe, getStoreVersion, getStoreVersion);
  return getSnapshot();
}

/**
 * Convex-shaped useMutation — returns a stable function that calls the
 * mutation and notifies subscribers. Mirrors `convex/react`'s shape so the
 * swap to real Convex is a one-line change.
 */
export function useStoreMutation<TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => TReturn
): (...args: TArgs) => TReturn {
  const [stable] = useState(() => fn);
  return stable;
}

/* ============================== Dev helpers =============================== */

/** Expose for debugging — never relied on in production. */
declare global {
  interface Window {
    __mnf_db__?: DB;
  }
}
if (typeof window !== "undefined" && import.meta.env.DEV) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__mnf_db__ = db;
  useEffect; // keep import alive for tree-shake noise
}
